/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-mixed-operators, no-loop-func, no-const-assign */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import API, { getApiResourceUrl } from './services/api'; // Adjust the import path as necessary
import { fetchInvestigationForIncident, resolveInvestigationStatus, preferInvestigationStatus, getCanonicalIncidentId, getClientCacheForId, setClientCacheForId } from './utils/incidentHelpers';
import { hasWcbDashboardAccessEmail } from './utils/wcbAccess';
import { useLocation, useNavigate } from 'react-router-dom';
import './FireIncidentInvestigations.css'; // Fire Department-specific styles
import InvestigationStatusTracker from './components/InvestigationStatusTracker';
import { getDepartmentDisplayName } from './utils/departmentLabels';
import { getAppPath } from './utils/runtimeMode';

// Material-UI imports for the select menu and dialog
import { FormControl, InputLabel, Select, MenuItem, Checkbox as MUICheckbox, ListItemText, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';


  const craigDepartments = ["Parks", "Recreation", "Roads", "Utilities"];
  const isCraigB = (localStorage.getItem('userEmail') || '').toLowerCase() === 'craigb@diamondvalley.town';
  
  // Complete list of all departments for filtering
  const ALL_DEPARTMENTS = [
    'CAO',
    'Communications',
    'Finance',
    'Legislative Services',
    'Fire Department',
    'Parks',
    'Planning and Development',
    'Recreation',
    'Roads',
    'Safety',
    'Utilities'
  ];

const FIRE_DEPARTMENT_LABEL = 'Fire Department';
const isFireDeptIncident = (incident = {}) => {
  const target = FIRE_DEPARTMENT_LABEL.toLowerCase();

  const departments = [
    incident.PRIMARY_DEPARTMENT,
    incident.primaryDepartment,
    incident.SECONDARY_DEPARTMENT,
    incident.secondaryDepartment,
    incident.department,
    incident.DEPARTMENT
  ]
    .map(value => String(value || '').trim().toLowerCase())
    .filter(Boolean);

  return departments.some((normalized) => (
    normalized === target || normalized === 'fire' || normalized.includes('fire')
  ));
};

const normalizeDisplayValue = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';

  const lowered = normalized.toLowerCase();
  if (['unknown', 'n/a', 'na', 'not available', 'not specified', 'unassigned', 'not assigned'].includes(lowered)) {
    return '';
  }

  return normalized;
};

const getAssignedInvestigatorEmail = (incident = {}) => {
  const assignee = normalizeDisplayValue(incident.ASSIGNEE || incident.assignee);
  return assignee && assignee.includes('@') ? assignee : '';
};

const isAssignedFireInvestigatorForIncident = (incident = {}, email = '') => {
  const assignedEmail = safeToLowerCase(getAssignedInvestigatorEmail(incident));
  const currentUserEmail = safeToLowerCase(String(email || '').trim());
  return Boolean(assignedEmail && currentUserEmail && isFireDeptIncident(incident) && assignedEmail === currentUserEmail);
};

function safeToLowerCase(str) {
  if (typeof str !== 'string') return '';
  return str.toLowerCase();
}

function isCraigManagerForIncident(incident) {
  if (!isCraigB) return false;
  if (!incident) return false;

  // Get department safely as a string
  let dept =
    (typeof incident.primaryDepartment === 'string' && incident.primaryDepartment) ||
    (typeof incident.PRIMARY_DEPARTMENT === 'string' && incident.PRIMARY_DEPARTMENT) ||
    (typeof incident.department === 'string' && incident.department) ||
    (typeof incident.DEPARTMENT === 'string' && incident.DEPARTMENT) ||
    '';
  dept = safeToLowerCase(dept).trim();
  if (!dept) return false;

  // Defensive: filter out any non-string values in craigDepartments
  return Array.isArray(craigDepartments) && craigDepartments
    .filter(d => typeof d === 'string' && d)
    .some(d => dept.includes(safeToLowerCase(d)));
}

// canonical ID helper moved to src/utils/incidentHelpers.js

// Unsafe Acts options
const UNSAFE_ACTS_OPTIONS = [
  "Failure to communicate or coordinate",
  "Failure to identify hazard or risk",
  "Failure to use lockout/tagout",
  "Failure to react/correct",
  "Failure to secure properly",
  "Failure to warn",
  "Failure to wear appropriate PPE",
  "Fatigue",
  "Horseplay",
  "Improper lifting",
  "Improper loading/unloading",
  "Improper placement",
  "Making safety devices inoperable",
  "Operating at improper speed",
  "Operating equipment/tools without authority",
  "Removing safety devices",
  "Servicing equipment while in operation",
  "Under the influence of drugs/alcohol",
  "Unsafe mixing/combining",
  "Unsafe positon/posture",
  "Using defective equipment",
  "Using equipment improperly",
  "Wilful misconduct",
  "Working at unsafe speed",
  "Other"
];

const UNSAFE_CONDITIONS_OPTIONS = [
  "Congested workspace or restricted action",
  "Defective tools, equipment or materials",
  "Extreme temperatures",
  "Extreme weather conditions",
  "Fire or explosion hazard",
  "Harmful materials",
  "Hazardous environmental conditions (gases, dust, smoke, fumes, vapors)",
  "Improper or inadequate PPE",
  "Inadequate guards or barriers",
  "Inadequate or excessive lighting",
  "Inadequate preparation or planning",
  "Inadequate ventilation",
  "Inadequate warning system",
  "Noise exposure",
  "Poor housekeeping/disorderly workspace",
  "Poor or missing instructions or procedures",
  "Poor road condtions",
  "Radiation exposure",
  "Unsafe job design",
  "Unsafe mobile equipment",
  "Other"
];

const PERSONAL_FACTORS_OPTIONS = [
  "Complacency",
  "Exessive frustration",
  "Improper attempt to avoid discomfort",
  "Improper attempt to save time",
  "Improper motivation",
  "Improper performance rewards",
  "Inadequate capability",
  "Inadequate initial training",
  "Inadequate orientation",
  "Inadequate physical capabilities",
  "Inadequate reinforcement of proper behaviour",
  "Inappropriate aggression (violence/harassment)",
  "Inappropriate peer pressure",
  "Inadequate coaching",
  "Lack of experience",
  "Lack of knowledge",
  "Lack of skill",
  "Lack of training or refresher training",
  "Misunderstanding of instruction",
  "Stress",
  "Other"
];

const JOB_FACTORS_OPTIONS = [
  "Conflicting objectives/goals/standards",
  "Conflicting roles and responsibilities",
  "Inadequate engineering controls",
  "Inadequate leadership/supervision",
  "Inadequate maintenance (scheduled or preventative)",
  "Inadequate matching of individual qualification and task requirements",
  "Inadequate policy/procedure/directive or guideline",
  "Inadequate purchasing",
  "Inadequate tools or equipment",
  "Inadequate work standards",
  "Wear and tear",
  "Other"
];

const ROOT_CAUSE_ANALYSIS_OPTIONS = [
  "Management commitment and administration",
  "Materials or service",
  "Performance observations",
  "Personal Protective Equipment (PPE)",
  "Preventative maintenance",
  "Purchasing controls",
  "Safe work pactices/procedures",
  "Worker knowledge and skills training",
  "Other"
];

const isSupervisor = (role) => {
  if (!role) return false;
  const normalizedRole = String(role).toUpperCase();
  return normalizedRole === 'SUPERVISOR' || normalizedRole === 'FIELD_SUPERVISOR';
};

const LEADERSHIP_INVESTIGATION_EDIT_ROLES = new Set(['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER', 'OHS_ADVISOR']);

const canEditLeadershipInvestigationFields = ({ role, email, isDelegate = false, isOverride = false }) => {
  const normalizedRole = String(role || '').toUpperCase();
  const normalizedEmail = safeToLowerCase(String(email || '').trim());

  return LEADERSHIP_INVESTIGATION_EDIT_ROLES.has(normalizedRole)
    || isOverride
    || normalizedEmail === 'safety@diamondvalley.town'
    || normalizedEmail === 'cao@diamondvalley.town'
    || hasWcbDashboardAccessEmail(normalizedEmail)
    || normalizedEmail === 'craigb@diamondvalley.town';
};

const formatInvestigationStatus = (status) => {
  switch (status) {
    case 'PENDING': return 'Pending Submission';
    case 'SUBMITTED': return 'Submitted - Awaiting Supervisor Review';
    case 'SUPERVISOR_APPROVED': return 'Supervisor Approved - Awaiting Manager Review';
    case 'NEEDS_REVISION': return 'Needs Revision';
    case 'COMPLETED': return 'Fully Approved';
    default: return status;
  }
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  console.log('formatDisplayDate input:', dateString);
  
  // For date-only strings (YYYY-MM-DD), create date without timezone conversion
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    // Split the date string and create date with local timezone
    const [year, month, day] = dateString.split('-').map(Number);
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }
  
  // Handle other date formats
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.log('Invalid date input:', dateString);
    return dateString; // Return original if parsing fails
  }
  
  console.log('Parsed date object:', date);
  console.log('UTC date string:', date.toISOString());
  console.log('Local date string:', date.toLocaleDateString());
  
  // Format as YYYY-MM-DD to be more explicit and avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Validation helper placed near the top to avoid use-before-define ESLint errors.
const validateIncidentIdForFetch = (id, contextLabel, incidents = [], selectedIncident = null) => {
  try {
    const idStr = String(id || '');

    if (selectedIncident && (String(selectedIncident.id) === idStr || String(selectedIncident.ID) === idStr)) {
      return true;
    }

    if (Array.isArray(incidents) && incidents.some(inc => String(inc.id) === idStr || String(inc.ID) === idStr)) {
      return true;
    }

    console.warn(`⚠️ Suspicious incident fetch: ${contextLabel} called with id=`, id, '\nSelectedIncident=', selectedIncident, '\nKnownIncidentsSample=', (incidents && incidents.slice ? incidents.slice(0,5) : incidents));
    window.__suspiciousIncidentFetch = window.__suspiciousIncidentFetch || [];
    window.__suspiciousIncidentFetch.push({ id, context: contextLabel, when: new Date().toISOString() });

    return false;
  } catch (e) {
    return true;
  }
};

// Helper: pick the most authoritative investigation status available
// Helper: treat null/empty server fields as missing and prefer the most authoritative non-empty value.
// Use status helpers from src/utils/incidentHelpers.js (preferInvestigationStatus, resolveInvestigationStatus)

// Helper: return all id variants for an incident (strings)
const getAllIdVariants = (inc = {}) => {
  return [inc.id, inc.ID, inc.incident_id, inc.INCIDENT_ID, inc.incidentId]
    .filter(Boolean)
    .map(v => String(v));
};

const getDraftStorageKey = (incidentLike = {}) => {
  if (incidentLike === null || incidentLike === undefined) return '';
  if (typeof incidentLike === 'string' || typeof incidentLike === 'number') {
    return String(incidentLike).trim();
  }

  return String(
    getCanonicalIncidentId(incidentLike)
    || incidentLike.id
    || incidentLike.ID
    || incidentLike.incidentId
    || incidentLike.INCIDENT_ID
    || ''
  ).trim();
};

const readInvestigationDraftMap = () => {
  try {
    const storedDrafts = localStorage.getItem('fireIncidentInvestigationDrafts');
    return storedDrafts ? JSON.parse(storedDrafts) : {};
  } catch (error) {
    console.warn('Failed to read fire incident investigation drafts:', error);
    return {};
  }
};

const writeInvestigationDraftMap = (draftsMap) => {
  localStorage.setItem('fireIncidentInvestigationDrafts', JSON.stringify(draftsMap || {}));
};

const getDraftKeysForIncident = (incidentLike = {}) => {
  const directKey = getDraftStorageKey(incidentLike);
  const objectKeys = typeof incidentLike === 'object' && incidentLike !== null
    ? getAllIdVariants(incidentLike)
    : [];

  return Array.from(new Set([directKey, ...objectKeys].filter(Boolean).map(String)));
};

const getInvestigationStatusRank = (statusValue) => {
  switch (String(statusValue || '').toUpperCase()) {
    case 'PENDING':
      return 0;
    case 'NEEDS_REVISION':
      return 1;
    case 'SUBMITTED':
      return 2;
    case 'SUPERVISOR_APPROVED':
      return 3;
    case 'COMPLETED':
      return 4;
    default:
      return -1;
  }
};

const getInvestigationServerTimestamp = (serverData = {}) => {
  const candidates = [
    serverData.UPDATED_AT,
    serverData.updatedAt,
    serverData.updated_at,
    serverData.INVESTIGATION_UPDATED_AT,
    serverData.investigationUpdatedAt,
    serverData.CREATED_AT,
    serverData.createdAt,
    serverData.DATE,
    serverData.date
  ];

  let latestTimestamp = 0;
  candidates.forEach((value) => {
    if (!value) return;
    const timestamp = new Date(value).getTime();
    if (Number.isFinite(timestamp) && timestamp > latestTimestamp) {
      latestTimestamp = timestamp;
    }
  });

  return latestTimestamp;
};

const getDraftEntryForIncident = (incidentLike = {}, draftsMap = readInvestigationDraftMap()) => {
  const keys = getDraftKeysForIncident(incidentLike);
  let bestEntry = null;

  keys.forEach((key) => {
    const draftData = draftsMap[key];
    if (!draftData) return;

    const draftTimestamp = new Date(draftData.lastSaved || 0).getTime() || 0;
    if (!bestEntry || draftTimestamp > bestEntry.timestamp) {
      bestEntry = { key, draftData, timestamp: draftTimestamp };
    }
  });

  return bestEntry;
};

const isDraftObsoleteForServerData = ({ draftData, serverData }) => {
  if (!draftData) return true;

  const serverStatus = resolveInvestigationStatus(serverData || {}, {}) || '';
  const draftStatus = draftData.INVESTIGATION_STATUS || draftData.investigationStatus || '';
  const serverRank = getInvestigationStatusRank(serverStatus);
  const draftRank = getInvestigationStatusRank(draftStatus);

  if (serverRank >= getInvestigationStatusRank('SUPERVISOR_APPROVED')) {
    return true;
  }

  const serverTimestamp = getInvestigationServerTimestamp(serverData);
  const draftTimestamp = new Date(draftData.lastSaved || 0).getTime() || 0;

  if (serverTimestamp > 0 && draftTimestamp > 0 && draftTimestamp <= serverTimestamp) {
    return true;
  }

  if (serverRank >= 0 && draftRank >= 0 && serverRank > draftRank) {
    return true;
  }

  return false;
};

const clearDraftForIncident = (incidentLike = {}) => {
  const draftsMap = readInvestigationDraftMap();
  let changed = false;

  getDraftKeysForIncident(incidentLike).forEach((key) => {
    if (draftsMap[key]) {
      delete draftsMap[key];
      changed = true;
    }
  });

  if (changed) {
    writeInvestigationDraftMap(draftsMap);
  }

  return changed;
};

// Client-cache helpers are provided by src/utils/incidentHelpers.js

const buildInvestigationClientCachePayload = (data = {}, investigationStatus) => ({
  immediateActions: data.immediateActions || data.contributingFactors || data.CONTRIBUTING_FACTORS || '',
  unsafeActs: data.unsafeActs || [],
  unsafeConditions: data.unsafeConditions || [],
  personalFactors: data.personalFactors || [],
  jobFactors: data.jobFactors || [],
  rootCause: data.rootCause || '',
  rootCauseAnalysis: data.rootCauseAnalysis || [],
  correctiveActions: data.correctiveActions || [],
  wcbReportRequired: data.wcbReportRequired || 'No',
  wcbReportReason: data.wcbReportReason || '',
  wcbReportReasonOther: data.wcbReportReasonOther || '',
  ohsReportRequired: data.ohsReportRequired || 'No',
  ohsReportReason: data.ohsReportReason || '',
  ohsReportReference: data.ohsReportReference || '',
  investigationStatus: investigationStatus || data.investigationStatus || data.INVESTIGATION_STATUS || 'PENDING'
});


const normalizeStatusField = (incident = {}) => {
  const status = resolveInvestigationStatus(incident, {}) || 'PENDING';
  return {
    ...incident,
    investigationStatus: status,
    INVESTIGATION_STATUS: status
  };
};

const resolveIncidentEmployeeEmail = (srcIncident = {}) => {
  const clean = (val) => {
    const s = String(val ?? '').trim();
    return s && s.includes('@') ? s.toLowerCase() : '';
  };

  return (
    clean(srcIncident.EMPLOYEE_ID) ||
    clean(srcIncident.employee_id) ||
    clean(srcIncident.employeeId) ||
    clean(srcIncident.EMPLOYEES) ||
    clean(srcIncident.employees) ||
    ''
  );
};

const toDisplayNameFromEmail = (emailValue = '') => {
  const normalized = String(emailValue || '').trim();
  if (!normalized.includes('@')) return normalized;

  const localPart = normalized.split('@')[0] || '';
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// (validateIncidentIdForFetch is defined near the top to avoid use-before-define errors)

const mergeIncidentAttachmentLists = (...attachmentLists) => {
  const mergedAttachments = new Map();

  attachmentLists.forEach((attachmentList) => {
    if (!Array.isArray(attachmentList)) return;

    attachmentList.forEach((attachment, index) => {
      if (!attachment) return;

      const attachmentId = attachment.id
        || attachment.ID
        || attachment.fileId
        || attachment.FILEID
        || attachment.ATTACHMENT_ID
        || attachment.downloadUrl
        || attachment.name
        || `attachment-${mergedAttachments.size}-${index}`;

      const normalizedId = String(attachmentId);
      if (!mergedAttachments.has(normalizedId)) {
        mergedAttachments.set(normalizedId, attachment);
      }
    });
  });

  return Array.from(mergedAttachments.values());
};

const fetchRefreshedIncidentById = async (id) => {
  try {
    console.log('FORCED REFRESH: Fetching fresh incident data for ID:', id, 'at', new Date().toISOString());

  // Validate id before fetching to detect detail-row IDs being used as incident IDs
  validateIncidentIdForFetch(id, 'fetchRefreshedIncidentById', window.__incidentsCache || [], window.__selectedIncident || null);

  const response = await API.get(`/api/incidents/${id}`);
    
    console.log('FORCED REFRESH: Raw API response:', response);
    console.log('FORCED REFRESH: Response data:', response.data);
    
    const incidentData = Array.isArray(response.data) ? response.data[0] : response.data;
    
    console.log('FORCED REFRESH: Processed incident data:', incidentData);
    
    if (!incidentData) {
      throw new Error('No incident data received from server');
    }
    
    const refreshedIncident = {
      id: incidentData.ID, // <-- INCIDENTS.ID only!
      title: incidentData.TITLE || incidentData.title || 'Untitled',
      location: incidentData.LOCATION || incidentData.location || '',
      severity: incidentData.SEVERITY || incidentData.severity || '',
      description: incidentData.DESCRIPTION || incidentData.description || '',
      status: incidentData.STATUS || incidentData.status || '',
      date: incidentData.DATE || incidentData.date || '',
      primaryDepartment: incidentData.PRIMARY_DEPARTMENT || incidentData.primaryDepartment || '',
      department: incidentData.DEPARTMENT || incidentData.department || '',
      ROOT_CAUSE: incidentData.ROOT_CAUSE || '',
      rootCause: incidentData.ROOT_CAUSE || '',
      UNSAFE_CONDITIONS: incidentData.UNSAFE_CONDITIONS || '',
      unsafeConditions: incidentData.UNSAFE_CONDITIONS || '',
      UNSAFE_ACTS: incidentData.UNSAFE_ACTS || '',
      unsafeActs: incidentData.UNSAFE_ACTS || '',
      PERSONAL_FACTORS: incidentData.PERSONAL_FACTORS || '',
      personalFactors: incidentData.PERSONAL_FACTORS || '',
      JOB_FACTORS: incidentData.JOB_FACTORS || '',
      jobFactors: incidentData.JOB_FACTORS || '',
      ROOT_CAUSE_ANALYSIS: incidentData.ROOT_CAUSE_ANALYSIS || '',
      rootCauseAnalysis: incidentData.ROOT_CAUSE_ANALYSIS || '',
      CONTRIBUTING_FACTORS: incidentData.CONTRIBUTING_FACTORS || '',
      contributingFactors: incidentData.CONTRIBUTING_FACTORS || '',
      immediateActions: incidentData.CONTRIBUTING_FACTORS || '',
      CORRECTIVE_ACTIONS_JSON: incidentData.CORRECTIVE_ACTIONS_JSON || '',
      correctiveActionsJson: incidentData.CORRECTIVE_ACTIONS_JSON || '',
      investigationStatus: preferInvestigationStatus(incidentData.INVESTIGATION_STATUS ?? incidentData.investigationStatus ?? incidentData.status, null, 'PENDING') || 'PENDING',
      INVESTIGATION_STATUS: preferInvestigationStatus(incidentData.INVESTIGATION_STATUS ?? incidentData.investigationStatus ?? incidentData.status, null, 'PENDING') || 'PENDING',
      SUPERVISOR_APPROVAL: incidentData.SUPERVISOR_APPROVAL || 'PENDING',
      MANAGER_APPROVAL: incidentData.MANAGER_APPROVAL || 'PENDING',
      DELEGATE_EMAIL: incidentData.DELEGATE_EMAIL || '',
      delegateEmail: incidentData.DELEGATE_EMAIL || '',
      DELEGATE_INSTRUCTIONS: incidentData.DELEGATE_INSTRUCTIONS || '',
      delegateInstructions: incidentData.DELEGATE_INSTRUCTIONS || '',
      DELEGATE_FINDINGS: incidentData.DELEGATE_FINDINGS || '',
      delegateFindings: incidentData.DELEGATE_FINDINGS || '',
      DELEGATE_RECOMMENDATIONS: incidentData.DELEGATE_RECOMMENDATIONS || '',
      delegateRecommendations: incidentData.DELEGATE_RECOMMENDATIONS || '',
      DELEGATE_COMPLETED: incidentData.DELEGATE_COMPLETED || false,
      ISSUE_DESCRIPTION: incidentData.ISSUE_DESCRIPTION || '',
      issueDescription: incidentData.ISSUE_DESCRIPTION || '',
      attachments: mergeIncidentAttachmentLists(incidentData.ATTACHMENTS, incidentData.attachments),
      ATTACHMENTS: mergeIncidentAttachmentLists(incidentData.ATTACHMENTS, incidentData.attachments)
    };
    // Attempt to fetch canonical investigation row under this incident and merge it.
    // Use fetchInvestigationForIncident which probes canonical and legacy endpoints, and try a set of candidate IDs
    try {
      const candidates = new Set([String(id), String(incidentData?.ID || ''), String(incidentData?.incident_id || '' )].filter(Boolean));
      let inv = null;
      let invSourceId = null;
      for (const cid of candidates) {
        try {
          inv = await fetchInvestigationForIncident(cid);
          if (inv) {
            invSourceId = cid;
            console.log('Found investigation row for candidate id:', cid, inv);
            break;
          }
        } catch (e) {
          console.debug('fetchInvestigationForIncident failed for', cid, e && e.message);
        }
      }

      if (inv) {
        // If the investigation row points to a different canonical incident_id, fetch that parent incident instead
        const parentId = inv.incident_id || inv.INCIDENT_ID || inv.incidentId || inv.ID || null;
        if (parentId && String(parentId) !== String(incidentData.ID)) {
          console.log('Investigation row points to parent incident:', parentId, '— treating parent as canonical and attempting to fetch parent incident data');
          refreshedIncident.id = String(parentId);
          refreshedIncident.ID = refreshedIncident.ID || String(parentId);
          try {
            const parentResp = await API.get(`/api/incidents/${parentId}`);
            const parent = parentResp?.data?.incident || (Array.isArray(parentResp?.data) ? parentResp.data[0] : parentResp?.data);
            if (parent) {
              refreshedIncident.id = parent.ID || refreshedIncident.id;
              refreshedIncident.title = parent.TITLE || refreshedIncident.title;
              refreshedIncident.location = parent.LOCATION || refreshedIncident.location;
              refreshedIncident.description = parent.DESCRIPTION || refreshedIncident.description;
              refreshedIncident.date = parent.DATE || refreshedIncident.date;
            }
          } catch (parentErr) {
            console.warn('Failed to fetch parent incident for parentId', parentId, parentErr && parentErr.message);
          }
        }

        // Merge investigation fields (status, delegate, corrective actions etc.) into refreshedIncident
        refreshedIncident.investigationStatus = inv.INVESTIGATION_STATUS ?? inv.investigationStatus ?? refreshedIncident.investigationStatus;
        refreshedIncident.INVESTIGATION_STATUS = refreshedIncident.investigationStatus;
        refreshedIncident.DELEGATE_EMAIL = inv.DELEGATE_EMAIL || inv.delegateEmail || refreshedIncident.DELEGATE_EMAIL;
        refreshedIncident.delegateEmail = refreshedIncident.DELEGATE_EMAIL;
        refreshedIncident.CORRECTIVE_ACTIONS_JSON = inv.CORRECTIVE_ACTIONS_JSON || inv.correctiveActionsJson || refreshedIncident.CORRECTIVE_ACTIONS_JSON;
        refreshedIncident.correctiveActionsJson = refreshedIncident.CORRECTIVE_ACTIONS_JSON;
        refreshedIncident.ROOT_CAUSE = inv.ROOT_CAUSE || inv.rootCause || refreshedIncident.ROOT_CAUSE;
        refreshedIncident.rootCause = refreshedIncident.ROOT_CAUSE;
        // If investigation has attachments, merge them
        if (Array.isArray(inv.ATTACHMENTS) && inv.ATTACHMENTS.length) {
          const mergedAttachments = mergeIncidentAttachmentLists(
            refreshedIncident.ATTACHMENTS,
            refreshedIncident.attachments,
            inv.ATTACHMENTS,
            inv.attachments
          );
          refreshedIncident.ATTACHMENTS = mergedAttachments;
          refreshedIncident.attachments = mergedAttachments;
        }
        // Ensure contributing / immediate actions fields from the investigation row are merged too
        try {
          const contrib = inv.CONTRIBUTING_FACTORS || inv.contributing_factors || inv.contributingFactors || inv.CONTRIBUTING || inv.contributing || inv.immediateActions || inv.immediate_actions || inv.immediateAction || inv.immediate || null;
          if (contrib !== undefined && contrib !== null) {
            refreshedIncident.CONTRIBUTING_FACTORS = contrib;
            refreshedIncident.contributingFactors = contrib;
            refreshedIncident.immediateActions = contrib;
          }
        } catch (mergeErr) {
          console.debug('Failed to merge contributing/immediate fields from investigation row:', mergeErr && mergeErr.message);
        }
      } else {
        console.debug('No investigation row found for any candidate ids:', Array.from(candidates));
      }
    } catch (ie) {
      console.debug('Investigation lookup failed for id', id, ie && ie.message);
    }
    // Merge client cache for this incident (if any) so submitted fields survive until backend reflects them
    try {
      // Prefer the canonical id we resolved for the refreshedIncident (parent incident),
      // then fall back to any ids returned in the raw incidentData response.
      const canonicalCacheId = refreshedIncident.id || incidentData.ID || incidentData.incident_id || incidentData.id;
      const cache = getClientCacheForId(canonicalCacheId) || getClientCacheForId(incidentData.ID) || getClientCacheForId(incidentData.incident_id) || getClientCacheForId(incidentData.id);
      if (cache) {
        // Apply cache fields but do not overwrite authoritative server fields when present
        Object.keys(cache).forEach(k => {
          const existing = refreshedIncident[k];
          const val = cache[k];
          if ((existing === undefined || existing === null || existing === '' || (Array.isArray(existing) && existing.length === 0)) && val !== undefined) {
            refreshedIncident[k] = val;
          }
        });
        // If server returned null for INVESTIGATION_STATUS, prefer cached status
        if ((incidentData.INVESTIGATION_STATUS === null || incidentData.INVESTIGATION_STATUS === undefined) && cache.investigationStatus) {
          refreshedIncident.investigationStatus = cache.investigationStatus;
          refreshedIncident.INVESTIGATION_STATUS = cache.investigationStatus;
        }
      }
    } catch (e) {
      console.warn('Failed to merge client cache into refreshedIncident:', e);
    }

    // Ensure investigationStatus is not left as null; prefer previous selected incident or fallback
    try {
      const prev = window.__selectedIncident || {};
      const resolved = resolveInvestigationStatus(refreshedIncident, prev || {});
      refreshedIncident.investigationStatus = resolved;
      refreshedIncident.INVESTIGATION_STATUS = resolved;
    } catch (e) {
      // ignore
    }

    console.log('FRESH DATA FROM SERVER (merged with cache/prev):', refreshedIncident);
    return refreshedIncident;
  } catch (err) {
    console.error('Error fetching fresh incident data:', err);
    throw err;
  }
};

function normalizeInvestigationFormData(data) {
  // Some endpoints return wrapped payloads, e.g. { success: true, investigation: {...} }
  // or { success: true, incident: {...} }. Unwrap so normalization sees the real fields.
  try {
    if (data && typeof data === 'object') {
      if (data.investigation && typeof data.investigation === 'object') {
        data = data.investigation;
      } else if (data.incident && typeof data.incident === 'object') {
        data = data.incident;
      }
    }
  } catch (e) {
    // ignore
  }

  const normalizeYesNo = (raw, defaultValue = 'No') => {
    if (raw === undefined || raw === null || raw === '') return defaultValue;
    if (raw === true) return 'Yes';
    if (raw === false) return 'No';
    const s = String(raw).trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'yes' || s === 'y') return 'Yes';
    if (s === '0' || s === 'false' || s === 'no' || s === 'n') return 'No';
    return defaultValue;
  };

  function toArray(val) {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim().length > 0) {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      // If parsed is not an array, fallback to splitting
      return val.split(',').map(s => s.trim()).filter(Boolean);
    } catch {
      // If not valid JSON, fallback to splitting
      return val.split(',').map(s => s.trim()).filter(Boolean);
    }
  }
  return [];
}

// (validateIncidentIdForFetch is defined near the top to avoid use-before-define errors)

  const contributingObj = (() => {
    try {
      const raw = data.CONTRIBUTING_FACTORS || data.contributing_factors || data.contributingFactors || data.immediateActions || data.immediate_actions || '';

      // If the backend ever returns this already-parsed, accept it.
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;

      if (typeof raw !== 'string') return null;

      // CONTRIBUTING_FACTORS is historically a free-text field.
      // For backward-compatible persistence we sometimes store JSON in it.
      // Depending on backend serialization, it can arrive as:
      // 1) '{"a":1}' (normal JSON string)
      // 2) '"{\"a\":1}"' (double-encoded JSON string)
      // Handle both.
      let s = raw.trim();

      for (let i = 0; i < 2; i++) {
        if (s.startsWith('{') && s.endsWith('}')) {
          try {
            const parsed = JSON.parse(s);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
          } catch (e) {
            // If it looks like JSON but parse fails, try un-escaping quotes once.
            try {
              const relaxed = s.replace(/\\"/g, '"');
              const parsed2 = JSON.parse(relaxed);
              if (parsed2 && typeof parsed2 === 'object' && !Array.isArray(parsed2)) return parsed2;
            } catch {
              // ignore
            }
          }
          return null;
        }

        // Double-encoded: outer quotes around a JSON object string
        if (s.startsWith('"') && s.endsWith('"')) {
          try {
            const inner = JSON.parse(s);
            if (typeof inner === 'string') {
              s = inner.trim();
              continue;
            }
            if (inner && typeof inner === 'object' && !Array.isArray(inner)) return inner;
          } catch {
            return null;
          }
        }
        break;
      }
      return null;
    } catch {
      return null;
    }
  })();

  return {
    incidentDate: data.DATE || data.date || data.incidentDate || '',
    location: data.LOCATION || data.location || '',
    description: data.DESCRIPTION || data.description || '',

    // Reporting requirements (defaults to No)
    // Precedence: if backend ever adds explicit DB columns, those win; otherwise
    // prefer CONTRIBUTING_FACTORS JSON over any stale camelCase fields.
    wcbReportRequired: normalizeYesNo(
      (contributingObj?.wcbReportRequired ?? data.WCB_REPORT_REQUIRED ?? data.wcbReportRequired),
      'No'
    ),
    wcbReportReason: contributingObj?.wcbReportReason || data.WCB_REPORT_REASON || data.wcbReportReason || '',
    wcbReportReasonOther: contributingObj?.wcbReportReasonOther || data.WCB_REPORT_REASON_OTHER || data.wcbReportReasonOther || '',
    ohsReportRequired: normalizeYesNo(
      (contributingObj?.ohsReportRequired ?? data.OHS_REPORT_REQUIRED ?? data.ohsReportRequired),
      'No'
    ),
    ohsReportReason: contributingObj?.ohsReportReason || data.OHS_REPORT_REASON || data.ohsReportReason || '',
    ohsReportReference: contributingObj?.ohsReportReference || data.OHS_REPORT_REFERENCE || data.ohsReportReference || '',

    unsafeActs: toArray(data.UNSAFE_ACTS || data.unsafeActs),
    unsafeConditions: toArray(data.UNSAFE_CONDITIONS || data.unsafeConditions),
    personalFactors: (() => {
      const val = data.PERSONAL_FACTORS || data.personalFactors;
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') {
        try {
          const parsed = JSON.parse(val);
          if (Array.isArray(parsed)) return parsed;
          return val.split(',').map(s => s.trim()).filter(Boolean);
        } catch {
          return val.split(',').map(s => s.trim()).filter(Boolean);
        }
      }
      return [];
    })(),
    jobFactors: toArray(data.JOB_FACTORS || data.jobFactors),
    rootCauseAnalysis: toArray(data.ROOT_CAUSE_ANALYSIS || data.rootCauseAnalysis || data.root_cause_analysis),
    correctiveActions: toArray(data.CORRECTIVE_ACTIONS_JSON || data.correctiveActionsJson || data.correctiveActions),
  // Normalize contributing/immediate fields to a display-friendly string
  immediateActions: (() => {
    if (contributingObj && typeof contributingObj.immediateActions === 'string') {
      return contributingObj.immediateActions;
    }

    const raw = data.CONTRIBUTING_FACTORS || data.contributing_factors || data.contributingFactors || data.immediateActions || data.immediate_actions || '';
    if (!raw && raw !== 0) return '';
    if (Array.isArray(raw)) return raw.join(', ');
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed === '') return '';
      // Try to parse JSON arrays stored as strings
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.join(', ');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && typeof parsed.immediateActions === 'string') {
          return parsed.immediateActions;
        }
      } catch (e) {
        // not JSON — continue
      }
      return trimmed;
    }
    // Fallback to string conversion
    try { return String(raw); } catch { return '' }
  })(),
    rootCause: data.ROOT_CAUSE || data.rootCause || '',
    delegateEmail: data.DELEGATE_EMAIL || data.delegateEmail || '',
    delegateComments: data.DELEGATE_INSTRUCTIONS || data.delegateInstructions || '',
    delegateFindings: data.DELEGATE_FINDINGS || data.delegateFindings || '',
    delegateRecommendations: data.DELEGATE_RECOMMENDATIONS || data.delegateRecommendations || '',
    attachments: Array.isArray(data.ATTACHMENTS) ? data.ATTACHMENTS : [],
  // Do not default to 'PENDING' here; let authoritative merges decide when status is absent
  investigationStatus: data.INVESTIGATION_STATUS || data.investigationStatus || undefined,
  };
}

const Button = ({ children, type = "button", onClick, className = "" }) => {
  // If the investigation form is globally locked, only render the Close button.
  // We store a small flag in localStorage so this works reliably during render.
  try {
    const isLocked = localStorage.getItem('INVESTIGATION_LOCKED') === 'true';
    const labelText = typeof children === 'string'
      ? children
      : (children && children.props && (typeof children.props.children === 'string' ? children.props.children : '')) || '';
    if (isLocked && String(labelText).trim() !== 'Close') {
      return null;
    }
  } catch (e) {
    // localStorage may be unavailable in some environments; fall back to default rendering
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={`btn ${className}`}
    >
      {children}
    </button>
  );
};

const Input = ({ name, type = "text", label, onChange, value, required = false, disabled = false }) => (
  <div className="form-group">
    <label>{label}</label>
    <input
      name={name}
      type={type}
      onChange={onChange}
      value={value}
      required={required}
      disabled={disabled}
      className="form-control"
    />
  </div>
);

const Textarea = ({ name, label, onChange, value, required = false, disabled = false }) => (
  <div className="form-group">
    <label>{label}</label>
    <textarea
      name={name}
      onChange={onChange}
      value={value}
      required={required}
      disabled={disabled}
      className="form-control"
    />
  </div>
);

const Checkbox = ({ label, checked, onChange, disabled = false }) => (
  <div className="checkbox-container">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="checkbox-input"
      disabled={disabled}
    />
    <label>{label}</label>
  </div>
);

const AttachmentManager = ({ attachments, onUpload, onRemove, disabled, setFormData, incidentId, selectedIncident = null }) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const canonicalId = getCanonicalIncidentId(selectedIncident) || (incidentId ? String(incidentId).trim() : null);

    console.log('🔍 AttachmentManager canonical incidentId:', canonicalId, 'raw incidentId:', incidentId, 'selectedIncident:', selectedIncident);

    if (!canonicalId) {
      const uploadError = 'Cannot upload attachment without a canonical incident ID.';
      setError(uploadError);
      console.warn(uploadError, { incidentId, selectedIncident });
      return;
    }

    console.log('🔍 AttachmentManager: Uploading files with canonical incidentId:', canonicalId);

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('incidentId', canonicalId);
      console.log('✅ Added canonical incidentId to formData:', canonicalId);

      try {
        const response = await API.post('/api/incidents/attachments', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 minutes timeout for file uploads
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: percentCompleted
            }));
          }
        });

        if (response.data.success) {
          console.log('File uploaded successfully for canonical incidentId:', canonicalId, response.data);
          // Prefer the server-provided attachment object when available
          const serverAttachment = response.data.attachment;
          const newFile = serverAttachment ? {
            id: serverAttachment.id || response.data.fileId,
            name: serverAttachment.name || response.data.fileName,
            path: serverAttachment.path || serverAttachment.storagePath || response.data.filePath,
            type: serverAttachment.mimetype || serverAttachment.type || file.type,
            size: serverAttachment.size || file.size,
            downloadUrl: getApiResourceUrl(serverAttachment.downloadUrl || `/api/incidents/attachments/${response.data.fileId}`)
          } : {
            id: response.data.fileId,
            name: response.data.fileName,
            path: response.data.filePath,
            type: file.type,
            size: file.size,
            downloadUrl: getApiResourceUrl(`/api/incidents/attachments/${response.data.fileId}`)
          };

          setUploadedFiles(prev => [...prev, newFile]);
          onUpload(newFile, !!response.data.linkedToIncident);
          
          setUploadProgress(prev => {
            const newProgress = {...prev};
            delete newProgress[file.name];
            return newProgress;
          });
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        setError(`Failed to upload ${file.name}: ${error.response?.data?.message || error.message}`);
        
        setUploadProgress(prev => {
          const newProgress = {...prev};
          delete newProgress[file.name];
          return newProgress;
        });
      }
    }
  };

  const handleRemoveAttachment = async (fileId) => {
    try {
      if (window.confirm('Are you sure you want to remove this attachment?')) {
        await API.delete(`/api/incidents/attachments/${fileId}`);
        onRemove(fileId);
      }
    } catch (error) {
      console.error('Error removing attachment:', error);
      setError(`Failed to remove attachment: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div className="attachment-manager">
      <div className="attachment-input">
        <input
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          onChange={handleFileSelect}
          disabled={disabled}
          id="file-upload"
          style={{ display: 'none' }}
        />
        <label htmlFor="file-upload" className={`file-upload-label ${disabled ? 'disabled' : ''}`}>
          <span className="upload-icon">📎</span>
          Select Files...
        </label>
        <span className="file-types">Supported types: Images, PDFs, Documents, Spreadsheets</span>
      </div>

      {error && (
        <div className="upload-error">
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {Object.keys(uploadProgress).length > 0 && (
        <div className="upload-progress">
          {Object.entries(uploadProgress).map(([filename, progress]) => (
            <div key={filename} className="progress-item">
              <div className="progress-filename">{filename}</div>
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${progress}%` }}></div>
              </div>
              <div className="progress-percentage">{progress}%</div>
            </div>
          ))}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="attachments-list">
          <h4>Attached Files ({attachments.length})</h4>
          <ul>
            {attachments.map((file) => (
              <li key={file.id} className="attachment-item">
                <div className="attachment-icon">
                  {file.type?.includes('image') ? '🖼️' : file.type?.includes('pdf') ? '📄' : '📎'}
                </div>
                <div className="attachment-details">
                  <div className="attachment-name">{file.name}</div>
                  <div className="attachment-size">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
                <div className="attachment-actions">
                  <a
                    href={getApiResourceUrl(file.downloadUrl || `/api/incidents/attachments/${file.id}`)}
                    className="btn-download"
                    title="Download file"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ⬇️
                  </a>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(file.id)}
                      className="btn-remove"
                      title="Remove file"
                    >
                      ❌
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const DelegateView = ({ incident, onClose, onComplete }) => {
  const [delegateNotes, setDelegateNotes] = useState('');
  const [delegateFindings, setDelegateFindings] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('handleSubmit called', e);
    if (!delegateNotes) {
      alert('Please enter your notes as delegate');
      return;
    }
    
    if (!isCompleted) {
      alert('Please confirm you have completed all delegate tasks');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await API.post(`/api/incidents/${incident.id}/delegate`, {
        delegateNotes,
        delegateFindings
      });
      
      if (response.data.success) {
        alert('Your delegate tasks have been submitted successfully');
        if (onComplete) onComplete();
      } else {
        alert('Failed to submit: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error submitting delegate tasks:', error);
      alert('Failed to submit: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="delegate-view">
      <div className="delegate-banner" style={{ background: '#e0f7fa', padding: '10px', borderRadius: '5px', marginBottom: '20px' }}>
        <p style={{ color: '#006064' }}><strong>You are viewing this incident as a delegate.</strong> You can only complete the sections below.</p>
      </div>
      
      <div className="form-group">
        <h4 className="mb-3">Incident Details</h4>
        <div className="incident-details" style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
          <p><strong>ID:</strong> {incident.id}</p>
          <p><strong>Title:</strong> {incident.title}</p>
          <p><strong>Date:</strong> {incident.date}</p>
          <p><strong>Location:</strong> {incident.location}</p>
          <p><strong>Description:</strong> {incident.description}</p>
        </div>
      </div>
      
      <div className="form-group">
        <label>Delegate Notes <span className="required">*</span></label>
        <textarea
          value={delegateNotes}
          onChange={(e) => setDelegateNotes(e.target.value)}
          className="form-control"
          rows={5}
          placeholder="Enter your observations and findings as delegate..."
          required
        />
      </div>
      
      <div className="form-group">
        <label>Additional Findings</label>
        <textarea
          value={delegateFindings}
          onChange={(e) => setDelegateFindings(e.target.value)}
          className="form-control"
          rows={3}
          placeholder="Document any additional findings or recommendations..."
        />
      </div>
      
      <div className="form-check">
        <input
          type="checkbox"
          id="delegateCompleted"
          checked={isCompleted}
          onChange={(e) => setIsCompleted(e.target.checked)}
          className="form-check-input"
          required
        />
        <label htmlFor="delegateCompleted" className="form-check-label">
          I confirm I have completed all assigned delegate tasks for this incident
        </label>
      </div>
      
      <div className="form-actions mt-4">
        <Button 
          type="button" 
          onClick={onClose} 
          className="btn-secondary"
        >
          Close
        </Button>
        <Button 
          type="submit" 
          className="btn-primary"
          disabled={loading || !delegateNotes || !isCompleted}
        >
          {loading ? 'Submitting...' : 'Submit Delegate Report'}
        </Button>
      </div>
    </form>
  );
};

const IncidentInvestigationForm = ({ incident, onClose, onIncidentUpdate, fetchRefreshedIncidentById, setIncidents, fetchIncidents, correctIncidentId, setUrlIncidentId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routedIncident = location.state?.incident || null;
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [formData, setFormData] = useState(() => {
    const seed = (incident && typeof incident === 'object') ? incident : {};
    const seedForNormalize = {
      ...seed,
      // Ensure normalizer sees the keys it expects
      date: seed.date || seed.DATE || seed.incidentDate || '',
      LOCATION: seed.LOCATION || seed.location || '',
      DESCRIPTION: seed.DESCRIPTION || seed.description || '',
      // Preserve CONTRIBUTING_FACTORS JSON if present (used to store WCB/OHS answers)
      CONTRIBUTING_FACTORS:
        seed.CONTRIBUTING_FACTORS ||
        seed.contributing_factors ||
        seed.contributingFactors ||
        seed.immediateActions ||
        seed.immediate_actions ||
        '',
      immediateActions: seed.immediateActions || seed.IMMEDIATE_ACTIONS || seed.CONTRIBUTING_FACTORS || '',
      // Arrays/JSON-strings
      unsafeActs: seed.unsafeActs || seed.UNSAFE_ACTS,
      unsafeConditions: seed.unsafeConditions || seed.UNSAFE_CONDITIONS,
      personalFactors: seed.personalFactors || seed.PERSONAL_FACTORS,
      jobFactors: seed.jobFactors || seed.JOB_FACTORS,
      rootCauseAnalysis: seed.rootCauseAnalysis || seed.ROOT_CAUSE_ANALYSIS,
      correctiveActions: seed.correctiveActions || seed.CORRECTIVE_ACTIONS_JSON || seed.correctiveActionsJson,
      attachments: Array.isArray(seed.attachments) ? seed.attachments : (Array.isArray(seed.ATTACHMENTS) ? seed.ATTACHMENTS : []),
      // Delegate fields
      delegateSelected: false,
      delegateEmail: seed.delegateEmail || seed.DELEGATE_EMAIL || '',
      delegateComments: seed.delegateComments || seed.DELEGATE_INSTRUCTIONS || '',
      delegateFindings: seed.delegateFindings || seed.DELEGATE_FINDINGS || '',
      delegateRecommendations: seed.delegateRecommendations || seed.DELEGATE_RECOMMENDATIONS || '',
    };
    return normalizeInvestigationFormData(seedForNormalize);
  });
  
  const [localIncident, setIncident] = useState(incident || {});
  const [originalReportAttachments, setOriginalReportAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [correctiveActionIndex, setCorrectiveActionIndex] = useState(0);
  
  const [expandedSections, setExpandedSections] = useState({ workflow: false, originalIncident: false });
  const [delegateSelected, setDelegateSelected] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [usersDirectory, setUsersDirectory] = useState([]);
  const [incidentType, setIncidentType] = useState("");
  const [hasDraft, setHasDraft] = useState(false);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState({
    isDelegate: false,
    canEdit: false,
    canView: false,
    canAssignDelegate: false,
    canApproveInvestigation: false,
    canRejectInvestigation: false,
    canManageCorrectiveActions: false
  });
  const [authError, setAuthError] = useState(false);

  const canonicalIncidentId = useMemo(() => {
    const resolvedId = getCanonicalIncidentId(routedIncident) || getCanonicalIncidentId(localIncident) || getCanonicalIncidentId(incident) || (correctIncidentId ? String(correctIncidentId).trim() : null);
    console.log('🔍 Dashboard incidentId (canonical):', resolvedId, { routedIncident, incident, localIncident, correctIncidentId });
    return resolvedId;
  }, [routedIncident, localIncident, incident, correctIncidentId]);
  const [error, setError] = useState(null);

  const [approvalForm, setApprovalForm] = useState({
    feedback: '',
    showApprovalSection: false,
    loading: false,
    error: null
  });

  const [savedData, setSavedData] = useState(null);
  const [viewMode, setViewMode] = useState('edit');
  const currentUserEmail = localStorage.getItem('userEmail') || '';
  const isAssignedFireInvestigator = useMemo(() => (
    isAssignedFireInvestigatorForIncident(localIncident || incident || routedIncident || {}, currentUserEmail)
  ), [localIncident, incident, routedIncident, currentUserEmail]);
  const isWcbReadOnlyViewer = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get('wcbReadonly') === '1' && hasWcbDashboardAccessEmail(localStorage.getItem('userEmail') || '');
  }, [location.search]);
  const canEditLeadershipSection = canEditLeadershipInvestigationFields({
    role: userRole || localStorage.getItem('userRole') || 'USER',
    email: currentUserEmail,
    isDelegate: userPermissions.isDelegate,
    isOverride: isAssignedFireInvestigator
  });
  const leadershipFieldsDisabled = viewMode === 'locked' || !canEditLeadershipSection;
  const getApprovalContext = () => {
    const currentIncident = localIncident || incident || {};
    const normalizedUserRole = String(userRole || '').toUpperCase();
    const normalizedStatus = String(
      resolveInvestigationStatus(currentIncident, incident || {}) ||
      currentIncident.investigationStatus ||
      currentIncident.INVESTIGATION_STATUS ||
      ''
    ).toUpperCase();
    const supervisorApproved = String(currentIncident.SUPERVISOR_APPROVAL || '').toUpperCase() === 'APPROVED';
    const managerApproved = String(currentIncident.MANAGER_APPROVAL || '').toUpperCase() === 'APPROVED';
    const isSupervisorRole = normalizedUserRole === 'SUPERVISOR' || normalizedUserRole === 'FIELD_SUPERVISOR' || isAssignedFireInvestigatorForIncident(currentIncident, currentUserEmail);
    const isAdminRole = normalizedUserRole === 'ADMIN';
    const isManagerRole = normalizedUserRole === 'MANAGER';
    const isCraigManager = isCraigManagerForIncident(currentIncident);
    const canManagerReview = isManagerRole || isCraigManager || isAdminRole;
    const isCompleted = normalizedStatus === 'COMPLETED' || (supervisorApproved && managerApproved);

    let approvalActorRole = null;
    if (isAdminRole) {
      approvalActorRole = supervisorApproved || normalizedStatus === 'SUPERVISOR_APPROVED'
        ? 'MANAGER'
        : 'SUPERVISOR';
    } else if (isSupervisorRole) {
      approvalActorRole = 'SUPERVISOR';
    } else if (canManagerReview) {
      approvalActorRole = 'MANAGER';
    }

    const canShowApprovalSection = Boolean(approvalActorRole) && !isCompleted && !isWcbReadOnlyViewer;

    return {
      normalizedUserRole,
      normalizedStatus,
      approvalActorRole,
      canShowApprovalSection,
      shouldDisableSubmit: Boolean(approvalActorRole),
      isManagerRole,
      isSupervisorRole,
      isAdminRole
    };
  };

  const approvalContext = getApprovalContext();
  const revisionFeedback = String(
    localIncident?.MANAGER_NOTES ||
    localIncident?.managerNotes ||
    localIncident?.MANAGER_FEEDBACK ||
    localIncident?.managerFeedback ||
    incident?.MANAGER_NOTES ||
    incident?.managerNotes ||
    incident?.MANAGER_FEEDBACK ||
    incident?.managerFeedback ||
    ''
  ).trim();

  // Track when formData was last set by local actions to avoid overwriting recent local updates
  const formDataLastSetRef = useRef(0);
  const setFormDataSafe = (payload) => {
    try { if (typeof window !== 'undefined') window.__lastFormDataSnapshot = payload; } catch (e) {}
    formDataLastSetRef.current = Date.now();
    setFormData(payload);
  };

  const isFormLocked = useMemo(() => {
    

    const status = (localIncident?.investigationStatus || '').toUpperCase();
    const supervisorSignOff = localIncident?.SUPERVISOR_SIGN_OFF === 1;
    const managerSignOff = localIncident?.MANAGER_SIGN_OFF === 1;
    const supervisorApproved = (localIncident?.SUPERVISOR_APPROVAL || '').toUpperCase() === 'APPROVED';
    const managerApproved = (localIncident?.MANAGER_APPROVAL || '').toUpperCase() === 'APPROVED';

    return status === 'COMPLETED'
      || (supervisorSignOff && managerSignOff)
      || (supervisorApproved && managerApproved);
  }, [localIncident]);

  const canSaveDraft = !isWcbReadOnlyViewer && !isFormLocked && Boolean(approvalContext.approvalActorRole);

  const visibleAttachments = useMemo(() => {
    const attachmentSources = [
      localIncident?.attachments,
      localIncident?.ATTACHMENTS,
      incident?.attachments,
      incident?.ATTACHMENTS,
      formData?.attachments
    ];

    const normalizedAttachments = [];
    const seenAttachmentIds = new Set();

    attachmentSources.forEach((attachmentsList) => {
      if (!Array.isArray(attachmentsList)) return;

      attachmentsList.forEach((attachment, index) => {
        if (!attachment) return;

        const attachmentId = attachment.FILEID || attachment.fileid || attachment.id || attachment.ID || '';
        const attachmentName = attachment.ORIGINALNAME || attachment.originalname || attachment.name || attachment.filename || attachment.FILENAME || '';
        const dedupeKey = String(attachmentId || `${attachmentName}-${index}`).trim();

        if (dedupeKey && seenAttachmentIds.has(dedupeKey)) {
          return;
        }

        if (dedupeKey) {
          seenAttachmentIds.add(dedupeKey);
        }

        normalizedAttachments.push({
          id: attachmentId,
          name: attachmentName || `Attachment ${normalizedAttachments.length + 1}`,
          size: parseInt(attachment.FILESIZE || attachment.filesize || attachment.size || 0, 10) || 0,
          type: attachment.MIMETYPE || attachment.mimetype || attachment.type || '',
          downloadUrl: attachment.downloadUrl || (attachmentId ? `/api/incidents/attachments/${attachmentId}` : undefined)
        });
      });
    });

    return normalizedAttachments;
  }, [formData?.attachments, incident?.ATTACHMENTS, incident?.attachments, localIncident?.ATTACHMENTS, localIncident?.attachments]);

  const originalIncidentAttachments = useMemo(() => {
    const attachmentSources = [
      originalReportAttachments,
      localIncident?.attachments,
      localIncident?.ATTACHMENTS,
      incident?.attachments,
      incident?.ATTACHMENTS
    ];

    const mergedAttachments = [];
    const seenAttachmentKeys = new Set();

    attachmentSources.forEach((attachmentsList) => {
      if (!Array.isArray(attachmentsList)) return;

      attachmentsList.forEach((attachment, index) => {
        if (!attachment || attachment.isInvestigationAttachment) return;
        if (attachment.source && !['incident', 'original'].includes(attachment.source)) return;

        const attachmentId = attachment.fileId
          || attachment.fileid
          || attachment.FILEID
          || attachment.FILE_ID
          || attachment.id
          || attachment.ID
          || `${attachment.ORIGINALNAME || attachment.originalname || attachment.FILENAME || attachment.filename || attachment.name || 'attachment'}-${index}`;

        const dedupeKey = String(attachmentId);
        if (seenAttachmentKeys.has(dedupeKey)) return;

        seenAttachmentKeys.add(dedupeKey);
        mergedAttachments.push(attachment);
      });
    });

    return mergedAttachments;
  }, [incident?.ATTACHMENTS, incident?.attachments, localIncident?.ATTACHMENTS, localIncident?.attachments, originalReportAttachments]);

  const renderOriginalReportSubmittedLinks = (label = 'Submitted Files / Images') => {
    console.log('ATTACHMENTS:', originalIncidentAttachments);

    return (
      <div className="detail-item full-width">
        <span className="label">{label}:</span>
        <div className="value">
          {originalIncidentAttachments.length > 0 ? (
            <ul className="attachments-display" style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
              {originalIncidentAttachments.map((attachment, index) => {
                const fileId = attachment.fileId
                  || attachment.fileid
                  || attachment.FILEID
                  || attachment.FILE_ID
                  || attachment.id
                  || attachment.ID;

                const attachmentUrl = attachment.downloadUrl
                  ? getApiResourceUrl(attachment.downloadUrl)
                  : (fileId ? getApiResourceUrl(`/api/incidents/attachments/${fileId}`) : '');
                const attachmentName = attachment.ORIGINALNAME
                  || attachment.originalname
                  || attachment.FILENAME
                  || attachment.filename
                  || attachment.name
                  || `Submitted Attachment ${index + 1}`;
                const attachmentType = attachment.type || attachment.mimetype || attachment.MIMETYPE || '';
                const isImage = typeof attachmentType === 'string' && attachmentType.toLowerCase().includes('image');

                return (
                  <li key={`${fileId || attachmentName}-${index}`} className="attachment-item" style={{ marginBottom: '12px' }}>
                    {attachmentUrl ? (
                      isImage ? (
                        <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" className="attachment-link">
                          <img
                            src={attachmentUrl}
                            alt={attachmentName}
                            style={{ maxWidth: '200px', display: 'block', marginBottom: '6px' }}
                          />
                          <span>🖼️ {attachmentName}</span>
                        </a>
                      ) : (
                        <a
                          href={attachmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="attachment-link"
                        >
                          📎 {attachmentName}
                        </a>
                      )
                    ) : (
                      <span>{attachmentName}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <span>None attached</span>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    setIncident(incident);
  }, [incident]);

  // If the fetchIncidentById stored a server-processed snapshot on window, apply it to formData here
  useEffect(() => {
    try {
      const snap = typeof window !== 'undefined' ? window.__serverProcessedIncidentSnapshot : null;
      if (snap) {
        console.log('Applying server-processed incident snapshot to formData from useEffect');
        try { setFormDataSafe(normalizeInvestigationFormData(snap)); } catch (e) { console.warn('Failed setFormDataSafe from snapshot', e); }
        try { setSavedData(normalizeInvestigationFormData(snap)); } catch (e) {}
        try { delete window.__serverProcessedIncidentSnapshot; } catch (e) {}
      }
    } catch (e) { /* ignore */ }
  }, [selectedIncident]);

  useEffect(() => {
    if (!incident || !incident.id) return;

    let didPrompt = false;

    const loadIncidentData = async () => {
      if (!incident || !incident.id) return;

      try {
        // Determine canonical id to fetch: prefer parent INCIDENT_ID when present
          const canonicalFetchId = canonicalIncidentId;
          let attachmentFetchId = canonicalFetchId;
          console.log('Loading incident data. Using fetch id:', canonicalFetchId, 'original incident.id:', incident.id, 'incident object sample:', { ID: incident?.ID, INCIDENT_ID: incident?.INCIDENT_ID });
          validateIncidentIdForFetch(canonicalFetchId, 'loadIncidentData', window.__incidentsCache || [], incident);

          // Fetch fresh data from server using canonicalFetchId via centralized API client.
          // This ensures baseURL/auth are consistent and avoids direct fetch() to wrong ports.
          let freshData = null;
          try {
            const resp = await API.get(`/api/incidents/${canonicalFetchId}`);
            freshData = Array.isArray(resp.data) ? resp.data[0] : resp.data;
          } catch (err) {
            console.warn(`GET /api/incidents/${canonicalFetchId} failed:`, err && (err.message || err.response && err.response.status));
            // If 404, try to resolve canonical parent by scanning the incidents list for a matching id
            if (err && err.response && err.response.status === 404) {
              try {
                const listResp = await API.get('/api/incidents');
                const list = Array.isArray(listResp.data) ? listResp.data : (listResp.data && listResp.data.incidents) || [];
                const candidate = list.find(it => {
                  const ids = [it.ID, it.id, it.incident_id, it.INCIDENT_ID].filter(Boolean).map(String);
                  return ids.includes(String(canonicalFetchId));
                });
                if (candidate) {
                  const parentId = candidate.ID || candidate.id || candidate.INCIDENT_ID || candidate.incident_id;
                  const parentResp = await API.get(`/api/incidents/${parentId}`);
                  freshData = Array.isArray(parentResp.data) ? parentResp.data[0] : parentResp.data;
                  attachmentFetchId = parentId;
                  console.log('Resolved parent incident id via list scan:', parentId);
                } else {
                  // Could not resolve via list — rethrow original error
                  throw err;
                }
              } catch (listErr) {
                console.warn('Failed to resolve parent incident from list after 404:', listErr && listErr.message);
                throw err;
              }
            } else {
              throw err;
            }
          }

          attachmentFetchId = freshData?.ID
            || freshData?.id
            || freshData?.INCIDENT_ID
            || freshData?.incident_id
            || freshData?.incidentId
            || attachmentFetchId;

          try {
            const attachmentResp = await API.get(`/api/incidents/${attachmentFetchId}/attachments`);
            const explicitAttachments = Array.isArray(attachmentResp?.data?.attachments)
              ? attachmentResp.data.attachments
              : (Array.isArray(attachmentResp?.data) ? attachmentResp.data : []);

            const mergedAttachments = mergeIncidentAttachmentLists(
              freshData?.ATTACHMENTS,
              freshData?.attachments,
              explicitAttachments
            );

            setOriginalReportAttachments(mergedAttachments);

            if (mergedAttachments.length > 0) {
              freshData.ATTACHMENTS = mergedAttachments;
              freshData.attachments = mergedAttachments;
            }
          } catch (attachmentErr) {
            console.warn(`Failed to fetch explicit attachments for incident ${attachmentFetchId}:`, attachmentErr && (attachmentErr.message || attachmentErr.response?.status));
          }

          // Try merging investigation-row fields (from INCIDENT_INVESTIGATIONS) so the form
          // has access to IMMEDIATE_ACTIONS / CONTRIBUTING_FACTORS / INVESTIGATION_STATUS etc.
          // Run this regardless of which GET path produced freshData.
          try {
            if (freshData && typeof freshData === 'object') {
              const probeIds = Array.from(new Set([
                canonicalFetchId,
                incident.id,
                incident?.ID,
                incident?.INCIDENT_ID,
                incident?.incident_id,
                incident?.incidentId,
                freshData?.id,
                freshData?.ID,
                freshData?.INCIDENT_ID,
                freshData?.incident_id,
                freshData?.incidentId
              ].filter(v => v !== undefined && v !== null && String(v).trim() !== '').map(v => String(v))));

              try {
                if (typeof window !== 'undefined') window.__lastInvestigationProbeIds = probeIds;
              } catch (e) {}
              console.debug('Investigation probe candidates (rehydration):', probeIds);

              let invRow = null;
              let invHitId = null;
              for (const pid of probeIds) {
                // eslint-disable-next-line no-await-in-loop
                const candidate = await fetchInvestigationForIncident(pid).catch(() => null);
                if (candidate && typeof candidate === 'object') {
                  invRow = candidate;
                  invHitId = pid;
                  break;
                }
              }

              try {
                if (typeof window !== 'undefined') window.__lastInvestigationProbeHit = { invHitId, invRow };
              } catch (e) {}

              if (!invRow) {
                console.debug('Investigation probe found no row (rehydration).');
              }

              if (invRow && typeof invRow === 'object') {
                console.debug('Investigation probe hit for id:', invHitId);
                const tryParseJsonObject = (raw) => {
                  try {
                    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
                    if (typeof raw !== 'string') return null;
                    const trimmed = raw.trim();
                    if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
                    const parsed = JSON.parse(trimmed);
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
                    return null;
                  } catch {
                    return null;
                  }
                };

                const hasReportingKeys = (raw) => {
                  const obj = tryParseJsonObject(raw);
                  if (!obj) return false;
                  return obj.wcbReportRequired != null || obj.ohsReportRequired != null;
                };

                const invKeys = [
                  'INVESTIGATION_STATUS','CONTRIBUTING_FACTORS','CORRECTIVE_ACTIONS_JSON','ROOT_CAUSE',
                  'UNSAFE_ACTS','UNSAFE_CONDITIONS','PERSONAL_FACTORS','JOB_FACTORS','IMMEDIATE_ACTIONS','CORRECTIVE_ACTIONS'
                ];
                invKeys.forEach(k => {
                  try {
                    const v = invRow[k] !== undefined ? invRow[k] : (invRow[k.toLowerCase?.()] !== undefined ? invRow[k.toLowerCase()] : undefined);
                    if (v !== undefined && v !== null) {
                      // Prefer investigation-row JSON for CONTRIBUTING_FACTORS when it contains WCB/OHS keys.
                      if (k === 'CONTRIBUTING_FACTORS') {
                        const existing = freshData.CONTRIBUTING_FACTORS ?? freshData.contributingFactors ?? freshData.immediateActions;
                        const existingObj = tryParseJsonObject(existing);
                        const incomingObj = tryParseJsonObject(v);
                        const shouldPreferIncoming = hasReportingKeys(v) || (!!incomingObj && !existingObj);
                        if (shouldPreferIncoming) {
                          freshData.CONTRIBUTING_FACTORS = v;
                          freshData.contributingFactors = v;
                          freshData.IMMEDIATE_ACTIONS = v;
                          freshData.immediateActions = v;
                          return;
                        }
                      }

                      if (freshData[k] === undefined || freshData[k] === null || freshData[k] === '' || (Array.isArray(freshData[k]) && freshData[k].length === 0)) {
                        freshData[k] = v;
                      }
                      if (k === 'CONTRIBUTING_FACTORS') {
                        freshData.IMMEDIATE_ACTIONS = freshData.IMMEDIATE_ACTIONS || v;
                        freshData.immediateActions = freshData.immediateActions || v;
                      }
                      if (k === 'CORRECTIVE_ACTIONS_JSON') {
                        freshData.CORRECTIVE_ACTIONS_JSON = freshData.CORRECTIVE_ACTIONS_JSON || v;
                        freshData.correctiveActions = freshData.correctiveActions || v;
                      }
                    }
                  } catch (e) { /* ignore per-key */ }
                });
                console.debug('Merged investigation row into incident payload for normalization (rehydration)', { invHitId });
              }
            }
          } catch (mergeErr) {
            console.debug('Investigation merge error (non-fatal):', mergeErr && mergeErr.message);
          }

        // ...parse server data as before...

        try {
          const resolvedFreshStatus = resolveInvestigationStatus(freshData || {}, incident || {});
          const freshAttachments = mergeIncidentAttachmentLists(freshData?.ATTACHMENTS, freshData?.attachments);

          setIncident((prev) => ({
            ...(prev || {}),
            ...(freshData || {}),
            attachments: freshAttachments.length > 0
              ? freshAttachments
              : mergeIncidentAttachmentLists(prev?.ATTACHMENTS, prev?.attachments),
            ATTACHMENTS: freshAttachments.length > 0
              ? freshAttachments
              : mergeIncidentAttachmentLists(prev?.ATTACHMENTS, prev?.attachments),
            investigationStatus: resolvedFreshStatus,
            INVESTIGATION_STATUS: resolvedFreshStatus
          }));
        } catch (syncErr) {
          console.warn('Failed to sync refreshed incident data into local state:', syncErr);
        }

        const serverDate = new Date(freshData.UPDATED_AT || freshData.updatedAt || 0);

        if (!didPrompt && !isFormLocked) {
          const storedDrafts = localStorage.getItem('fireIncidentInvestigationDrafts');
          if (storedDrafts) {
            const draftsMap = JSON.parse(storedDrafts);
            const draftData = draftsMap[incident.id];

            if (draftData) {
              const draftDate = new Date(draftData.lastSaved || 0);

              // Only prompt if draft is NEWER than server data
              const serverWorkflowStatus = String(resolveInvestigationStatus(freshData || {}, incident || {}) || '').toUpperCase();
              const canAutoApplyDraft = !['SUBMITTED', 'SUPERVISOR_APPROVED', 'NEEDS_REVISION', 'COMPLETED'].includes(serverWorkflowStatus);

              if (draftDate > serverDate && canAutoApplyDraft) {
                didPrompt = true;
                  const _draftNormalized = normalizeInvestigationFormData(draftData);
                  console.debug('DEBUG: about to setFormData from draft normalized payload:', _draftNormalized);
                  setFormData(_draftNormalized);
                  // verify after state update (async)
                  setTimeout(() => console.debug('DEBUG: formData after setting draft payload (approx):', typeof window !== 'undefined' ? window.__lastFormDataSnapshot : 'n/a'), 50);
                  setHasDraft(true);
                  setIsDraftLoaded(true);
                  return;
              } else if (draftDate > serverDate) {
                setHasDraft(true);
              } else {
                // Draft is older or same as server, do NOT prompt
                setHasDraft(true);
              }
            }
          }
        }

  // Debug: capture the raw inputs the normalizer will use
  try {
    if (typeof window !== 'undefined') {
      window.__lastFreshDataBeforeNormalize = {
        id: freshData && (freshData.id || freshData.ID || freshData.INCIDENT_ID || freshData.incident_id),
        CONTRIBUTING_FACTORS: freshData && (freshData.CONTRIBUTING_FACTORS || freshData.contributing_factors || freshData.contributingFactors),
        immediateActions: freshData && (freshData.immediateActions || freshData.IMMEDIATE_ACTIONS || freshData.immediate_actions),
        wcbReportRequired: freshData && freshData.wcbReportRequired,
        WCB_REPORT_REQUIRED: freshData && freshData.WCB_REPORT_REQUIRED,
        ohsReportRequired: freshData && freshData.ohsReportRequired,
        OHS_REPORT_REQUIRED: freshData && freshData.OHS_REPORT_REQUIRED
      };
    }
  } catch (e) { /* ignore */ }

  const _freshNormalized = normalizeInvestigationFormData(freshData);
  // Lightweight safe setter: avoid clobbering formData that was very recently set locally (e.g. right after a POST)
  console.debug('DEBUG: about to setFormData from fresh server payload:', _freshNormalized);
  try {
    if (!formDataLastSetRef) formDataLastSetRef = { current: 0 };
  } catch (e) {}
  const now = Date.now();
  if (!formDataLastSetRef.current || (now - formDataLastSetRef.current) > 1000) {
    setFormDataSafe(_freshNormalized);
    setSavedData(_freshNormalized);
  } else {
    console.log('Skipping server fresh payload setFormData because local formData was updated recently');
  }
  setTimeout(() => console.debug('DEBUG: formData after attempting to set fresh server payload (approx):', typeof window !== 'undefined' ? window.__lastFormDataSnapshot : 'n/a'), 50);
        // ...rest of your logic...
      } catch (error) {
        console.error('Error loading incident data:', error);
        setError('Failed to load incident details. Please try again.');
      }
    };

    loadIncidentData();
  }, [canonicalIncidentId, incident?.id]);

  const checkDelegateStatus = async (incidentId) => {
    if (!incidentId) return false;
    
    try {
      const token = localStorage.getItem('authToken');
      const currentUserEmail = localStorage.getItem('userEmail') || '';
      
      if (!token) {
        console.log('No auth token found');
        setAuthError(true);
        return false;
      }
      
      // Check delegate status from current incident data instead of API call
      if (incident) {
        const delegateEmail = incident.DELEGATE_EMAIL || incident.delegateEmail;
        const isDelegate = delegateEmail && 
                          currentUserEmail && 
                          delegateEmail.toLowerCase() === currentUserEmail.toLowerCase();
        
        setUserPermissions(prev => ({
          ...prev,
          isDelegate: isDelegate
        }));
        
        return isDelegate;
      }
      
      return false;
      
    } catch (error) {
      console.error('Error checking delegate status:', error);
      setError(`Error checking permissions: ${error.message}`);
      return false;
    }
  };

const checkRolePermissions = async (incidentId) => {
  try {
    const response = await API.get(`/api/auth/me`);
    const userRoleRaw = response.data.role || response.data?.user?.role || 'USER';
    let userRole = String(userRoleRaw).toUpperCase();
    const currentUserEmail = localStorage.getItem('userEmail') || '';
    // If backend returns generic 'USER' but localStorage has a more specific role, prefer it for UI permissions
    try {
      const localRoleRaw = localStorage.getItem('userRole') || '';
      const localRole = String(localRoleRaw).toUpperCase();
      if ((userRole === 'USER' || !userRole) && localRole && localRole !== 'USER') {
        console.log('Overriding API role with localStorage role for UI:', localRole);
        userRole = localRole;
      }
    } catch (e) {
      // ignore localStorage failures
    }
    console.log('User role loaded from API (final):', userRole);
    setUserRole(userRole);
    
    // Check delegate status from current incident data instead of separate API call
    const delegateEmail = incident?.DELEGATE_EMAIL || incident?.delegateEmail;
    const isDelegate = delegateEmail && 
                      currentUserEmail && 
                      delegateEmail.toLowerCase() === currentUserEmail.toLowerCase();
    const isAssignedInvestigator = isAssignedFireInvestigatorForIncident(incident || {}, currentUserEmail);
    
    setUserPermissions({
      isDelegate: isDelegate,
      canEdit: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(userRole) ||
               isAssignedInvestigator ||
               isDelegate,
      canView: true,
      canAssignDelegate: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(userRole) || isAssignedInvestigator,
      canApproveInvestigation: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER'].includes(userRole) || isAssignedInvestigator,
      canRejectInvestigation: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER'].includes(userRole) || isAssignedInvestigator,
      canManageCorrectiveActions: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(userRole) || 
                                  isAssignedInvestigator ||
                                  isDelegate
    });
  } catch (error) {
    console.error('Error checking role permissions:', error);
    const fallbackRoleRaw = (localStorage.getItem('userRole') || 'USER');
    const fallbackRole = String(fallbackRoleRaw).toUpperCase();
    const fallbackEmail = localStorage.getItem('userEmail') || '';
    const isAssignedInvestigator = isAssignedFireInvestigatorForIncident(incident || {}, fallbackEmail);
    console.log('Using fallback role:', fallbackRole);
    setUserRole(fallbackRole);
    setUserPermissions({
      isDelegate: false,
      canEdit: isAssignedInvestigator,
      canView: true,
      canAssignDelegate: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(fallbackRole) || isAssignedInvestigator,
      canApproveInvestigation: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER'].includes(fallbackRole) || isAssignedInvestigator,
      canRejectInvestigation: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER'].includes(fallbackRole) || isAssignedInvestigator,
      canManageCorrectiveActions: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(fallbackRole) || isAssignedInvestigator
    });
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (option) => {
    setFormData((prev) => {
      const newUnsafeActs = prev.unsafeActs.includes(option)
        ? prev.unsafeActs.filter((act) => act !== option)
        : [...prev.unsafeActs, option];
      return { ...prev, unsafeActs: newUnsafeActs };
    });
  };

  const handleFileUpload = (e) => {
    setFormData((prev) => ({ 
      ...prev, 
      attachments: e.target.files ? Array.from(e.target.files) : [] 
    }));
  };

  const addCorrectiveAction = () => {
    const newAction = {
      id: Date.now(),
      description: '',
      assignedTo: '',
      targetDate: '',
      status: 'ASSIGNED',
      completedDate: null
    };
    
    setFormData((prev) => ({
      ...prev,
      correctiveActions: [...prev.correctiveActions, newAction]
    }));
    setCorrectiveActionIndex(formData.correctiveActions.length);
  };

    const updateCorrectiveAction = (index, field, value) => {
    const updatedActions = [...formData.correctiveActions];
    
    // If updating the target date, validate it's in the future
    if (field === "targetDate" && value) {
      const selectedDate = new Date(value);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      if (selectedDate < tomorrow) {
        setErrors(prev => ({
          ...prev,
          [`correctiveAction-${index}-targetDate`]: "Target date must be in the future"
        }));
        return; // Don't update the value if it's invalid
      }
    }
    
    if (field === "status" && value === "COMPLETED" && updatedActions[index].status !== "COMPLETED") {
      updatedActions[index] = {
        ...updatedActions[index],
        [field]: value,
        completedDate: new Date().toISOString().split('T')[0]
      };
    } else {
      updatedActions[index] = {
        ...updatedActions[index],
        [field]: value
      };
    }
    
    setFormData((prev) => ({
      ...prev,
      correctiveActions: updatedActions
    }));
    
    if (errors[`correctiveAction-${index}-${field}`]) {
      const newErrors = {...errors};
      delete newErrors[`correctiveAction-${index}-${field}`];
      setErrors(newErrors);
    }
  };

  const removeCorrectiveAction = (index) => {
    const updatedActions = [...formData.correctiveActions];
    updatedActions.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      correctiveActions: updatedActions
    }));
    if (correctiveActionIndex >= index) {
      setCorrectiveActionIndex(Math.max(0, correctiveActionIndex - 1));
    }
  };

  const validateForm = (data) => {
    const newErrors = {};

    const hasRootCauseSelection = Array.isArray(data.rootCauseAnalysis) && data.rootCauseAnalysis.some((entry) => {
      if (typeof entry === 'string') {
        return entry.trim().length > 0;
      }

      if (entry && typeof entry === 'object' && entry.other !== undefined) {
        return String(entry.other || '').trim().length > 0;
      }

      return false;
    });

    if (!hasRootCauseSelection) {
      newErrors.rootCauseAnalysis = 'Please select at least one Root Cause Analysis option';
    }

    const wcbRequired = String(data.wcbReportRequired || 'No').trim().toLowerCase() === 'yes';
    if (wcbRequired) {
      if (!String(data.wcbReportReason || '').trim()) {
        newErrors.wcbReportReason = 'Please select a reason for WCB reporting';
      }
      if (String(data.wcbReportReason || '').trim().toLowerCase() === 'other' && !String(data.wcbReportReasonOther || '').trim()) {
        newErrors.wcbReportReasonOther = 'Please specify the WCB reporting reason';
      }
    }

    const ohsRequired = String(data.ohsReportRequired || 'No').trim().toLowerCase() === 'yes';
    if (ohsRequired) {
      if (!String(data.ohsReportReason || '').trim()) {
        newErrors.ohsReportReason = 'Please select a reason for OHS reporting';
      }
      if (!String(data.ohsReportReference || '').trim()) {
        newErrors.ohsReportReference = 'Please enter the OHS report reference (date + file #)';
      }
    }
    
    data.correctiveActions.forEach((action, index) => {
      if (!action.description?.trim()) {
        newErrors[`correctiveAction-${index}-description`] = "Description is required";
      }
      if (!action.assignedTo?.trim()) {
        newErrors[`correctiveAction-${index}-assignedTo`] = "Assignment is required";
      }
      
      // Target date validation
      if (!action.targetDate) {
        newErrors[`correctiveAction-${index}-targetDate`] = "Target date is required";
      } else {
        // Check if target date is in the future
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        
        const selectedDate = new Date(action.targetDate);
        
        if (selectedDate < tomorrow) {
          newErrors[`correctiveAction-${index}-targetDate`] = "Target date must be in the future";
        }
      }
    });
    
    return newErrors;
  };

  const handleSaveDraft = () => {
    if (isWcbReadOnlyViewer) {
      alert('This investigation is read-only for WCB reviewers. Draft changes cannot be saved.');
      return;
    }

    if (!canSaveDraft) {
      alert('Draft saving is only available while a supervisor or manager review is in progress.');
      return;
    }

    const draftKey = getDraftStorageKey(incident);
    if (draftKey) {
      try {
        const draftsMap = readInvestigationDraftMap();
        const draftWithMeta = {
          ...normalizeInvestigationFormData(formData),
          lastSaved: new Date().toISOString(),
          incidentId: draftKey,
          investigationStatus: resolveInvestigationStatus(localIncident || incident || {}, incident || {}) || 'PENDING'
        };
        draftsMap[draftKey] = draftWithMeta;
        writeInvestigationDraftMap(draftsMap);
        setHasDraft(true);
        setIsDraftLoaded(true);
        console.log('Draft saved:', draftWithMeta);
        alert('Investigation saved as draft.');
      } catch (error) {
        console.error('Error saving draft:', error);
        alert('Failed to save draft. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      e.nativeEvent &&
      e.nativeEvent.submitter &&
      e.nativeEvent.submitter.type !== 'submit'
    ) {
      console.warn('Form submit blocked: not triggered by submit button');
      return;
    }
    console.log('handleSubmit called', e);

    if (isWcbReadOnlyViewer) {
      alert('This investigation is read-only for WCB reviewers. Submitting changes is disabled.');
      return;
    }
    
    if (viewMode === 'locked') {
      alert('This investigation has been approved and is now locked for editing.');
      return;
    }
    
    const formErrors = validateForm(formData);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    try {
      console.log('FORM->DB MAPPINGS:', {
        'FORM: immediateActions': formData.immediateActions,
        'Sent as CONTRIBUTING_FACTORS to DB': formData.immediateActions,
        'FORM: unsafeConditions': formData.unsafeConditions,
        'Sent as UNSAFE_CONDITIONS to DB': formData.unsafeConditions,
        'FORM: personalFactors': formData.personalFactors,
        'Sent as PERSONAL_FACTORS to DB': formData.personalFactors,
        'FORM: jobFactors': formData.jobFactors,
        'Sent as JOB_FACTORS to DB': formData.jobFactors,
        'FORM: rootCause': formData.rootCause,
        'Sent as ROOT_CAUSE to DB': formData.rootCause,
        'FORM: rootCauseAnalysis': formData.rootCauseAnalysis,
        'Sent as ROOT_CAUSE_ANALYSIS to DB': formData.rootCauseAnalysis
      });

      console.log('Submitting investigation with data:', formData);
      
      const newStatus = 'SUBMITTED';
      
      // Create a prepared form data object that matches the server's expected keys and types
      // The server expects correctiveActions and arrays as JSON strings (it uses PARSE_JSON on insert)
      const preparedFormData = {
        // use explicit keys the backend reads
        incidentDate: formData.incidentDate || incident.date || '',
        rootCause: formData.rootCause || '',
        correctiveActions: JSON.stringify(formData.correctiveActions || []),
        immediateActions: formData.immediateActions || formData.contributingFactors || formData.CONTRIBUTING_FACTORS || '',
        unsafeActs: JSON.stringify(formData.unsafeActs || []),
        unsafeConditions: JSON.stringify(formData.unsafeConditions || []),
        personalFactors: JSON.stringify(formData.personalFactors || []),
        jobFactors: JSON.stringify(formData.jobFactors || []),
        rootCauseAnalysis: JSON.stringify(formData.rootCauseAnalysis || []),

        // New WCB/OHS reporting questions (investigation)
        wcbReportRequired: formData.wcbReportRequired || 'No',
        wcbReportReason: formData.wcbReportReason || '',
        wcbReportReasonOther: formData.wcbReportReasonOther || '',
        ohsReportRequired: formData.ohsReportRequired || 'No',
        ohsReportReason: formData.ohsReportReason || '',
        ohsReportReference: formData.ohsReportReference || ''
      };

      // Debugging aid: store the exact payload on window for easy inspection.
      // In Chrome DevTools you can run: window.__lastPreparedInvestigationSubmission
      try { window.__lastPreparedInvestigationSubmission = preparedFormData; } catch (e) { /* ignore */ }

      console.log('Prepared data for submission:', preparedFormData);
      console.log('Prepared WCB/OHS fields:', {
        wcbReportRequired: preparedFormData.wcbReportRequired,
        wcbReportReason: preparedFormData.wcbReportReason,
        wcbReportReasonOther: preparedFormData.wcbReportReasonOther,
        ohsReportRequired: preparedFormData.ohsReportRequired,
        ohsReportReason: preparedFormData.ohsReportReason,
        ohsReportReference: preparedFormData.ohsReportReference
      });

  // Remember the original detail-row id we posted to so we can update its UI when heuristics resolve
  const originalDetailId = incident?.id;
      
      // Heuristic-safe submit: POST first, then prefer canonical parent id from POST response.
      // This avoids a 404 when the UI is currently using a detail-row PK instead of the INCIDENTS.ID.
      const postResp = await API.post(`/api/incidents/${incident.id}/investigation`, preparedFormData);

      // Persist the raw POST response to a global snapshot so fetch fallbacks can use it immediately
      try {
        try { window.__lastPostResponseData = postResp; } catch(e) { /* ignore */ }
        try { window.__serverProcessedIncidentSnapshot = postResp && postResp.data ? (Array.isArray(postResp.data) ? postResp.data[0] : postResp.data) : postResp; } catch(e) { /* ignore */ }
        console.log('Stored client-side POST snapshot for fallback use:', window.__serverProcessedIncidentSnapshot);
      } catch(e) {
        // ignore
      }

      // Debug: log POST response shape to help locate canonical id fields
      try {
        console.log('POST /investigation response data sample:', postResp && postResp.data ? postResp.data : postResp);
      } catch (e) {
        /* ignore logging errors */
      }

      // Try to infer the canonical incident id from the POST response (search deeply for INCIDENT_ID or incident_id)
      let updatedIncident = Array.isArray(postResp.data) ? postResp.data[0] : postResp.data || {};
      // If the POST returned a wrapper (e.g. { success: true, incident: {...} }) try to unwrap
      if (updatedIncident && updatedIncident.incident) {
        updatedIncident = updatedIncident.incident;
      } else if (updatedIncident && updatedIncident.investigation) {
        updatedIncident = updatedIncident.investigation;
      }

      // Deep-search helper to find common parent id keys in nested response objects
      const findCanonicalId = (obj) => {
        const seen = new WeakSet();
        const keyRegex = /(^|_|-)?(INCIDENT|INCIDENTS|incident)(_?|-?)(ID|Id|id)?$/i;

        function _search(value) {
          if (value == null) return null;
          // Never accept bare numeric primitives here.
          // We only accept ids when the *key name* indicates it's an incident id.
          if (typeof value === 'string' || typeof value === 'number') return null;
          if (Array.isArray(value)) {
            for (const item of value) {
              const found = _search(item);
              if (found) return found;
            }
            return null;
          }
          if (typeof value === 'object') {
            if (seen.has(value)) return null;
            seen.add(value);
            for (const k of Object.keys(value)) {
              try {
                if (keyRegex.test(k) && value[k] != null && String(value[k]).trim() !== '') {
                  const candidateStr = String(value[k]).trim();
                  if (/^\d+$/.test(candidateStr) && Number(candidateStr) > 0) return candidateStr;
                }
              } catch (e) {}
              const foundNested = _search(value[k]);
              if (foundNested) return foundNested;
            }
            return null;
          }
          return null;
        }

        return _search(obj);
      };

      const canonicalFromPostRaw =
        findCanonicalId(postResp.data) ||
        updatedIncident?.INCIDENT_ID ||
        updatedIncident?.incident_id ||
        updatedIncident?.incidentId ||
        null;
      const canonicalFromPost =
        canonicalFromPostRaw != null && /^\d+$/.test(String(canonicalFromPostRaw)) && Number(canonicalFromPostRaw) > 0
          ? String(canonicalFromPostRaw)
          : null;

      // Decide which id to use for the refresh GET. Default to the original incident.id
      let refreshId = incident.id;
      if (canonicalFromPost) {
        refreshId = canonicalFromPost;
        console.log('Detected canonical parent id from POST response:', refreshId);
      }

      // Attempt to fetch the refreshed canonical incident. If GET fails, fall back to using the POST response body.
      // First try the investigation sub-endpoint which some backends populate immediately after POST
      let finalGetResp = null;
      try {
        try {
          console.log('Attempting query-style investigation lookup for id:', refreshId);
          // Prefer the dedicated helper which accepts either an incidentId or an investigation row id
          const inv = await fetchInvestigationForIncident(refreshId);
          if (inv) {
            console.log('Query-style investigation lookup returned a row for id:', refreshId, inv);
            finalGetResp = { data: inv };
          } else {
            // Fallback to trying the sub-endpoint (some backends populate this) and then canonical incident GET
            console.log('Query-style lookup returned no row, falling back to sub-endpoint/canonical GET for id:', refreshId);
            const invResp = await API.get(`/api/incidents/${refreshId}/investigation`).catch(e => {
              console.warn('Sub-endpoint investigation GET failed for', refreshId, e && e.message);
              return null;
            });
            if (invResp && invResp.data) {
              finalGetResp = { data: invResp.data };
            } else {
              finalGetResp = await API.get(`/api/incidents/${refreshId}`);
            }
          }
        } catch (invErr) {
          // investigation lookup failed - fall back to canonical incident GET
          console.warn('Investigation lookup failed for id', refreshId, invErr && invErr.message);
          finalGetResp = await API.get(`/api/incidents/${refreshId}`);
        }
      } catch (getErr) {
        console.warn('Refresh GET failed for id', refreshId, 'falling back to POST response. Error:', getErr && getErr.message);

        // As a last-ditch try: if the POST hinted at a different canonical id, try fetching that explicitly
        if (canonicalFromPost && String(canonicalFromPost) !== String(incident.id)) {
          try {
            finalGetResp = await API.get(`/api/incidents/${canonicalFromPost}`);
            refreshId = canonicalFromPost;
            console.log('Fallback fetch succeeded for canonical id:', canonicalFromPost);
          } catch (secondGetErr) {
            console.warn('Fallback fetch also failed for canonical id', canonicalFromPost, secondGetErr && secondGetErr.message);
          }
        }
        // If still not found, try a heuristic scan across all incidents to locate the parent by date/location/title
        if (!finalGetResp) {
          try {
            console.log('Attempting heuristic parent lookup via /api/incidents list...');
            const listResp = await API.get('/api/incidents');
            const list = Array.isArray(listResp.data) ? listResp.data : (Array.isArray(listResp.data?.incidents) ? listResp.data.incidents : []);
            const formDate = String(preparedFormData.incidentDate || incident.date || '').split('T')[0];
            const formLoc = String(preparedFormData.location || incident.location || '').toLowerCase().trim();
            const formTitle = String(preparedFormData.description || incident.title || incident.TITLE || '').toLowerCase().trim();

            const candidate = list.find(it => {
              const itDate = String(it.DATE || it.date || '').split('T')[0];
              const itLoc = String(it.LOCATION || it.location || '').toLowerCase().trim();
              const itTitle = String(it.TITLE || it.title || it.ISSUE_DESCRIPTION || '').toLowerCase().trim();

              const dateMatch = formDate && itDate && itDate === formDate;
              const locMatch = formLoc && itLoc && (itLoc === formLoc || itLoc.includes(formLoc) || formLoc.includes(itLoc));
              const titleMatch = formTitle && itTitle && (itTitle.includes(formTitle) || formTitle.includes(itTitle));

              // prefer date + (location or title) match
              return dateMatch && (locMatch || titleMatch);
            });

            if (candidate) {
              const candidateId = candidate.ID || candidate.id || candidate.incident_id || candidate.INCIDENT_ID;
              console.log('Heuristic matched parent incident:', candidateId, candidate);
              refreshId = candidateId;
              try {
                // First try the investigation sub-endpoint on the resolved parent — some backends populate this faster
                  try {
                    // Prefer query-style lookup first
                    const invProbeRow = await fetchInvestigationForIncident(refreshId);
                    if (invProbeRow) {
                      console.log('Heuristic: query-style investigation lookup returned data for parent id:', refreshId, invProbeRow);
                      finalGetResp = { data: invProbeRow };
                    } else {
                      // Fallback to sub-endpoint then canonical GET
                      const invProbe = await API.get(`/api/incidents/${refreshId}/investigation`).catch(e => { console.warn('Heuristic: sub-endpoint investigation GET failed for', refreshId, e && e.message); return null; });
                      if (invProbe && invProbe.data) {
                        finalGetResp = { data: invProbe.data };
                      } else {
                        finalGetResp = await API.get(`/api/incidents/${refreshId}`);
                      }
                    }
                  } catch (invProbeErr) {
                    console.warn('Heuristic: investigation probe failed for parent id', refreshId, invProbeErr && invProbeErr.message);
                    // fall back to canonical incident GET
                    finalGetResp = await API.get(`/api/incidents/${refreshId}`);
                  }
              } catch (e) {
                console.warn('GET on heuristic-matched parent id failed', refreshId, e && e.message);
              }
            } else {
              console.log('Heuristic parent lookup did not find a match');
            }
          } catch (heurErr) {
            console.warn('Error during heuristic parent lookup:', heurErr && heurErr.message);
          }
        }
      }

      let finalData = finalGetResp ? (Array.isArray(finalGetResp.data) ? finalGetResp.data[0] : finalGetResp.data) : updatedIncident;

      // If available, prefer the canonicalized/merged result from fetchRefreshedIncidentById
      // This will attempt to also read the investigation detail row and merge CONTRIBUTING_FACTORS,
      // INVESTIGATION_STATUS and other investigation-level fields into the final data object.
      try {
        if (typeof fetchRefreshedIncidentById === 'function' && refreshId) {
          // Primary probe
          let fetched = null;
          try {
            fetched = await fetchRefreshedIncidentById(refreshId);
            if (fetched && typeof fetched === 'object') {
              finalData = { ...finalData, ...fetched };
              console.log('Submit-flow: merged fetchRefreshedIncidentById result into finalData:', fetched);
            }
          } catch (e) {
            console.warn('Submit-flow: primary fetchRefreshedIncidentById failed for', refreshId, e && e.message);
          }

          // If primary probe returned nothing useful (no INVESTIGATION_STATUS / CONTRIBUTING_FACTORS / immediateActions),
          // attempt fallbacks: original detail-row id (where POST was sent) and any canonical id hinted by the POST response.
          const lacksInvestigationFields = (obj) => {
            if (!obj || typeof obj !== 'object') return true;
            const hasStatus = (obj.INVESTIGATION_STATUS || obj.investigationStatus);
            const hasContrib = (obj.CONTRIBUTING_FACTORS || obj.contributingFactors || obj.immediateActions);
            return !(hasStatus || hasContrib);
          };

          if (lacksInvestigationFields(fetched)) {
            // 1) try original detail id (the id we posted to)
            if (originalDetailId && String(originalDetailId) !== String(refreshId)) {
              try {
                const alt = await fetchRefreshedIncidentById(originalDetailId);
                if (alt && typeof alt === 'object') {
                  finalData = { ...finalData, ...alt };
                  console.log('Submit-flow: fallback merged fetchRefreshedIncidentById(originalDetailId) into finalData:', originalDetailId, alt);
                  fetched = alt;
                }
              } catch (altErr) {
                console.warn('Submit-flow: fallback fetchRefreshedIncidentById failed for originalDetailId', originalDetailId, altErr && altErr.message);
              }
            }

            // 2) try canonical id discovered from POST response (if available)
            try {
              const postHint = (typeof canonicalFromPost !== 'undefined' && canonicalFromPost) ? String(canonicalFromPost) : null;
              if (postHint && postHint !== String(refreshId) && postHint !== String(originalDetailId)) {
                try {
                  const alt2 = await fetchRefreshedIncidentById(postHint);
                  if (alt2 && typeof alt2 === 'object') {
                    finalData = { ...finalData, ...alt2 };
                    console.log('Submit-flow: fallback merged fetchRefreshedIncidentById(canonicalFromPost) into finalData:', postHint, alt2);
                    fetched = alt2;
                  }
                } catch (alt2Err) {
                  console.warn('Submit-flow: fallback fetchRefreshedIncidentById failed for canonicalFromPost', postHint, alt2Err && alt2Err.message);
                }
              }
            } catch (e) {
              // ignore
            }
          }

          // Final fallback: directly probe investigation rows for likely candidate IDs and merge investigation fields
          if (lacksInvestigationFields(fetched)) {
            try {
              const probeCandidates = Array.from(new Set([
                String(originalDetailId || ''),
                String(incident?.id || ''),
                String(finalData?.id || ''),
                String(canonicalFromPost || '')
              ].filter(Boolean)));

              for (const cid of probeCandidates) {
                try {
                  console.log('Submit-flow: probing investigation endpoint for candidate id:', cid);
                  const inv = await fetchInvestigationForIncident(cid);
                  if (inv) {
                    // Merge only investigation-level fields and avoid overwriting authoritative non-empty server fields
                    const pick = (keys) => {
                      keys.forEach(k => {
                        const val = inv[k] ?? inv[k.toUpperCase?.()];
                        if (val !== undefined && val !== null) {
                          // Map common DB keys to UI keys
                          if (k.toUpperCase && k.toUpperCase() === 'CONTRIBUTING_FACTORS') {
                            finalData.CONTRIBUTING_FACTORS = finalData.CONTRIBUTING_FACTORS || val;
                            finalData.contributingFactors = finalData.contributingFactors || val;
                            finalData.immediateActions = finalData.immediateActions || val;
                          } else if (k.toUpperCase && k.toUpperCase() === 'CORRECTIVE_ACTIONS_JSON') {
                            finalData.CORRECTIVE_ACTIONS_JSON = finalData.CORRECTIVE_ACTIONS_JSON || val;
                          } else if (k.toUpperCase && k.toUpperCase() === 'INVESTIGATION_STATUS') {
                            finalData.investigationStatus = finalData.investigationStatus || val;
                            finalData.INVESTIGATION_STATUS = finalData.INVESTIGATION_STATUS || val;
                          } else {
                            // generic mapping
                            finalData[k] = finalData[k] || val;
                          }
                        }
                      });
                    };

                    pick([
                      'INVESTIGATION_STATUS',
                      'CONTRIBUTING_FACTORS',
                      'correctiveActionsJson',
                      'CORRECTIVE_ACTIONS_JSON',
                      'ROOT_CAUSE',
                      'UNSAFE_ACTS',
                      'UNSAFE_CONDITIONS',
                      'PERSONAL_FACTORS',
                      'JOB_FACTORS',
                      // WCB/OHS reporting (investigation)
                      'WCB_REPORT_REQUIRED',
                      'WCB_REPORT_REASON',
                      'WCB_REPORT_REASON_OTHER',
                      'OHS_REPORT_REQUIRED',
                      'OHS_REPORT_REASON',
                      'OHS_REPORT_REFERENCE'
                    ]);

                    // attachments
                    if (Array.isArray(inv.ATTACHMENTS) && inv.ATTACHMENTS.length) {
                      const mergedAttachments = mergeIncidentAttachmentLists(
                        finalData.ATTACHMENTS,
                        finalData.attachments,
                        inv.ATTACHMENTS,
                        inv.attachments
                      );
                      finalData.ATTACHMENTS = mergedAttachments;
                      finalData.attachments = mergedAttachments;
                    }

                    console.log('Submit-flow: merged investigation fields from direct probe for', cid, inv);
                    break; // stop after first successful probe
                  }
                } catch (probeErr) {
                  console.warn('Submit-flow: probe failed for investigation id', cid, probeErr && probeErr.message);
                }
              }
            } catch (e) {
              console.warn('Submit-flow: final investigation probes failed:', e && e.message);
            }
          }
        }
      } catch (e) {
        console.warn('Submit-flow: fetchRefreshedIncidentById overall attempt failed, continuing with existing finalData:', e && e.message);
      }

      // Prefer canonical id from the POST response when available (this is the true INCIDENTS.ID)
      const canonicalFromPostId = canonicalFromPost || updatedIncident?.INCIDENT_ID || updatedIncident?.incident_id || updatedIncident?.incidentId;
      if (canonicalFromPostId) {
        // If finalData appears to be a detail row (or GET failed), enforce canonical id so the UI and subsequent calls use the parent INCIDENTS.ID
        try {
          if (!finalData) finalData = {};
          if (String(finalData.id) !== String(canonicalFromPostId)) {
            console.log('Enforcing canonical INCIDENTS.ID from POST response:', canonicalFromPostId, 'over finalData.id:', finalData.id);
            finalData.id = canonicalFromPostId;
          }
          // also normalize common server fields
          finalData.ID = finalData.ID || canonicalFromPostId;
        } catch (e) {
          console.warn('Failed to enforce canonical id on finalData', e);
        }
      } else {
        // Normalize: if the backend returned a detail-row with a parent INCIDENT id inside the final data, prefer that
        const canonicalFromFinal = finalData?.INCIDENT_ID || finalData?.incident_id || finalData?.incidentId;
        if (canonicalFromFinal && String(canonicalFromFinal) !== String(finalData?.id)) {
          console.log('Normalizing final incident id from parent INCIDENT id:', canonicalFromFinal, 'was:', finalData.id);
          finalData.id = canonicalFromFinal;
          finalData.ID = finalData.ID || canonicalFromFinal;
        }
      }

      // Ensure UI reflects the new workflow status immediately
      try {
        finalData.investigationStatus = newStatus;
        finalData.INVESTIGATION_STATUS = newStatus;
      } catch (e) {
        // ignore if finalData is not an object
      }

      // Merge submitted form fields into finalData when the backend omits them.
      // Prefer authoritative server values, but fall back to the submitted values so the user's input isn't lost.
      try {
        const _isEmptyPlaceholder = (v) => {
          if (v === undefined || v === null) return true;
          if (Array.isArray(v)) return v.length === 0;
          if (typeof v === 'string') {
            const s = v.trim();
            if (s === '') return true;
            const low = s.toLowerCase();
            if (low === 'none' || low === 'null' || s === '[]') return true;
            return false;
          }
          return false;
        };

        const mergeIfMissing = (targetKey, submittedKey) => {
          const existing = finalData[targetKey];
          const submitted = preparedFormData ? preparedFormData[submittedKey] : formData[submittedKey];
          if (_isEmptyPlaceholder(existing) && submitted !== undefined) {
            finalData[targetKey] = submitted;
          }
        };

        // textual / string fields
        mergeIfMissing('immediateActions', 'immediateActions');
        mergeIfMissing('CONTRIBUTING_FACTORS', 'immediateActions');
        mergeIfMissing('rootCause', 'rootCause');
        mergeIfMissing('ROOT_CAUSE', 'rootCause');

        // corrective actions: backend may use CORRECTIVE_ACTIONS_JSON
        if ((finalData.CORRECTIVE_ACTIONS_JSON === undefined || finalData.CORRECTIVE_ACTIONS_JSON === null || finalData.CORRECTIVE_ACTIONS_JSON === '') && preparedFormData && preparedFormData.correctiveActions) {
          finalData.CORRECTIVE_ACTIONS_JSON = preparedFormData.correctiveActions;
        }

        // arrays (may be sent as JSON strings)
        mergeIfMissing('unsafeActs', 'unsafeActs');
        mergeIfMissing('UNSAFE_ACTS', 'unsafeActs');
        mergeIfMissing('unsafeConditions', 'unsafeConditions');
        mergeIfMissing('UNSAFE_CONDITIONS', 'unsafeConditions');
        mergeIfMissing('personalFactors', 'personalFactors');
        mergeIfMissing('PERSONAL_FACTORS', 'personalFactors');
        mergeIfMissing('jobFactors', 'jobFactors');
        mergeIfMissing('JOB_FACTORS', 'jobFactors');
        mergeIfMissing('rootCauseAnalysis', 'rootCauseAnalysis');
        mergeIfMissing('ROOT_CAUSE_ANALYSIS', 'rootCauseAnalysis');

        // WCB/OHS reporting answers
        // After Submit, prefer the just-submitted selections for the UI unless the server explicitly
        // provided authoritative DB-style fields. This avoids the refresh/merge flow resetting the
        // dropdowns back to older/stale values.
        if (preparedFormData) {
          finalData.wcbReportRequired = preparedFormData.wcbReportRequired;
          finalData.wcbReportReason = preparedFormData.wcbReportReason;
          finalData.wcbReportReasonOther = preparedFormData.wcbReportReasonOther;
          finalData.ohsReportRequired = preparedFormData.ohsReportRequired;
          finalData.ohsReportReason = preparedFormData.ohsReportReason;
          finalData.ohsReportReference = preparedFormData.ohsReportReference;

          // Only set DB-style keys if the server didn't provide them.
          if (finalData.WCB_REPORT_REQUIRED === undefined || finalData.WCB_REPORT_REQUIRED === null || finalData.WCB_REPORT_REQUIRED === '') {
            finalData.WCB_REPORT_REQUIRED = preparedFormData.wcbReportRequired;
          }
          if (finalData.WCB_REPORT_REASON === undefined || finalData.WCB_REPORT_REASON === null || finalData.WCB_REPORT_REASON === '') {
            finalData.WCB_REPORT_REASON = preparedFormData.wcbReportReason;
          }
          if (finalData.WCB_REPORT_REASON_OTHER === undefined || finalData.WCB_REPORT_REASON_OTHER === null || finalData.WCB_REPORT_REASON_OTHER === '') {
            finalData.WCB_REPORT_REASON_OTHER = preparedFormData.wcbReportReasonOther;
          }
          if (finalData.OHS_REPORT_REQUIRED === undefined || finalData.OHS_REPORT_REQUIRED === null || finalData.OHS_REPORT_REQUIRED === '') {
            finalData.OHS_REPORT_REQUIRED = preparedFormData.ohsReportRequired;
          }
          if (finalData.OHS_REPORT_REASON === undefined || finalData.OHS_REPORT_REASON === null || finalData.OHS_REPORT_REASON === '') {
            finalData.OHS_REPORT_REASON = preparedFormData.ohsReportReason;
          }
          if (finalData.OHS_REPORT_REFERENCE === undefined || finalData.OHS_REPORT_REFERENCE === null || finalData.OHS_REPORT_REFERENCE === '') {
            finalData.OHS_REPORT_REFERENCE = preparedFormData.ohsReportReference;
          }
        } else {
          // Fallback if preparedFormData isn't available for any reason
          mergeIfMissing('wcbReportRequired', 'wcbReportRequired');
          mergeIfMissing('WCB_REPORT_REQUIRED', 'wcbReportRequired');
          mergeIfMissing('wcbReportReason', 'wcbReportReason');
          mergeIfMissing('WCB_REPORT_REASON', 'wcbReportReason');
          mergeIfMissing('wcbReportReasonOther', 'wcbReportReasonOther');
          mergeIfMissing('WCB_REPORT_REASON_OTHER', 'wcbReportReasonOther');
          mergeIfMissing('ohsReportRequired', 'ohsReportRequired');
          mergeIfMissing('OHS_REPORT_REQUIRED', 'ohsReportRequired');
          mergeIfMissing('ohsReportReason', 'ohsReportReason');
          mergeIfMissing('OHS_REPORT_REASON', 'ohsReportReason');
          mergeIfMissing('ohsReportReference', 'ohsReportReference');
          mergeIfMissing('OHS_REPORT_REFERENCE', 'ohsReportReference');
        }

        // If the server response we merged from does not include these fields, ensure they still
        // round-trip through normalizeInvestigationFormData by embedding them into CONTRIBUTING_FACTORS.
        // This prevents the post-submit refresh from resetting the selects back to defaults.
        const ensureContributingFactorsJson = () => {
          const raw = finalData.CONTRIBUTING_FACTORS || finalData.contributingFactors || finalData.immediateActions || '';
          const immediateActions = finalData.immediateActions || (preparedFormData && preparedFormData.immediateActions) || '';
          const reporting = {
            wcbReportRequired: (preparedFormData && preparedFormData.wcbReportRequired) || formData.wcbReportRequired || 'No',
            wcbReportReason: (preparedFormData && preparedFormData.wcbReportReason) || formData.wcbReportReason || '',
            wcbReportReasonOther: (preparedFormData && preparedFormData.wcbReportReasonOther) || formData.wcbReportReasonOther || '',
            ohsReportRequired: (preparedFormData && preparedFormData.ohsReportRequired) || formData.ohsReportRequired || 'No',
            ohsReportReason: (preparedFormData && preparedFormData.ohsReportReason) || formData.ohsReportReason || '',
            ohsReportReference: (preparedFormData && preparedFormData.ohsReportReference) || formData.ohsReportReference || ''
          };

          try {
            if (typeof raw === 'string') {
              const trimmed = raw.trim();
              if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                const parsed = JSON.parse(trimmed);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  const merged = { ...parsed, immediateActions: parsed.immediateActions ?? immediateActions, ...reporting };
                  const json = JSON.stringify(merged);
                  finalData.CONTRIBUTING_FACTORS = json;
                  finalData.contributingFactors = json;
                  return;
                }
              }
            }
          } catch (e) {
            // ignore parse errors
          }

          // Fallback: wrap any existing text into immediateActions
          const json = JSON.stringify({ immediateActions, ...reporting });
          finalData.CONTRIBUTING_FACTORS = json;
          finalData.contributingFactors = json;
        };

        ensureContributingFactorsJson();
      } catch (e) {
        console.warn('Failed to merge submitted form fields into finalData:', e);
      }

      // If finalData still lacks investigation-level fields after all merges/fallbacks,
      // copy only specific investigation-level fields from the normalized POST response
      // and persist them to client cache so the UI can render them immediately.
      try {
        const lacksImportantFieldsFinal = (obj) => {
          if (!obj || typeof obj !== 'object') return true;
          const hasStatus = obj.INVESTIGATION_STATUS || obj.investigationStatus;
          const hasContrib = obj.CONTRIBUTING_FACTORS || obj.contributingFactors || obj.immediateActions;
          return !(hasStatus || hasContrib);
        };

        const postBody = postResp && postResp.data ? (Array.isArray(postResp.data) ? postResp.data[0] : postResp.data) : (updatedIncident || null);
        if (lacksImportantFieldsFinal(finalData) && postBody) {
          try {
            const postNormalized = normalizeInvestigationFormData(postBody);
            console.log('Submit-flow: finalData missing investigation fields — applying selected fields from normalized POST response', postNormalized);

            // Only copy investigation-level keys to avoid overwriting canonical fields
            const keysToCopy = [
              'immediateActions', 'CONTRIBUTING_FACTORS', 'rootCause', 'ROOT_CAUSE',
              'CORRECTIVE_ACTIONS_JSON', 'correctiveActions',
              'unsafeActs', 'UNSAFE_ACTS', 'unsafeConditions', 'UNSAFE_CONDITIONS',
              'personalFactors', 'PERSONAL_FACTORS', 'jobFactors', 'JOB_FACTORS',
              'rootCauseAnalysis', 'ROOT_CAUSE_ANALYSIS',
              'investigationStatus', 'INVESTIGATION_STATUS',
              'attachments', 'ATTACHMENTS'
            ];

            for (const k of keysToCopy) {
              // prefer normalized POST values, but only if finalData doesn't already have a meaningful value
              const existing = finalData[k];
              const candidate = postNormalized[k];
              const isEmpty = existing === undefined || existing === null || existing === '' || (Array.isArray(existing) && existing.length === 0);
              if (isEmpty && candidate !== undefined) {
                finalData[k] = candidate;
              }
            }

            // Persist selected fields to client cache immediately so other views can read them
            try {
              const canonicalIdForCache = finalData?.id || canonicalFromPostId || refreshId || incident?.id;
              if (canonicalIdForCache) {
                const cachePayload = buildInvestigationClientCachePayload({
                  ...formData,
                  immediateActions: finalData.immediateActions || postNormalized.immediateActions || formData.immediateActions,
                  unsafeActs: finalData.unsafeActs || postNormalized.unsafeActs || formData.unsafeActs,
                  unsafeConditions: finalData.unsafeConditions || postNormalized.unsafeConditions || formData.unsafeConditions,
                  personalFactors: finalData.personalFactors || postNormalized.personalFactors || formData.personalFactors,
                  jobFactors: finalData.jobFactors || postNormalized.jobFactors || formData.jobFactors,
                  rootCause: finalData.rootCause || postNormalized.rootCause || formData.rootCause,
                  rootCauseAnalysis: finalData.rootCauseAnalysis || postNormalized.rootCauseAnalysis || formData.rootCauseAnalysis,
                  correctiveActions: formData.correctiveActions,
                  wcbReportRequired: finalData.WCB_REPORT_REQUIRED || finalData.wcbReportRequired || postNormalized.wcbReportRequired || formData.wcbReportRequired,
                  wcbReportReason: finalData.WCB_REPORT_REASON || finalData.wcbReportReason || postNormalized.wcbReportReason || formData.wcbReportReason,
                  wcbReportReasonOther: finalData.WCB_REPORT_REASON_OTHER || finalData.wcbReportReasonOther || postNormalized.wcbReportReasonOther || formData.wcbReportReasonOther,
                  ohsReportRequired: finalData.OHS_REPORT_REQUIRED || finalData.ohsReportRequired || postNormalized.ohsReportRequired || formData.ohsReportRequired,
                  ohsReportReason: finalData.OHS_REPORT_REASON || finalData.ohsReportReason || postNormalized.ohsReportReason || formData.ohsReportReason,
                  ohsReportReference: finalData.OHS_REPORT_REFERENCE || finalData.ohsReportReference || postNormalized.ohsReportReference || formData.ohsReportReference
                }, finalData.investigationStatus || postNormalized.investigationStatus || newStatus);
                try { setClientCacheForId(canonicalIdForCache, cachePayload); console.log('Submit-flow: wrote investigative cache for id', canonicalIdForCache, cachePayload); } catch (e) { console.warn('Failed to write client cache for POST fallback', e && e.message); }
              }
            } catch (e) {
              // ignore client cache errors
            }
          } catch (e) {
            console.warn('Submit-flow: failed to apply POST fallback normalization', e && e.message);
          }
        }
      } catch (e) {
        // ignore fallback errors
      }

  const _finalNormalized = normalizeInvestigationFormData(finalData);
  console.debug('DEBUG: about to setFormData from final merged payload:', _finalNormalized);
  // Use safe setter and mark recent local set time so subsequent server fetches don't immediately overwrite
  setFormDataSafe(_finalNormalized);
      // Update local incident state but preserve the original detail id for the modal if it was open for the detail
      try {
        if (selectedIncident && String(selectedIncident.id) === String(originalDetailId) && String(finalData?.id) !== String(originalDetailId)) {
          // Preserve modal's id (so URL and open card remain on detail id) but update status and fields
          setSelectedIncident(prev => ({ ...prev, ...finalData, id: prev.id }));
          // Merge finalData while preserving the original detail id and the most authoritative status
          const mergedStatus = resolveInvestigationStatus(finalData, incident || {});
          setIncident(prev => ({ ...prev, ...finalData, id: prev?.id || originalDetailId, investigationStatus: mergedStatus, INVESTIGATION_STATUS: mergedStatus }));
        } else {
          const mergedStatus2 = resolveInvestigationStatus(finalData, incident || {});
          setIncident({ ...incident, ...finalData, ...normalizeInvestigationFormData(finalData), investigationStatus: mergedStatus2, INVESTIGATION_STATUS: mergedStatus2 });
        }
      } catch (e) {
        // fallback
  const mergedStatus3 = resolveInvestigationStatus(finalData, incident || {});
  setIncident({ ...incident, ...finalData, ...normalizeInvestigationFormData(finalData), investigationStatus: mergedStatus3, INVESTIGATION_STATUS: mergedStatus3 });
      }

      // Update incidents list: match the original incident id, the canonical parent id from POST, or the final resolved id (from GET/heuristic)
      const resolvedIds = new Set(getAllIdVariants(finalData).concat(getAllIdVariants(incident)).concat(canonicalFromPostId ? [String(canonicalFromPostId)] : []).concat(refreshId ? [String(refreshId)] : []));
      setIncidents(prev =>
        prev.map(inc => {
          const incIds = getAllIdVariants(inc);
          const intersects = incIds.some(id => resolvedIds.has(id));
          if (intersects) {
            const mergedStatus = resolveInvestigationStatus(finalData, inc || {});
            console.log('Submit-flow: updating incidents list row', inc.id, 'matched ids', incIds, '-> setting status', mergedStatus);
            return { ...inc, ...finalData, ...normalizeInvestigationFormData(finalData), investigationStatus: mergedStatus, INVESTIGATION_STATUS: mergedStatus };
          }
          return inc;
        })
      );

      // Heuristic: ensure the most-recent matching parent incident row also receives the submitted investigation fields
      try {
        const submittedPayload = {
          immediateActions: finalData.immediateActions || (postResp && postResp.data && (Array.isArray(postResp.data) ? postResp.data[0] : postResp.data))?.immediateActions || formData.immediateActions,
          CONTRIBUTING_FACTORS: finalData.CONTRIBUTING_FACTORS || formData.immediateActions,
          investigationStatus: finalData.investigationStatus || newStatus,
          CORRECTIVE_ACTIONS_JSON: finalData.CORRECTIVE_ACTIONS_JSON || (preparedFormData && preparedFormData.correctiveActions) || null,
          unsafeActs: finalData.unsafeActs || formData.unsafeActs,
          unsafeConditions: finalData.unsafeConditions || formData.unsafeConditions,
          personalFactors: finalData.personalFactors || formData.personalFactors,
          jobFactors: finalData.jobFactors || formData.jobFactors,
          rootCause: finalData.rootCause || formData.rootCause,
          rootCauseAnalysis: finalData.rootCauseAnalysis || formData.rootCauseAnalysis,
          attachments: finalData.attachments || formData.attachments || finalData.ATTACHMENTS,

          // WCB/OHS reporting (keep these on the client-side merged incident too)
          wcbReportRequired: (finalData.WCB_REPORT_REQUIRED || finalData.wcbReportRequired) || (preparedFormData && preparedFormData.wcbReportRequired) || formData.wcbReportRequired || 'No',
          wcbReportReason: (finalData.WCB_REPORT_REASON || finalData.wcbReportReason) || (preparedFormData && preparedFormData.wcbReportReason) || formData.wcbReportReason || '',
          wcbReportReasonOther: (finalData.WCB_REPORT_REASON_OTHER || finalData.wcbReportReasonOther) || (preparedFormData && preparedFormData.wcbReportReasonOther) || formData.wcbReportReasonOther || '',
          ohsReportRequired: (finalData.OHS_REPORT_REQUIRED || finalData.ohsReportRequired) || (preparedFormData && preparedFormData.ohsReportRequired) || formData.ohsReportRequired || 'No',
          ohsReportReason: (finalData.OHS_REPORT_REASON || finalData.ohsReportReason) || (preparedFormData && preparedFormData.ohsReportReason) || formData.ohsReportReason || '',
          ohsReportReference: (finalData.OHS_REPORT_REFERENCE || finalData.ohsReportReference) || (preparedFormData && preparedFormData.ohsReportReference) || formData.ohsReportReference || ''
        };

        const formDate = String(preparedFormData?.incidentDate || incident?.date || '').split('T')[0];
        const formLoc = String((preparedFormData && preparedFormData.location) || incident?.location || '').toLowerCase().trim();
        const formTitle = String((preparedFormData && preparedFormData.description) || incident?.title || incident?.TITLE || '').toLowerCase().trim();

        // Use functional update so we read the latest incidents state safely
        setIncidents(prev => {
          const local = Array.isArray(prev) ? prev : [];
          const candidates = local.filter(it => {
            try {
              const itDate = String(it.DATE || it.date || '').split('T')[0];
              const itLoc = String(it.LOCATION || it.location || '').toLowerCase().trim();
              const itTitle = String(it.TITLE || it.title || it.ISSUE_DESCRIPTION || '').toLowerCase().trim();

              const dateMatch = formDate && itDate && itDate === formDate;
              const locMatch = formLoc && itLoc && (itLoc === formLoc || itLoc.includes(formLoc) || formLoc.includes(itLoc));
              const titleMatch = formTitle && itTitle && (itTitle.includes(formTitle) || formTitle.includes(itTitle));

              return dateMatch && (locMatch || titleMatch);
            } catch (e) {
              return false;
            }
          });

          if (!candidates.length) {
            const canonicalHint = String(finalData?.id || canonicalFromPostId || refreshId || incident?.id || '');
            if (canonicalHint) {
              candidates.push(...local.filter(it => getAllIdVariants(it).map(String).includes(String(canonicalHint))));
            }
          }

          if (candidates.length) {
            candidates.sort((a, b) => {
              const aDate = new Date(a.DATE || a.date || 0).getTime() || 0;
              const bDate = new Date(b.DATE || b.date || 0).getTime() || 0;
              if (bDate !== aDate) return bDate - aDate;
              const aId = Number(a.ID || a.id || 0) || 0;
              const bId = Number(b.ID || b.id || 0) || 0;
              return bId - aId;
            });

            const latest = candidates[0];
            const latestId = latest.ID || latest.id || latest.incident_id || latest.INCIDENT_ID;

            return local.map(it => {
              const ids = getAllIdVariants(it).map(String);
              if (String(latestId) && ids.includes(String(latestId))) {
                const merged = { ...it, ...submittedPayload, ...normalizeInvestigationFormData(submittedPayload) };
                console.log('Submit-flow: patched latest incident row with submitted investigation fields for id', latestId, merged);
                return merged;
              }
              return it;
            });
          }

          return prev;
        });
      } catch (e) {
        console.warn('Submit-flow: heuristic patch to latest incident row failed:', e && e.message);
      }

      // Inform parent/consumer that the incident was updated so any selected view re-renders
      try {
        if (typeof onIncidentUpdate === 'function') {
          // Ensure callers receive the canonical INCIDENTS.ID when available
          const canonicalId = String(finalData?.id || finalData?.ID || canonicalFromPostId || refreshId || incident?.id || '');
          const representative = { ...incident, ...finalData, id: canonicalId, ID: canonicalId, investigationStatus: resolveInvestigationStatus(finalData, incident || {}) };
          onIncidentUpdate(representative);
        }
      } catch (e) {
        console.warn('Failed to call onIncidentUpdate after submit merge:', e);
      }

      // Persist submitted form fields in client cache until the backend reflects them
      try {
        const canonicalIdForCache = finalData?.id || canonicalFromPostId || refreshId || incident?.id;
        const cachePayload = buildInvestigationClientCachePayload({
          ...formData,
          immediateActions: finalData.immediateActions || finalData.CONTRIBUTING_FACTORS || formData.immediateActions,
          unsafeActs: finalData.unsafeActs || formData.unsafeActs,
          unsafeConditions: finalData.unsafeConditions || formData.unsafeConditions,
          personalFactors: finalData.personalFactors || formData.personalFactors,
          jobFactors: finalData.jobFactors || formData.jobFactors,
          rootCause: finalData.rootCause || formData.rootCause,
          rootCauseAnalysis: finalData.rootCauseAnalysis || formData.rootCauseAnalysis,
          correctiveActions: formData.correctiveActions,
          wcbReportRequired: finalData.WCB_REPORT_REQUIRED || finalData.wcbReportRequired || formData.wcbReportRequired,
          wcbReportReason: finalData.WCB_REPORT_REASON || finalData.wcbReportReason || formData.wcbReportReason,
          wcbReportReasonOther: finalData.WCB_REPORT_REASON_OTHER || finalData.wcbReportReasonOther || formData.wcbReportReasonOther,
          ohsReportRequired: finalData.OHS_REPORT_REQUIRED || finalData.ohsReportRequired || formData.ohsReportRequired,
          ohsReportReason: finalData.OHS_REPORT_REASON || finalData.ohsReportReason || formData.ohsReportReason,
          ohsReportReference: finalData.OHS_REPORT_REFERENCE || finalData.ohsReportReference || formData.ohsReportReference
        }, finalData.investigationStatus || newStatus);
        if (canonicalIdForCache) setClientCacheForId(canonicalIdForCache, cachePayload);
      } catch (e) {
        console.warn('Failed to set client cache after submit:', e);
      }

      // Additionally, if the original detail id is present as its own row, update its status fields so the card at the detail link updates too
      if (originalDetailId) {
        setIncidents(prev => prev.map(inc => {
          const incIds = getAllIdVariants(inc).map(String);
          if (incIds.includes(String(originalDetailId))) {
            const canonicalIdForThis = String(finalData?.id || finalData?.ID || canonicalFromPostId || refreshId || inc.id || originalDetailId);
            return {
              ...inc,
              id: canonicalIdForThis,
              ID: canonicalIdForThis,
              investigationStatus: newStatus,
              INVESTIGATION_STATUS: newStatus,
              SUPERVISOR_APPROVAL: finalData.SUPERVISOR_APPROVAL || inc.SUPERVISOR_APPROVAL,
              MANAGER_APPROVAL: finalData.MANAGER_APPROVAL || inc.MANAGER_APPROVAL
            };
          }
          return inc;
        }));
      }

      // If the modal is open for the previous detail incident, update the selectedIncident to the canonical final data
      try {
        const finalCanonicalId = String(finalData?.id || canonicalFromPostId || refreshId || '');
        if (selectedIncident && finalCanonicalId && String(selectedIncident.id) !== finalCanonicalId) {
          console.log('Replacing selectedIncident with canonical finalData id:', finalCanonicalId);
          setSelectedIncident({ ...selectedIncident, ...finalData });
        }
      } catch (e) {
        console.warn('Error while syncing selectedIncident to canonical final data:', e);
      }

      // Remove the draft after successful submit
      clearDraftForIncident(finalData || incident || refreshId);
      setHasDraft(false);
      setIsDraftLoaded(false);

      // Show a message to the user
      if (newStatus === 'SUBMITTED') {
        alert('Your incident investigation has been submitted and is awaiting supervisor review.');
      } else if (newStatus === 'SUPERVISOR_APPROVED') {
        alert('Supervisor approval complete. Awaiting manager approval.');
      } else if (newStatus === 'COMPLETED') {
        alert('Investigation fully approved and completed.');
      } else {
        alert('Your incident investigation has been submitted.'); 
      }
      onClose();
      // If we resolved a canonical parent id, normalize the URL so the investigation card shows the parent
      try {
        const canonicalToUseRaw = canonicalFromPostId || refreshId;
        const canonicalToUse =
          canonicalToUseRaw != null && /^\d+$/.test(String(canonicalToUseRaw)) && Number(canonicalToUseRaw) > 0
            ? String(canonicalToUseRaw)
            : null;
        if (canonicalToUse) {
          const url = new URL(window.location.href);
          url.searchParams.set('incidentId', String(canonicalToUse));
          window.history.replaceState({}, '', url);
          setUrlIncidentId(String(canonicalToUse));
          console.log('Updated URL incidentId to canonical id:', canonicalToUse);
        } else {
          console.warn('Skipping URL incidentId normalization due to invalid canonical id:', canonicalToUseRaw);
        }
      } catch (e) {
        console.warn('Failed to normalize URL incidentId to canonical id:', e);
      }
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit the investigation form. Please try again: ' + error.message);
    }
  };

  const handleDelegateSubmit = async () => {
    if (!formData.delegateFindings) {
      alert('Please complete the delegate findings section');
      return;
    }
    
    try {
      console.log('Submitting delegate findings:', {
        delegateFindings: formData.delegateFindings,
        delegateRecommendations: formData.delegateRecommendations,
      });

      const response = await API.post(`/api/incidents/${incident.id}/delegate-findings`, {
        delegateFindings: formData.delegateFindings,
        delegateRecommendations: formData.delegateRecommendations,
        markCompleted: true
      });

      if (response.data.success) {
        alert('Delegate findings submitted successfully. The investigation will now proceed to review.');
        
        try {
          const updatedIncidentResponse = await API.get(`/api/incidents/${incident.id}`);
          const updatedIncidentData = Array.isArray(updatedIncidentResponse.data) 
            ? updatedIncidentResponse.data[0] 
            : updatedIncidentResponse.data;
          
          const updatedIncident = normalizeStatusField({
            ...incident,
            ...updatedIncidentData,
            DELEGATE_FINDINGS: formData.delegateFindings,
            DELEGATE_RECOMMENDATIONS: formData.delegateRecommendations,
            DELEGATE_COMPLETED: true
          });
          
          if (onIncidentUpdate) {
            const canonicalId = String(getCanonicalIncidentId(updatedIncident) || updatedIncident?.id || updatedIncident?.ID || incident?.id || '');
            onIncidentUpdate({ ...updatedIncident, id: canonicalId, ID: canonicalId });
          }
        } catch (fetchError) {
          console.error('Error fetching updated incident:', fetchError);
        }
        
        // Refresh the incident data in-app instead of forcing a full reload
        setTimeout(() => {
          try {
            const currentId = String(getCanonicalIncidentId(incident) || correctIncidentId || incident?.id);
            if (currentId) {
              console.log('Refreshing incident in-app after delegate submit:', currentId);
              if (fetchRefreshedIncidentById) {
                fetchRefreshedIncidentById(currentId).then(ref => {
                  if (ref && onIncidentUpdate) {
                    const canonicalRefId = String(getCanonicalIncidentId(ref) || ref?.id || ref?.ID || currentId || '');
                    onIncidentUpdate({ ...ref, id: canonicalRefId, ID: canonicalRefId });
                  }
                }).catch(e => console.error('Error fetching refreshed incident:', e));
              }
            }
          } catch (e) {
            console.error('Failed in-app refresh after delegate submit, falling back to reload:', e);
            // As a last resort (should be rare), perform a reload
            // window.location.reload();
          }
        }, 300);
      } else {
        alert('Failed to submit delegate findings: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error submitting delegate findings:', error);
      alert('Failed to submit delegate findings: ' + (error.response?.data?.error || error.message));
    }
  }

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleApproval = async (action) => {
    if (action === 'REJECT' && !approvalForm.feedback.trim()) {
      alert('Feedback is required when rejecting an investigation');
      return;
    }
    
    const { approvalActorRole: role } = getApprovalContext();

    if (!role) {
      alert('Your account cannot approve or return this investigation for revision.');
      return;
    }

    if (action === 'APPROVE') {
      const formErrors = validateForm(formData);
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);
        if (formErrors.rootCauseAnalysis) {
          alert('Please select at least one Root Cause Analysis option before approving this investigation.');
          return;
        }
      }
    }

    const approvalIncidentId = String(
      correctIncidentId || getCanonicalIncidentId(incident) || incident?.id || incident?.ID || ''
    ).trim();

    if (!approvalIncidentId) {
      alert('Could not determine incident id for approval. Please refresh and try again.');
      return;
    }
    
    const newStatus = action === 'APPROVE' ? 
      (role === 'SUPERVISOR' ? 'SUPERVISOR_APPROVED' : 'COMPLETED') : 
      'NEEDS_REVISION';
    
    console.log(`Approval action: ${action} by ${role}, setting status to: ${newStatus}`);
    
    const updatedIncident = {
      ...incident,
      investigationStatus: newStatus,
      INVESTIGATION_STATUS: newStatus,
      SUPERVISOR_APPROVAL: role === 'SUPERVISOR' ? 
                          (action === 'APPROVE' ? 'APPROVED' : 'REJECTED') : 
                          incident?.SUPERVISOR_APPROVAL,
      MANAGER_APPROVAL: role === 'MANAGER' ? 
                        (action === 'APPROVE' ? 'APPROVED' : 'REJECTED') : 
                        incident?.MANAGER_APPROVAL
    };
    
    console.log('Updating local incident with:', updatedIncident);
    
  const resolvedApprovalStatus = resolveInvestigationStatus(updatedIncident, incident || {});
  setIncident({ ...updatedIncident, investigationStatus: resolvedApprovalStatus, INVESTIGATION_STATUS: resolvedApprovalStatus });
    
    try {
      const preparedInvestigationData = {
        incidentDate: formData.incidentDate || incident.date || '',
        rootCause: formData.rootCause || '',
        correctiveActions: JSON.stringify(formData.correctiveActions || []),
        immediateActions: formData.immediateActions || formData.contributingFactors || formData.CONTRIBUTING_FACTORS || '',
        unsafeActs: JSON.stringify(formData.unsafeActs || []),
        unsafeConditions: JSON.stringify(formData.unsafeConditions || []),
        personalFactors: JSON.stringify(formData.personalFactors || []),
        jobFactors: JSON.stringify(formData.jobFactors || []),
        rootCauseAnalysis: JSON.stringify(formData.rootCauseAnalysis || []),
        wcbReportRequired: formData.wcbReportRequired || 'No',
        wcbReportReason: formData.wcbReportReason || '',
        wcbReportReasonOther: formData.wcbReportReasonOther || '',
        ohsReportRequired: formData.ohsReportRequired || 'No',
        ohsReportReason: formData.ohsReportReason || '',
        ohsReportReference: formData.ohsReportReference || ''
      };

      const response = await API.post(`/api/incidents/${approvalIncidentId}/approval`, {
        action,
        feedback: approvalForm.feedback,
        role,
        investigationData: preparedInvestigationData
      });
      
      if (response.data.success) {
        try {
          setClientCacheForId(approvalIncidentId, buildInvestigationClientCachePayload(formData, newStatus));
        } catch (cacheError) {
          console.warn('Failed to cache approval form state:', cacheError);
        }

        try {
          clearDraftForIncident({ ...incident, id: approvalIncidentId, ID: approvalIncidentId });
          setHasDraft(false);
          setIsDraftLoaded(false);
        } catch (draftError) {
          console.error('Error clearing draft after review action:', draftError);
        }

        if (action === 'APPROVE') {
          console.log('Removed draft for approved fire investigation:', approvalIncidentId);
        }

        if (onIncidentUpdate) {
          const canonicalId2 = String(getCanonicalIncidentId(updatedIncident) || updatedIncident?.id || updatedIncident?.ID || incident?.id || '');
          onIncidentUpdate({ ...updatedIncident, id: canonicalId2, ID: canonicalId2 });
        }
        
        alert(action === 'APPROVE'
          ? 'Investigation advanced successfully.'
          : 'Investigation returned for revision successfully.');

        // Update the incident view in-app rather than reloading the whole page
        setTimeout(() => {
          try {
            const currentId = String(getCanonicalIncidentId(incident) || correctIncidentId || incident?.id);
            if (currentId) {
              console.log('Refreshing incident in-app after approval:', currentId);
              if (fetchRefreshedIncidentById) {
                fetchRefreshedIncidentById(currentId).then(ref => {
                  if (ref && onIncidentUpdate) {
                    const canonicalRefId2 = String(getCanonicalIncidentId(ref) || ref?.id || ref?.ID || currentId || '');
                    onIncidentUpdate({ ...ref, id: canonicalRefId2, ID: canonicalRefId2 });
                  }
                }).catch(e => console.error('Error fetching refreshed incident after approval:', e));
              }
            }
          } catch (e) {
            console.error('Failed in-app refresh after approval:', e);
          }
        }, 1000);
      } else {
        alert(`Failed to ${action.toLowerCase()} investigation: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      if (incident && incident.id) {
        try {
          const response = await API.get(`/api/auth/me`);
          const userRoleRaw = response.data.role || response.data?.user?.role || 'USER';
          let role = String(userRoleRaw).toUpperCase();
          try {
            const localRoleRaw = localStorage.getItem('userRole') || '';
            const localRole = String(localRoleRaw).toUpperCase();
            if ((role === 'USER' || !role) && localRole && localRole !== 'USER') {
              console.log('Overriding API role with localStorage role for UI:', localRole);
              role = localRole;
            }
          } catch (e) {}
          console.log('Re-checked user role (final):', role);
          setUserRole(role);

          await checkRolePermissions(incident.id);
        } catch (error) {
          console.error('Error checking permissions:', error);
          const fallbackRole = localStorage.getItem('userRole') || 'USER';
          setUserRole(String(fallbackRole).toUpperCase());
        }
      }
    };
    
    checkPermissions();
  }, [incident?.id]);

  // Note: Using incident.attachments directly for now since the filtered API is returning empty results
  // This shows all attachments associated with the incident

  useEffect(() => {
    if (employees.length === 0) {
      API.get('/api/users', { params: { role: 'EMPLOYEE', scope: 'fire' } })
        .then(res => {
          // Sort alphabetically by last name
          const sorted = (res.data.users || []).sort((a, b) =>
            (a.lastName || '').localeCompare(b.lastName || '')
          );
          setEmployees(sorted);
        })
        .catch(err => {
          console.error('Failed to load employees:', err);
          setEmployees([]);
        });
    }
  }, [formData.delegateSelected, employees.length]);

  // Directory for name resolution (Reported By, etc.)
  // Not role-filtered, so manager/supervisor/admin accounts resolve too.
  useEffect(() => {
    if (usersDirectory.length > 0) return;

    API.get('/api/users', { params: { scope: 'all' } })
      .then(res => {
        const sorted = (res.data.users || []).sort((a, b) =>
          (a.lastName || '').localeCompare(b.lastName || '')
        );
        setUsersDirectory(sorted);
      })
      .catch(err => {
        console.error('Failed to load user directory:', err);
        setUsersDirectory([]);
      });
  }, [usersDirectory.length]);

  const handleAttachmentUpload = async (newAttachment, linkedToIncident = false) => {
    try {
      console.log('🔍 DEBUG: handleAttachmentUpload called with:', newAttachment);
      console.log('🔍 DEBUG: Current incident object:', incident);
      console.log('🔍 DEBUG: Current incident.id:', incident.id);
      console.log('🔍 DEBUG: correctIncidentId prop:', correctIncidentId);
      console.log('🔍 DEBUG: fetchRefreshedIncidentById function:', fetchRefreshedIncidentById);
      console.log('Attachment uploaded successfully:', newAttachment);
      
      // Use the correct incident ID (from prop or incident object)
      const actualIncidentId = correctIncidentId || incident.id;
      console.log('🔍 DEBUG: Using incident ID for refresh:', actualIncidentId);
      
      // If the server already linked the attachment to the incident, avoid a full forced refresh.
      // Instead, merge the new attachment into local incident.attachments and formData.attachments.
      if (linkedToIncident) {
        console.log('🔍 Server indicated attachment is already linked to incident. Merging locally.');
        const attachmentObj = newAttachment;
        const existingAttachments = Array.isArray(incident.attachments) ? [...incident.attachments] : [];
        const existingFormAttachments = Array.isArray(formData.attachments) ? [...formData.attachments] : [];

        const normalized = {
          id: attachmentObj.id || attachmentObj.FILEID || attachmentObj.fileId,
          name: attachmentObj.name || attachmentObj.originalName || attachmentObj.ORIGINALNAME || attachmentObj.FILE_NAME || attachmentObj.fileName,
          size: attachmentObj.size || attachmentObj.SIZE || 0,
          type: attachmentObj.mimetype || attachmentObj.type || attachmentObj.MIMETYPE || '',
          downloadUrl: attachmentObj.downloadUrl || `/api/incidents/attachments/${attachmentObj.id || attachmentObj.fileId}`
        };

        if (!existingAttachments.find(a => a.id === normalized.id)) existingAttachments.push(normalized);
        if (!existingFormAttachments.find(a => a.id === normalized.id)) existingFormAttachments.push(normalized);

        setIncident(prev => ({ ...(prev || {}), attachments: existingAttachments }));
        setFormData(prev => ({ ...(prev || {}), attachments: existingFormAttachments }));
      } else {
        // Fallback: perform a refresh to get authoritative state when we didn't link on upload
        if (fetchRefreshedIncidentById && actualIncidentId) {
          console.log('🔄 About to call fetchRefreshedIncidentById with ID:', actualIncidentId);
          const refreshedIncident = await fetchRefreshedIncidentById(actualIncidentId);
          const resolvedStatus = resolveInvestigationStatus(refreshedIncident, incident || {});
          setIncident({ ...incident, ...refreshedIncident, investigationStatus: resolvedStatus, INVESTIGATION_STATUS: resolvedStatus });
          console.log('✅ Incident data refreshed after upload:', refreshedIncident);
        } else {
          console.log('⚠️ Cannot refresh - missing fetchRefreshedIncidentById or incident ID:', {
            fetchRefreshedIncidentById: !!fetchRefreshedIncidentById,
            incidentId: actualIncidentId,
            originalIncidentId: incident.id,
            correctIncidentId: correctIncidentId
          });
        }
      }
    } catch (error) {
      console.error('❌ Error refreshing incident data after upload:', error);
      console.error('❌ Error details:', error.message, error.stack);
      alert('Failed to refresh the incident data');
    }
  };

    const handleAttachmentRemove = async (attachmentId) => {
    try {
      const response = await API.delete(`/api/incidents/attachments/${attachmentId}`);
      
      if (response.data.success) {
        console.log('Attachment removed from incident');

        const filterRemovedAttachment = (attachments = []) =>
          Array.isArray(attachments)
            ? attachments.filter((attachment) => {
                const currentId = attachment?.id || attachment?.ID || attachment?.fileId || attachment?.FILEID;
                return String(currentId) !== String(attachmentId);
              })
            : attachments;

        setOriginalReportAttachments((prev) => filterRemovedAttachment(prev));
        setFormData((prev) => ({
          ...prev,
          attachments: filterRemovedAttachment(prev.attachments)
        }));
        setIncident((prev) => ({
          ...(prev || {}),
          attachments: filterRemovedAttachment(prev?.attachments),
          ATTACHMENTS: filterRemovedAttachment(prev?.ATTACHMENTS)
        }));

        if (fetchRefreshedIncidentById && incident.id) {
          fetchRefreshedIncidentById(incident.id).catch((refreshError) => {
            console.warn('Attachment removed but refresh failed:', refreshError);
          });
        }
      } else {
        alert('Failed to remove the attachment: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error removing attachment:', error);
      alert('Failed to remove the attachment: ' + (error.response?.data?.message || error.message));
    }
  };

  // Add explicit save function for corrective actions
  const saveCorrectiveAction = async (index) => {
    const action = formData.correctiveActions[index];
    
    // Validate required fields
    if (!action.description || !action.assignedTo || !action.targetDate) {
      alert('Please fill in all required fields before saving');
      return;
    }
    
    // If the action has an ID, update in database; otherwise it's new and will be saved with form submit
    if (action.id) {
      try {
        // Create a temporary notification element
        const notifyElement = document.createElement('div');
        notifyElement.textContent = 'Saving changes...';
        notifyElement.style.position = 'fixed';
        notifyElement.style.top = '20px';
        notifyElement.style.right = '20px';
        notifyElement.style.backgroundColor = '#f0f9ff';
        notifyElement.style.border = '1px solid #bae6fd';
        notifyElement.style.borderRadius = '4px';
        notifyElement.style.padding = '10px 20px';
        notifyElement.style.zIndex = '9999';
        document.body.appendChild(notifyElement);
        
        const response = await API.put(`/api/corrective-actions/${action.id}`, {
          description: action.description,
          assignedTo: action.assignedTo,
          targetDate: action.targetDate,
          status: action.status || 'ASSIGNED'
        });
        
        // Update notification to success
        notifyElement.textContent = 'Changes saved successfully';
        notifyElement.style.backgroundColor = '#dcfce7';
        notifyElement.style.border = '1px solid #86efac';
        
        setTimeout(() => {
          document.body.removeChild(notifyElement);
        }, 3000);
        
        console.log('Corrective action saved:', response.data);
      } catch (error) {
        console.error('Failed to save corrective action:', error);
        
        // Show error notification
        const errorElement = document.createElement('div');
        errorElement.textContent = 'Error saving changes';
        errorElement.style.position = 'fixed';
        errorElement.style.top = '20px';
        errorElement.style.right = '20px';
        errorElement.style.backgroundColor = '#fee2e2';
        errorElement.style.border = '1px solid #fecaca';
        errorElement.style.borderRadius = '4px';
        errorElement.style.padding = '10px 20px';
        errorElement.style.zIndex = '9999';
        document.body.appendChild(errorElement);
        
        setTimeout(() => {
          document.body.removeChild(errorElement);
        }, 3000);
      }
    } else {
      // For new actions that don't have IDs yet, they'll be saved when the form is submitted
      alert('This action will be saved when you submit the form');
    }
  };

  // Add a dedicated function for marking actions as completed
  const markAsCompleted = async (index) => {
    const action = formData.correctiveActions[index];
    
    // First, confirm with the user
    if (!window.confirm(`Are you sure you want to mark this corrective action as completed? This will send notifications to all relevant parties.`)) {
      return;
    }
    
    try {
      // Update state immediately for user feedback
      const updatedActions = [...formData.correctiveActions];
      updatedActions[index] = {
        ...action,
        status: 'COMPLETED',
        completedDate: new Date().toISOString().split('T')[0],
        completedBy: localStorage.getItem('userEmail') || ''
      };
      
      setFormData(prev => ({
        ...prev,
        correctiveActions: updatedActions
      }));
      
      // If the action has an ID, update in database
      if (action.id) {
        const response = await API.put(`/api/corrective-actions/${action.id}/status`, {
          status: 'COMPLETED',
          sendNotification: true
        });
        
        console.log('Action marked as completed on server:', response.data);
        
        // Show success notification
        const notifyElement = document.createElement('div');
        notifyElement.textContent = 'Action marked as completed and notifications sent';
        notifyElement.style.position = 'fixed';
        notifyElement.style.top = '20px';
        notifyElement.style.right = '20px';
        notifyElement.style.backgroundColor = '#dcfce7';
        notifyElement.style.border = '1px solid #86efac';
        notifyElement.style.borderRadius = '4px';
        notifyElement.style.padding = '10px 20px';
        notifyElement.style.zIndex = '9999';
        document.body.appendChild(notifyElement);
        
        setTimeout(() => {
          document.body.removeChild(notifyElement);
        }, 3000);
      } else {
        // For new actions, they'll be saved as completed when submitting the form
        console.log('New action marked as completed, will be saved on form submit');
      }
    } catch (error) {
      console.error('Error marking action as completed:', error);
      alert('Failed to update status: ' + (error.response?.data?.error || error.message));
    }
  };

  // Update status for in-progress without notifications
  const markActionInProgress = async (index) => {
    const action = formData.correctiveActions[index];
    
    // Update state immediately
    const updatedActions = [...formData.correctiveActions];
    updatedActions[index] = {
      ...action,
      status: 'IN_PROGRESS'
    };
    
    setFormData(prev => ({
      ...prev,
      correctiveActions: updatedActions
    }));
    
    // If action exists in database, update it
    if (action.id) {
      try {
        await API.put(`/api/corrective-actions/${action.id}/status`, {
          status: 'IN_PROGRESS',
          sendNotification: false
        });
        
        console.log('Action marked as in progress');
      } catch (error) {
        console.error('Error updating status:', error);
      }
    }
  };

 const saveAndNotifyCorrectiveAction = async (index) => {
  // Validate the entire form first
  const formErrors = validateForm(formData);
  if (Object.keys(formErrors).length > 0) {
    setErrors(formErrors);
    alert('Please fix the errors in the form before saving.');
    return;
  }

    try {
      // Save the entire form (like hitting Submit)
      const preparedFormData = {
        incidentDate: formData.incidentDate || incident.date || '',
        rootCause: formData.rootCause || '',
        correctiveActions: JSON.stringify(formData.correctiveActions || []),
        immediateActions: formData.immediateActions || formData.contributingFactors || formData.CONTRIBUTING_FACTORS || '',
        unsafeActs: JSON.stringify(formData.unsafeActs || []),
        unsafeConditions: JSON.stringify(formData.unsafeConditions || []),
        personalFactors: JSON.stringify(formData.personalFactors || []),
        jobFactors: JSON.stringify(formData.jobFactors || []),
        rootCauseAnalysis: JSON.stringify(formData.rootCauseAnalysis || []),

        // Keep WCB/OHS reporting questions consistent with the main Submit flow
        wcbReportRequired: formData.wcbReportRequired || 'No',
        wcbReportReason: formData.wcbReportReason || '',
        wcbReportReasonOther: formData.wcbReportReasonOther || '',
        ohsReportRequired: formData.ohsReportRequired || 'No',
        ohsReportReason: formData.ohsReportReason || '',
        ohsReportReference: formData.ohsReportReference || ''
      };

      try { window.__lastPreparedCorrectiveActionSave = preparedFormData; } catch (e) { /* ignore */ }

      // Decide which incident identifier to use for the POST/GET/notify calls.
      // Prefer explicit incident link fields if present on the incident object (these indicate the parent INCIDENTS.ID).
      // As a stronger heuristic, scan all keys (and one-level nested objects) for any field name containing 'incident' whose value is numeric.
      const findParentIncidentId = (obj) => {
        if (!obj || typeof obj !== 'object') return null;
        const keys = Object.keys(obj);
        for (const key of keys) {
          const lower = key.toLowerCase();
          try {
            const val = obj[key];
            if (lower !== 'id' && lower.includes('incident') && val != null) {
              const valStr = String(val).trim();
              if (/^\d+$/.test(valStr)) return valStr;
            }
            // If value is an object, scan one level deeper
            if (val && typeof val === 'object') {
              const nested = findParentIncidentId(val);
              if (nested) return nested;
            }
          } catch (e) {
            // ignore and continue
          }
        }
        return null;
      };

      const explicitCandidate = incident?.incident_id || incident?.INCIDENT_ID || incident?.incidentId || null;
      const scannedCandidate = findParentIncidentId(incident);
  const targetId = explicitCandidate || scannedCandidate || getCanonicalIncidentId(incident);
      if (!targetId) {
        console.warn('No incident identifier found on incident object, defaulting to incident.id (may be a detail PK)', incident);
      } else if (String(targetId) !== String(getCanonicalIncidentId(incident))) {
        console.log('Using parent incident id resolved from detail object:', targetId, 'original id:', getCanonicalIncidentId(incident));
      }

  // POST the investigation using the resolved targetId
  const initialPostTarget = targetId;
  const postResponse = await API.post(`/api/incidents/${initialPostTarget}/investigation`, preparedFormData);

      // Determine the correct incident id to use for subsequent GET/notify calls.
      // Prefer any incident_id returned by the POST, otherwise use the resolved targetId.
      let lookupId = targetId;
      const postData = postResponse?.data;
      if (postData) {
        const candidate = postData.incident_id || postData.INCIDENT_ID || postData.incidentId || (postData.incident && (postData.incident.INCIDENT_ID || postData.incident.incident_id || postData.incident.id));
        if (candidate) lookupId = candidate;
      }

      // Try GET on the chosen lookupId; if that fails, attempt a heuristic scan across incidents and re-post to parent
      let response;
      try {
        response = await API.get(`/api/incidents/${lookupId}`);
      } catch (getErr) {
        // If the GET failed because lookupId was a detail PK (404), try a client-side heuristic:
        if (getErr?.response?.status === 404) {
          console.warn('GET /api/incidents/' + lookupId + ' returned 404 — attempting heuristic resolution');
          // Try to resolve parent incident by matching title/date/location from the incidents list
          try {
            const allResp = await API.get('/api/incidents');
            const candidates = Array.isArray(allResp.data) ? allResp.data : (allResp.data?.incidents || []);
            const matchByFields = (cand) => {
              try {
                const candDate = cand.DATE || cand.date || '';
                const candLocation = (cand.LOCATION || cand.location || '').toString().trim().toLowerCase();
                const candTitle = (cand.TITLE || cand.title || '').toString().trim().toLowerCase();
                const srcDate = formData.incidentDate || incident?.date || '';
                const srcLocation = (incident?.LOCATION || incident?.location || '').toString().trim().toLowerCase();
                const srcTitle = (incident?.TITLE || incident?.title || '').toString().trim().toLowerCase();
                // Match by date + (location or title)
                if (srcDate && candDate && String(candDate).startsWith(String(srcDate))) {
                  if (srcLocation && candLocation && candLocation.includes(srcLocation)) return true;
                  if (srcTitle && candTitle && candTitle.includes(srcTitle)) return true;
                }
                return false;
              } catch (e) { return false; }
            };
            const found = candidates.find(matchByFields);
            if (found) {
              const resolved = found.ID || found.id || found.IDENTIFIER || null;
              if (resolved) {
                console.log('Heuristic resolved parent incident id:', resolved, 'from candidate', found);
                lookupId = resolved;
                response = await API.get(`/api/incidents/${lookupId}`);
              }
            }
          } catch (heurErr) {
            console.warn('Heuristic resolution failed:', heurErr);
          }
        }

        // If still no response, as a last effort attempt to POST the investigation to the resolved lookupId (if different)
        if (!response && String(initialPostTarget) !== String(lookupId)) {
          try {
            console.log('Retrying POST to parent incident', lookupId, 'to ensure investigation row exists');
            await API.post(`/api/incidents/${lookupId}/investigation`, preparedFormData);
            response = await API.get(`/api/incidents/${lookupId}`);
          } catch (retryErr) {
            console.warn('Retry to save investigation to parent failed:', retryErr && retryErr.message);
          }
        }

        if (!response) throw getErr; // rethrow if unresolved
      }

      const updatedIncident = Array.isArray(response.data) ? response.data[0] : response.data;
      const updatedActions = Array.isArray(updatedIncident.CORRECTIVE_ACTIONS_JSON)
        ? updatedIncident.CORRECTIVE_ACTIONS_JSON
        : JSON.parse(updatedIncident.CORRECTIVE_ACTIONS_JSON || '[]');

      // Try to match the action by all fields
      const originalAction = formData.correctiveActions[index];
      const match = updatedActions.find(
        a =>
          a.description?.trim() === originalAction.description?.trim() &&
          a.assignedTo === originalAction.assignedTo &&
          a.targetDate === originalAction.targetDate
      );

      if (match && match.id) {
        await API.post(`/api/corrective-actions/${match.id}/notify`, {
          sendTo: match.assignedTo,
          incidentId: lookupId,
          description: match.description,
          targetDate: match.targetDate
        });
      } else {
        // If we posted the investigation to a detail PK (initialPostTarget) but resolved a different parent lookupId,
        // first retry saving the investigation to the parent so the INCIDENT_INVESTIGATIONS row is created/updated there.
        if (String(initialPostTarget) !== String(lookupId)) {
          try {
            console.log('Action not found in parent investigation; retrying to save investigation to parent incident', lookupId);
            // Re-post the prepared form data to the parent incident to ensure investigation row exists
            await API.post(`/api/incidents/${lookupId}/investigation`, preparedFormData);
            // Re-fetch the parent incident's investigation
            const retryResp = await API.get(`/api/incidents/${lookupId}`);
            const retryUpdated = Array.isArray(retryResp.data) ? retryResp.data[0] : retryResp.data;
            const retryActions = Array.isArray(retryUpdated.CORRECTIVE_ACTIONS_JSON)
              ? retryUpdated.CORRECTIVE_ACTIONS_JSON
              : JSON.parse(retryUpdated.CORRECTIVE_ACTIONS_JSON || '[]');
            const retryMatch = retryActions.find(a =>
              a.description?.trim() === originalAction.description?.trim() &&
              a.assignedTo === originalAction.assignedTo &&
              a.targetDate === originalAction.targetDate
            );
            if (retryMatch && retryMatch.id) {
              await API.post(`/api/corrective-actions/${retryMatch.id}/notify`, {
                sendTo: retryMatch.assignedTo,
                incidentId: lookupId,
                description: retryMatch.description,
                targetDate: retryMatch.targetDate
              });
              // done
            } else {
              // Still not found — as a last resort, try to create the corrective action under the parent
              try {
                console.log('Retry did not find action; creating corrective action under parent incident', lookupId);
                const caResp = await API.post('/api/corrective-actions', {
                  incidentId: lookupId,
                  description: originalAction.description,
                  assignedTo: originalAction.assignedTo,
                  targetDate: originalAction.targetDate
                });
                const createdActionId = caResp?.data?.id;
                if (createdActionId) {
                  await API.post(`/api/corrective-actions/${createdActionId}/notify`, {
                    sendTo: originalAction.assignedTo,
                    incidentId: lookupId,
                    description: originalAction.description,
                    targetDate: originalAction.targetDate
                  });
                } else {
                  alert('Could not create corrective action under parent incident. Please check the action details.');
                  return;
                }
              } catch (createErr2) {
                console.error('Failed to create/notify corrective action under parent incident after retry:', createErr2);
                alert('Could not create corrective action under parent incident: ' + (createErr2.response?.data?.error || createErr2.message));
                return;
              }
            }
          } catch (createErr) {
            console.error('Failed to retry save/notify under parent incident:', createErr);
            alert('Could not create corrective action under parent incident: ' + (createErr.response?.data?.error || createErr.message));
            return;
          }
        } else {
          alert('Could not find the saved corrective action to notify. Please check the action details.');
          return;
        }
      }

      // Optionally, refresh the form data from the backend using the resolved id
      const refreshed = await fetchRefreshedIncidentById(lookupId);
      if (refreshed) {
  // Preserve the most authoritative investigation status when applying refreshed data
  const resolvedStatus2 = resolveInvestigationStatus(refreshed, incident || {});
  setIncident({ ...incident, ...refreshed, investigationStatus: resolvedStatus2, INVESTIGATION_STATUS: resolvedStatus2 });
        // Merge refreshed server data with current formData so user-entered fields aren't lost
        try {
          const merged = { ...refreshed };
          const mergeIfMissingLocal = (tKey, fKey) => {
            if ((merged[tKey] === undefined || merged[tKey] === null || merged[tKey] === '' || (Array.isArray(merged[tKey]) && merged[tKey].length === 0)) && formData && formData[fKey] !== undefined) {
              merged[tKey] = formData[fKey];
            }
          };
          mergeIfMissingLocal('immediateActions', 'immediateActions');
          mergeIfMissingLocal('CONTRIBUTING_FACTORS', 'immediateActions');
          mergeIfMissingLocal('unsafeActs', 'unsafeActs');
          mergeIfMissingLocal('unsafeConditions', 'unsafeConditions');
          mergeIfMissingLocal('correctiveActionsJson', 'correctiveActions');
          setFormDataSafe(normalizeInvestigationFormData(merged));
        } catch (e) {
          console.warn('Failed to merge local formData with refreshed:', e);
          setFormDataSafe(normalizeInvestigationFormData(refreshed));
        }
      }

      alert('Corrective action saved and notification sent!');
    } catch (error) {
      console.error('Failed to save and notify:', error);
      alert('Failed to save and notify: ' + (error.response?.data?.error || error.message));
    }
};

  const testBackdatedNotifications = async (index) => {
    const action = formData.correctiveActions[index];
    
    if (!action.assignedTo || !action.targetDate) {
      alert('Please fill in assignee and target date before testing notifications');
      return;
    }
    
    // Create notification element to show status
    const notifyElement = document.createElement('div');
    notifyElement.textContent = 'Testing backdated notifications...';
    notifyElement.style.position = 'fixed';
    notifyElement.style.top = '20px';
    notifyElement.style.right = '20px';
    notifyElement.style.backgroundColor = '#f0f9ff';
    notifyElement.style.border = '1px solid #bae6fd';
    notifyElement.style.borderRadius = '4px';
    notifyElement.style.padding = '10px 20px';
    notifyElement.style.zIndex = '9999';
    document.body.appendChild(notifyElement);
    
    try {
      // Request to test backdated notifications
      const response = await API.post(`/api/corrective-actions/test-backdated-notification`, {
        incidentId: incident.id,
        department: incident.PRIMARY_DEPARTMENT || incident.primaryDepartment,
        assignedTo: action.assignedTo,
        targetDate: action.targetDate,
        description: action.description || 'Test backdated action',
        daysOverdue: 3 // Simulate 3 days overdue to test both supervisor and manager notifications
      });
      
      // Update UI with success message
      notifyElement.textContent = 'Test notifications sent! Check emails.';
      notifyElement.style.backgroundColor = '#dcfce7';
      notifyElement.style.border = '1px solid #86efac';
      
      // Show detailed results
      setTimeout(() => {
        alert(`Test Notification Results:
        
Department: ${response.data.departmentInfo.department}
Supervisor: ${response.data.departmentInfo.supervisorEmail}
Manager: ${response.data.departmentInfo.managerEmail}

Notifications successfully sent to department management.
A copy was also sent to your email for verification.`);
        
        document.body.removeChild(notifyElement);
      }, 2000);
    } catch (error) {
      console.error('Failed to test notifications:', error);
      
      // Show error notification
      notifyElement.textContent = `Error: ${error.response?.data?.error || error.message}`;
      notifyElement.style.backgroundColor = '#fee2e2';
      notifyElement.style.border = '1px solid #fecaca';
      
      setTimeout(() => {
        document.body.removeChild(notifyElement);
      }, 5000);
    }
  };

  const workflowOptions = (
    <div style={{ marginBottom: '24px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
      <button
        style={{
          width: '100%',
          backgroundColor: '#000',
          color: '#fff',
          padding: '12px',
          textAlign: 'left',
          fontWeight: '500',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: 'none',
          cursor: 'pointer',
        }}
        onClick={() => toggleSection('workflow')}
      >
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px', fontSize: '20px' }}>⏰</span>
          Workflow Options
        </span>
        {expandedSections.workflow ? (
          <span style={{ fontSize: '20px' }}>▲</span>
        ) : (
          <span style={{ fontSize: '20px' }}>▼</span>
        )}
      </button>

      {expandedSections.workflow && (
        <div style={{ padding: '16px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
              Add Delegate (When supervisor or manager is away):
            </label>
            <div className="form-group">
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="delegateSelected"
                    value="false"
                    checked={!formData.delegateSelected}
                    onChange={() => setFormData({ ...formData, delegateSelected: false, delegateEmail: '' })}
                    disabled={viewMode === 'locked'}
                  />
                  No
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="delegateSelected"
                    value="true"
                    checked={formData.delegateSelected}
                    onChange={() => setFormData({ ...formData, delegateSelected: true })}
                    disabled={viewMode === 'locked'}
                  />
                  Yes
                </label>
              </div>

              {formData.delegateSelected && (
                <div className="delegate-section">
                  <select
                    name="delegateEmail"
                    value={formData.delegateEmail}
                    onChange={(e) => setFormData({ ...formData, delegateEmail: e.target.value })}
                    className="form-select"
                    required={formData.delegateSelected}
                    disabled={viewMode === 'locked'}
                  >
                    <option value="">Select Delegate</option>
                    {employees.map((employee) => (
                      <option key={employee.email} value={employee.email}>
                        {employee.firstName} {employee.lastName} ({employee.email}) - {employee.department || 'No Department'}
                      </option>
                    ))}
                  </select>
                  <div className="form-text info-text form-textarea"></div>
                  <button
                    type="button"
                    className="btn btn-secondary mt-3"
                    onClick={async () => {
                      if (!formData.delegateEmail) {
                        alert('Please select a delegate first');
                        return;
                      }
                      try {
                        const token = localStorage.getItem('authToken');
                        await API.post(
                          '/api/notifications/assign-delegate',
                          {
                            incidentId: incident.id,
                            delegateEmail: formData.delegateEmail,
                            delegateInstructions: formData.delegateComments || 'Please review this incident as a delegate',
                          },
                          {
                            headers: {
                              'Authorization': token ? `Bearer ${token}` : '',
                              'Content-Type': 'application/json'
                            }
                          }
                        );
                        await API.post('/api/notifications/send', {
                          eventType: 'delegateAssigned',
                          data: {
                            incidentId: getCanonicalIncidentId(incident),
                            incidentType: incident?.incidentType,
                            incidentDate: formData.incidentDate,
                            location: formData.location,
                            delegateEmail: formData.delegateEmail,
                            delegateComments: formData.delegateComments,
                            primaryDepartment: incident?.primaryDepartment,
                            dashboardLink: `${window.location.origin}${getAppPath(`/fire-incident-investigations?incidentId=${incident?.incident_id || incident?.INCIDENT_ID || incident?.id}`)}`
                          }
                        });
                        await API.post('/api/notifications/send', {
                          eventType: 'delegateDepartmentNotification',
                          data: {
                            incidentId: getCanonicalIncidentId(incident),
                            incidentType: incident?.incidentType,
                            incidentDate: formData.incidentDate,
                            location: formData.location,
                            delegateEmail: formData.delegateEmail,
                            delegateComments: formData.delegateComments,
                            delegateDepartment: employees.find(emp => emp.email === formData.delegateEmail)?.department || incident?.primaryDepartment,
                            dashboardLink: `${window.location.origin}${getAppPath(`/fire-incident-investigations?incidentId=${incident?.incident_id || incident?.INCIDENT_ID || incident?.id}`)}`
                          }
                        });
                        alert(`Delegate assigned and notification sent to ${formData.delegateEmail}`);
                        fetchIncidents && fetchIncidents();
                        if (fetchRefreshedIncidentById) {
                          const refreshed = await fetchRefreshedIncidentById(incident.id);
                            if (refreshed) {
                            const resolved = resolveInvestigationStatus(refreshed, incident || {});
                            setIncident({ ...incident, ...refreshed, investigationStatus: resolved, INVESTIGATION_STATUS: resolved });
                            try {
                              const merged = { ...refreshed };
                              const mergeIfMissingLocal = (tKey, fKey) => {
                                if ((merged[tKey] === undefined || merged[tKey] === null || merged[tKey] === '' || (Array.isArray(merged[tKey]) && merged[tKey].length === 0)) && formData && formData[fKey] !== undefined) {
                                  merged[tKey] = formData[fKey];
                                }
                              };
                              mergeIfMissingLocal('immediateActions', 'immediateActions');
                              mergeIfMissingLocal('CONTRIBUTING_FACTORS', 'immediateActions');
                              mergeIfMissingLocal('unsafeActs', 'unsafeActs');
                              mergeIfMissingLocal('unsafeConditions', 'unsafeConditions');
                              setFormDataSafe(normalizeInvestigationFormData(merged));
                            } catch (e) {
                              console.warn('Failed to merge local formData with refreshed after delegate assign:', e);
                              setFormDataSafe(normalizeInvestigationFormData(refreshed));
                            }
                          }
                        }
                      } catch (error) {
                        console.error('Error in delegate process:', error);
                        if (error.response?.status === 401) {
                          alert('Authentication error: You need to be logged in to assign delegates. Please log in again.');
                        } else if (error.response?.status === 403) {
                          alert('Authorization error: You do not have permission to assign delegates.');
                        } else {
                          alert(`Failed: ${error.message}`);
                        }
                      }
                    }}
                    disabled={viewMode === 'locked'}
                  >
                    Test Delegate Notification
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                padding: '12px',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
              }}
            >
              <h3 style={{ fontWeight: '500', marginBottom: '8px' }}>Auto-notifications will be sent to:</h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', fontSize: '14px', lineHeight: '1.5' }}>
                <li>Your supervisor (Auto-selected based on department)</li>
                <li>Your manager (Auto-selected based on department)</li>
                <li>Health & Safety Advisor</li>
                {(incidentType === 'injury-lost-time' ||
                  incidentType === 'hospital-admittance' ||
                  incidentType === 'ohs-serious') && (
                  <li style={{ color: '#dc2626', fontWeight: '500' }}>CAO (Due to incident type)</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    if (isFormLocked || isWcbReadOnlyViewer) {
      setViewMode('locked');
    } else {
      setViewMode('edit');
    }
  }, [isFormLocked, isWcbReadOnlyViewer]);

  if (userPermissions.isDelegate) {
    return (
      <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Delegate View - Incident Investigation</DialogTitle>
        <DialogContent>
            <DelegateView 
            incident={incident}
            onClose={onClose}
            onComplete={() => {
              alert('Thank you for completing your delegate tasks!');
              onClose();
              // Refresh incident data in-app instead of reloading the page
              try {
                const currentId = String(getCanonicalIncidentId(incident) || correctIncidentId || incident?.id);
                if (currentId) {
                  console.log('Refreshing incident in-app after delegate completion:', currentId);
                  if (fetchRefreshedIncidentById) {
                    fetchRefreshedIncidentById(currentId).then(ref => {
                      if (ref && onIncidentUpdate) {
                        const canonicalRefId3 = String(getCanonicalIncidentId(ref) || ref?.id || ref?.ID || currentId || '');
                        onIncidentUpdate({ ...ref, id: canonicalRefId3, ID: canonicalRefId3 });
                      }
                    }).catch(e => console.error('Error refreshing after delegate completion:', e));
                  }
                }
              } catch (e) {
                console.error('Failed to refresh after delegate completion:', e);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }



  const resolveReportedByDisplay = (srcIncident = {}) => {
    const clean = normalizeDisplayValue;

    const rawAnonymousFlag =
      srcIncident.IS_ANONYMOUS ??
      srcIncident.isAnonymous ??
      srcIncident.is_anonymous;

    const normalizedAnonymousFlag = String(rawAnonymousFlag ?? '').trim().toLowerCase();
    if (
      rawAnonymousFlag === true ||
      rawAnonymousFlag === 1 ||
      ['true', 'yes', '1', 'y'].includes(normalizedAnonymousFlag)
    ) {
      return 'Anonymous Reporter';
    }

    const employeeEmail = resolveIncidentEmployeeEmail(srcIncident);

    const rawEmailOrUser =
      clean(srcIncident.USER) ||
      clean(srcIncident.user) ||
      clean(srcIncident.REPORTED_BY_EMAIL) ||
      clean(srcIncident.reportedByEmail) ||
      clean(srcIncident.reportedBy) ||
      clean(srcIncident.reported_by) ||
      clean(srcIncident.REPORTED_BY) ||
      '';

    const submitterEmail = rawEmailOrUser.includes('@') ? rawEmailOrUser : '';
    const email = employeeEmail || submitterEmail;

    const rawName =
      clean(srcIncident.REPORTED_BY_NAME) ||
      clean(srcIncident.reportedByName) ||
      clean(srcIncident.REPORTED_BY_FULL_NAME) ||
      clean(srcIncident.reportedByFullName) ||
      clean(srcIncident.REPORTING_FIREFIGHTER_NAME) ||
      clean(srcIncident.reportingFirefighterName) ||
      clean(srcIncident.REPORTING_EMPLOYEE_NAME) ||
      clean(srcIncident.reportingEmployeeName) ||
      clean(srcIncident.EMPLOYEE_NAME) ||
      clean(srcIncident.employeeName) ||
      '';

    if (rawName.toLowerCase() === 'anonymous reporter' || rawName.toLowerCase() === 'anonymous') {
      return 'Anonymous Reporter';
    }

    // If REPORTED_BY contains a human-readable name (not an email), keep it.
    let resolvedName = rawName || (rawEmailOrUser && !rawEmailOrUser.includes('@') ? rawEmailOrUser : '');

    if (resolvedName.toLowerCase() === 'anonymous reporter' || resolvedName.toLowerCase() === 'anonymous') {
      return 'Anonymous Reporter';
    }

    // If we only have an email, resolve it via the employees directory already loaded on this page.
    if (!resolvedName && email) {
      try {
        const emailLower = email.toLowerCase();
        const directory = (usersDirectory && usersDirectory.length > 0) ? usersDirectory : (employees || []);
        const match = directory.find((emp) => {
          const empEmail = clean(emp?.email || emp?.EMAIL || emp?.username || emp?.USERNAME).toLowerCase();
          return empEmail && empEmail === emailLower;
        });

        if (match) {
          const first = clean(match.firstName || match.FIRST_NAME || match.firstname || match.first_name);
          const last = clean(match.lastName || match.LAST_NAME || match.lastname || match.last_name);
          const full = `${first} ${last}`.trim();
          if (full) resolvedName = full;
        }
      } catch (e) {
        // ignore
      }
    }

    if (resolvedName && email) return `${resolvedName} (${email})`;
    if (resolvedName) return resolvedName;
    if (email) return email;
    return 'Not available';
  };

  const resolveDirectoryNameByEmail = (email) => {
    const normalizedEmail = normalizeDisplayValue(email).toLowerCase();
    if (!normalizedEmail) return '';

    try {
      const directory = (usersDirectory && usersDirectory.length > 0) ? usersDirectory : (employees || []);
      const match = directory.find((emp) => {
        const empEmail = normalizeDisplayValue(emp?.email || emp?.EMAIL || emp?.username || emp?.USERNAME).toLowerCase();
        return empEmail && empEmail === normalizedEmail;
      });

      if (!match) return '';

      const first = normalizeDisplayValue(match.firstName || match.FIRST_NAME || match.firstname || match.first_name);
      const last = normalizeDisplayValue(match.lastName || match.LAST_NAME || match.lastname || match.last_name);
      return `${first} ${last}`.trim();
    } catch (e) {
      return '';
    }
  };

  const formatReferencePersonDisplay = (name, email, fallback = 'N/A') => {
    const normalizedName = normalizeDisplayValue(name);
    const normalizedEmail = normalizeDisplayValue(email);

    if (normalizedName && normalizedEmail) return `${normalizedName} ${normalizedEmail}`;
    if (normalizedName) return normalizedName;
    if (normalizedEmail) return normalizedEmail;
    return fallback;
  };

  const resolveSubmittedByReferenceDisplay = (srcIncident = {}) => {
    const rawAnonymousFlag =
      srcIncident.IS_ANONYMOUS ??
      srcIncident.isAnonymous ??
      srcIncident.is_anonymous;

    const normalizedAnonymousFlag = String(rawAnonymousFlag ?? '').trim().toLowerCase();
    if (
      rawAnonymousFlag === true ||
      rawAnonymousFlag === 1 ||
      ['true', 'yes', '1', 'y'].includes(normalizedAnonymousFlag)
    ) {
      return 'Anonymous Reporter';
    }

    const email = normalizeDisplayValue(
      srcIncident.USER ||
      srcIncident.user ||
      srcIncident.REPORTED_BY_EMAIL ||
      srcIncident.reportedByEmail ||
      srcIncident.reportedBy ||
      srcIncident.reported_by ||
      srcIncident.REPORTED_BY ||
      ''
    );
    const name = normalizeDisplayValue(
      srcIncident.REPORTED_BY_NAME ||
      srcIncident.reportedByName ||
      srcIncident.REPORTED_BY_FULL_NAME ||
      srcIncident.reportedByFullName ||
      srcIncident.REPORTING_FIREFIGHTER_NAME ||
      srcIncident.reportingFirefighterName ||
      srcIncident.REPORTING_EMPLOYEE_NAME ||
      srcIncident.reportingEmployeeName ||
      srcIncident.EMPLOYEE_NAME ||
      srcIncident.employeeName ||
      resolveDirectoryNameByEmail(email)
    );

    return formatReferencePersonDisplay(name, email, 'N/A');
  };

  const resolveOnBehalfOfReferenceDisplay = (srcIncident = {}) => {
    const onBehalfFlag = String(
      srcIncident.COMPLETED_BY_INJURED_PARTY ||
      srcIncident.completedByInjuredParty ||
      ''
    ).trim().toLowerCase();

    if (!['yes', 'true', '1', 'y'].includes(onBehalfFlag)) {
      return 'N/A';
    }

    const employeeEmail = resolveIncidentEmployeeEmail(srcIncident);
    const employeeName = resolveDirectoryNameByEmail(employeeEmail) || normalizeDisplayValue(
      srcIncident.EMPLOYEE_NAME ||
      srcIncident.employeeName ||
      srcIncident.REPORTING_EMPLOYEE_NAME ||
      srcIncident.reportingEmployeeName
    );

    return formatReferencePersonDisplay(employeeName, employeeEmail, 'N/A');
  };

  const resolveIdentifiedInvestigatorReferenceDisplay = (srcIncident = {}) => {
    const investigatorEmail = getAssignedInvestigatorEmail(srcIncident);
    const investigatorName = resolveDirectoryNameByEmail(investigatorEmail);
    return formatReferencePersonDisplay(investigatorName, investigatorEmail, 'N/A');
  };

  const resolveInjuredPartyDisplay = (srcIncident = {}) => {
    const clean = normalizeDisplayValue;

    // Fire incident reporting stores the selected firefighter email in INCIDENTS.EMPLOYEE_ID
    const raw =
      clean(srcIncident.EMPLOYEE_ID) ||
      clean(srcIncident.employee_id) ||
      clean(srcIncident.employeeId) ||
      clean(srcIncident.EMPLOYEES) ||
      clean(srcIncident.employees) ||
      '';

    const email = raw.includes('@') ? raw : '';
    let resolvedName = raw && !raw.includes('@') ? raw : '';

    if (!resolvedName && email) {
      try {
        const emailLower = email.toLowerCase();
        const directory = (usersDirectory && usersDirectory.length > 0) ? usersDirectory : (employees || []);
        const match = directory.find((emp) => {
          const empEmail = clean(emp?.email || emp?.EMAIL || emp?.username || emp?.USERNAME).toLowerCase();
          return empEmail && empEmail === emailLower;
        });

        if (match) {
          const first = clean(match.firstName || match.FIRST_NAME || match.firstname || match.first_name);
          const last = clean(match.lastName || match.LAST_NAME || match.lastname || match.last_name);
          const full = `${first} ${last}`.trim();
          if (full) resolvedName = full;
        }
      } catch (e) {
        // ignore
      }
    }

    if (resolvedName && email) return `${resolvedName} (${email})`;
    if (resolvedName) return resolvedName;
    if (email) return email;
    return 'Not specified';
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Incident Investigation Form</DialogTitle>
      <DialogContent>
        {incident && (
          <div className="incident-header">
            <p><strong>{incident.title}</strong> at {incident.location}</p>
            <p className="incident-severity">Severity: {incident.severity}</p>

            <div className="reference-information">
              <h4>Reference Information</h4>
              <table className="reference-table">
                <tbody>
                  <tr>
                    <td>Incident ID:</td>
                    <td>{incident.id || incident.ID || 'Not available'}</td>
                  </tr>
                  <tr>
                    <td>Department:</td>
                    <td>
                      {getDepartmentDisplayName(
                        incident.primaryDepartment ||
                        incident.PRIMARY_DEPARTMENT ||
                        incident.department ||
                        incident.DEPARTMENT ||
                        'Not specified'
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Submitted by:</td>
                    <td>
                      {resolveSubmittedByReferenceDisplay(incident)}
                    </td>
                  </tr>
                  <tr>
                    <td>On behalf of:</td>
                    <td>
                      {resolveOnBehalfOfReferenceDisplay(incident)}
                    </td>
                  </tr>
                  <tr>
                    <td>Identified Investigator:</td>
                    <td>
                      {resolveIdentifiedInvestigatorReferenceDisplay(incident)}
                    </td>
                  </tr>
                  {incident.wcbClaimNumber && (
                    <tr>
                      <td>WCB Claim Number:</td>
                      <td>{incident.wcbClaimNumber}</td>
                    </tr>
                  )}
                  {incident.ohsReportNumber && (
                    <tr>
                      <td>OHS Report Number:</td>
                      <td>{incident.ohsReportNumber}</td>
                    </tr>
                  )}
                  {incident.witnesses && (
                    <tr>
                      <td>Witnesses:</td>
                      <td>
                        {(() => {
                          if (!incident.witnesses) return 'None';
                          
                          if (typeof incident.witnesses === 'string') {
                            return incident.witnesses;
                          }
                          
                          if (Array.isArray(incident.witnesses)) {
                            if (incident.witnesses.length > 0 && typeof incident.witnesses[0] === 'string') {
                              return incident.witnesses.join(', ');
                            }
                            
                            if (incident.witnesses.length > 0 && typeof incident.witnesses[0] === 'object') {
                              return incident.witnesses
                                .map(witness => witness.name || witness.NAME || 
                                               witness.witnessName || witness.WITNESS_NAME || 
                                               JSON.stringify(witness))
                                .join(', ');
                            }
                            
                            return 'None specified';
                          }
                          
                          if (typeof incident.witnesses === 'object') {
                            if (incident.witnesses.value || incident.witnesses.VALUE) {
                              const values = incident.witnesses.value || incident.witnesses.VALUE;
                              if (Array.isArray(values)) {
                                return values.join(', ');
                              }
                              return String(values);
                            }
                            
                            return Object.values(incident.witnesses)
                              .filter(v => typeof v === 'string' && v.length < 100)
                              .join(', ') || 'Data format not recognized';
                          }
                          
                          return String(incident.witnesses);
                        })()}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>Date:</td>
                    <td>
                      {incident.date || 'Not specified'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* --- MOVE "Original Incident Report" SECTION HERE --- */}
        <div className="original-incident-details">
          <div 
            className="section-header expandable" 
            onClick={() => toggleSection('originalIncident')}
          >
            <h3>
              Original Incident Report 
              {expandedSections.originalIncident ? <ExpandLess /> : <ExpandMore />}
            </h3>
          </div>
          
          <Collapse in={expandedSections.originalIncident}>
            <div className="original-incident-content card">
              <div className="details-grid">
                <div className="detail-item">
                  <span className="label">Incident Type:</span>
                  <span className="value">{incident.title}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Date/Time:</span>
                  <span className="value">
                    {formatDisplayDate(incident.date)} at {incident.time || 'N/A'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Location:</span>
                  <span className="value">{incident.location}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Severity:</span>
                  <span className="value">{incident.severity}</span>
                </div>
                {(incident.REPORTING_FIREFIGHTER_NAME || incident.reportingFirefighterName) && (
                  <div className="detail-item">
                    <span className="label">Reporting Firefighter:</span>
                    <span className="value">{incident.REPORTING_FIREFIGHTER_NAME || incident.reportingFirefighterName}</span>
                  </div>
                )}

                {(incident.EMPLOYEE_ID || incident.employee_id || incident.employees) && (
                  <div className="detail-item">
                    <span className="label">Injured Party:</span>
                    <span className="value">{resolveInjuredPartyDisplay(incident)}</span>
                  </div>
                )}

                {(incident.SUPERVISOR_EMAIL || incident.supervisorEmail) && (
                  <div className="detail-item">
                    <span className="label">Supervisor (Email):</span>
                    <span className="value">{incident.SUPERVISOR_EMAIL || incident.supervisorEmail}</span>
                  </div>
                )}
                {(incident.MANAGER_EMAIL || incident.managerEmail) && (
                  <div className="detail-item">
                    <span className="label">Manager (Email):</span>
                    <span className="value">{incident.MANAGER_EMAIL || incident.managerEmail}</span>
                  </div>
                )}
                <div className="detail-item full-width">
                  <span className="label">Description:</span>
                  <div className="value description-text">{incident.description}</div>
                </div>
                {/* --- TYPE-SPECIFIC FIELDS --- */}
                {(() => {
                    switch (incident.title) {
                    case 'Injuries Lost-time':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Employee Name:</span>
                            <span className="value">{incident.EMPLOYEE_NAME || incident.employeeName || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Date of Injury:</span>
                            <span className="value">{incident.DATE_OF_INJURY || incident.dateOfInjury || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Hours Lost:</span>
                            <span className="value">{incident.HOURS_LOST || incident.hoursLost || 'N/A'}</span>
                          </div>
                          <div className="detail-item full-width">
                            <span className="label">Description:</span>
                            <span className="value">{incident.DESCRIPTION || incident.description || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'Injuries Non-lost time':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Employee Name:</span>
                            <span className="value">{incident.EMPLOYEE_NAME || incident.employeeName || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Date of Injury:</span>
                            <span className="value">{incident.DATE_OF_INJURY || incident.dateOfInjury || 'N/A'}</span>
                          </div>
                          <div className="detail-item full-width">
                            <span className="label">Description:</span>
                            <span className="value">{incident.DESCRIPTION || incident.description || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'First Aid':
                      console.log('First Aid incident data:', {
                        id: incident.id,
                        TREATMENT_PROVIDED: incident.TREATMENT_PROVIDED,
                        treatmentProvided: incident.treatmentProvided,
                        BODY_PART_AFFECTED: incident.BODY_PART_AFFECTED,
                        bodyPartAffected: incident.bodyPartAffected,
                        INJURY_TYPE: incident.INJURY_TYPE,
                        injuryType: incident.injuryType,
                        MEDICAL_ATTENTION_REQUIRED: incident.MEDICAL_ATTENTION_REQUIRED,
                        medicalAttentionRequired: incident.medicalAttentionRequired,
                        // Check for any fields that might contain the data
                        possibleTreatmentFields: Object.keys(incident).filter(key => 
                          key.toLowerCase().includes('treatment')),
                        possibleBodyPartFields: Object.keys(incident).filter(key => 
                          key.toLowerCase().includes('body') || key.toLowerCase().includes('part')),
                        possibleInjuryFields: Object.keys(incident).filter(key => 
                          key.toLowerCase().includes('injury')),
                        possibleMedicalFields: Object.keys(incident).filter(key => 
                          key.toLowerCase().includes('medical') || key.toLowerCase().includes('attention')),
                        allKeys: Object.keys(incident)
                      });
                      
                      // Helper function to find field value with multiple possible names
                      const getFieldValue = (possibleNames) => {
                        for (const name of possibleNames) {
                          if (incident[name] !== undefined && incident[name] !== null && incident[name] !== '') {
                            return incident[name];
                          }
                        }
                        return 'N/A';
                      };
                      
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Treatment Provided:</span>
                            <span className="value">{getFieldValue([
                              'TREATMENT_PROVIDED', 'treatmentProvided', 'treatment_provided',
                              'TREATMENT', 'treatment'
                            ])}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Body Part Affected:</span>
                            <span className="value">{getFieldValue([
                              'BODY_PART_AFFECTED', 'bodyPartAffected', 'body_part_affected',
                              'BODY_PART', 'bodyPart', 'body_part'
                            ])}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Injury Type:</span>
                            <span className="value">{getFieldValue([
                              'INJURY_TYPE', 'injuryType', 'injury_type',
                              'INJURY', 'injury'
                            ])}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Medical Attention Required:</span>
                            <span className="value">{getFieldValue([
                              'MEDICAL_ATTENTION_REQUIRED', 'medicalAttentionRequired', 'medical_attention_required',
                              'MEDICAL_ATTENTION', 'medicalAttention', 'medical_attention'
                            ])}</span>
                          </div>
                        </>
                      );
                    case 'Hospital Admittance':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Patient Name:</span>
                            <span className="value">{incident.PATIENT_NAME || incident.patientName || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Date of Birth:</span>
                            <span className="value">{incident.DATE_OF_BIRTH || incident.dateOfBirth || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Admission Date:</span>
                            <span className="value">{incident.ADMISSION_DATE || incident.admissionDate || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Reason:</span>
                            <span className="value">{incident.REASON || incident.reason || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Doctor:</span>
                            <span className="value">{incident.DOCTOR || incident.doctor || 'N/A'}</span>
                          </div>
                          <div className="detail-item full-width">
                            <span className="label">Notes:</span>
                            <span className="value">{incident.NOTES || incident.notes || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'Fatalities':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Name:</span>
                            <span className="value">{incident.NAME || incident.name || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Cause:</span>
                            <span className="value">{incident.CAUSE || incident.cause || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Date:</span>
                            <span className="value">{incident.DATE || incident.date || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'Near Misses':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Near Miss Date:</span>
                            <span className="value">{incident.NEAR_MISS_DATE || incident.nearMissDate || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Near Miss Time:</span>
                            <span className="value">{incident.NEAR_MISS_TIME || incident.nearMissTime || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Submitted By:</span>
                            <span className="value">{resolveSubmittedByReferenceDisplay(incident)}</span>
                          </div>
                          {resolveOnBehalfOfReferenceDisplay(incident) !== 'N/A' && (
                            <div className="detail-item">
                              <span className="label">On Behalf Of:</span>
                              <span className="value">{resolveOnBehalfOfReferenceDisplay(incident)}</span>
                            </div>
                          )}
                          <div className="detail-item">
                            <span className="label">Actions Taken:</span>
                            <span className="value">{incident.ACTIONS_TAKEN || incident.actionsTaken || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Supervisor:</span>
                            <span className="value">{incident.SUPERVISOR || incident.supervisor || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'Vandalism':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Damage Type:</span>
                            <span className="value">{incident.DAMAGE_TYPE || incident.damageType || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Estimated Cost:</span>
                            <span className="value">{incident.ESTIMATED_COST || incident.estimatedCost || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Items Damaged:</span>
                            <span className="value">{incident.ITEMS_DAMAGED || incident.itemsDamaged || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Suspected Perpetrator:</span>
                            <span className="value">{incident.SUSPECTED_PERPETRATOR || incident.suspectedPerpetrator || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Reported to Police:</span>
                            <span className="value">{incident.REPORTED_TO_POLICE || incident.reportedToPolice || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Police Report Number:</span>
                            <span className="value">{incident.POLICE_REPORT_NUMBER || incident.policeReportNumber || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'Property/Equipment Damages':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Equipment Involved:</span>
                            <span className="value">{String(incident.EQUIPMENT_INVOLVED || incident.equipmentInvolved || 'N/A')}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Equipment (other):</span>
                            <span className="value">{incident.EQUIPMENT_OTHER || incident.equipmentOther || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Property Involved:</span>
                            <span className="value">{String(incident.PROPERTY_INVOLVED || incident.propertyInvolved || 'N/A')}</span>
                          </div>
                          <div className="detail-item full-width">
                            <span className="label">Property Description:</span>
                            <span className="value">{incident.PROPERTY_DESCRIPTION || incident.propertyDescription || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Over $5,000:</span>
                            <span className="value">{String(incident.OVER_5000 || incident.over5000 || 'N/A')}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Under $5,000:</span>
                            <span className="value">{String(incident.UNDER_5000 || incident.under5000 || 'N/A')}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Estimated Damages:</span>
                            <span className="value">{incident.ESTIMATED_DAMAGES || incident.estimatedDamages || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Reported to Police:</span>
                            <span className="value">{String(incident.REPORTED_TO_POLICE || incident.reportedToPolice || 'N/A')}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Police Report Date:</span>
                            <span className="value">{incident.POLICE_REPORT_DATE || incident.policeReportDate || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'Vehicle Incident':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Vehicle Type:</span>
                            <span className="value">{incident.VEHICLE_TYPE || incident.vehicleType || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Vehicle (Other):</span>
                            <span className="value">{incident.VEHICLE_OTHER || incident.vehicleOther || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">3rd Party Involved:</span>
                            <span className="value">{String(incident.THIRD_PARTY_INVOLVED || incident.thirdPartyInvolved || 'No')}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">3rd Party Name:</span>
                            <span className="value">{incident.THIRD_PARTY_NAME || incident.thirdPartyName || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">License Plate (Other Vehicle):</span>
                            <span className="value">{incident.THIRD_PARTY_LICENSE_PLATE || incident.thirdPartyLicensePlate || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Insurance (Other Vehicle):</span>
                            <span className="value">{incident.THIRD_PARTY_INSURANCE || incident.thirdPartyInsurance || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Reported to Police:</span>
                            <span className="value">{String(incident.REPORTED_TO_POLICE || incident.reportedToPolice || 'N/A')}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Police Report Date:</span>
                            <span className="value">{incident.POLICE_REPORT_DATE || incident.policeReportDate || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'Work Refusal':
                      return (
                        <>
                          <div className="detail-item full-width">
                            <span className="label">Description of the Refusal:</span>
                            <span className="value">
                              {incident.ISSUE_DESCRIPTION || incident.issueDescription || 'N/A'}
                            </span>
                          </div>
                          {incident.PHOTO_URLS && Array.isArray(incident.PHOTO_URLS) && incident.PHOTO_URLS.length > 0 && (
                            <div className="detail-item full-width">
                              <span className="label">Attached Photos:</span>
                              <div className="value">
                                <ul style={{ paddingLeft: 16 }}>
                                  {incident.PHOTO_URLS.map((photoPath, idx) => (
                                  <li key={idx}>
                                    <a href={getApiResourceUrl(photoPath)} target="_blank" rel="noopener noreferrer">
                                        View Photo
                                    </a>
                                  </li>
                                ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    case 'Health & Safety Concerns / Issues':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Issue Description:</span>
                            <span className="value">{incident.ISSUE_DESCRIPTION || incident.issueDescription || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Is Recurring:</span>
                            <span className="value">{incident.IS_RECURRING !== undefined ? (incident.IS_RECURRING ? 'Yes' : 'No') : 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Previous Issue Details:</span>
                            <span className="value">{incident.PREVIOUS_ISSUE_DETAILS || incident.previousIssueDetails || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Additional Notifications:</span>
                            <span className="value">{incident.ADDITIONAL_NOTIFICATIONS || incident.additionalNotifications || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Selected Manager ID:</span>
                            <span className="value">{incident.SELECTED_MANAGER_ID || incident.selectedManagerId || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'OHS Serious':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">OHS File Number / Details:</span>
                            <span className="value">{incident.DETAILS || incident.details || incident.fileNumber || 'N/A'}</span>
                          </div>
                        </>
                      );
                    case 'PSI':
                      return (
                        <>
                          <div className="detail-item">
                            <span className="label">Details:</span>
                            <span className="value">{incident.DETAILS || incident.details || 'N/A'}</span>
                          </div>
                        </>
                      );
                    default:
                      return null;
                  }
                })()}
                {renderOriginalReportSubmittedLinks()}
                {/* --- WCB Information if available --- */}
                {incident.wcbReportRequired && (
                  <>
                    <div className="detail-item">
                      <span className="label">WCB Claim #:</span>
                      <span className="value">{incident.wcbClaimNumber || 'Not yet assigned'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">WCB Submitted:</span>
                      <span className="value">
                        {incident.wcbReportSubmittedDate ? formatDisplayDate(incident.wcbReportSubmittedDate) : 'Not submitted'}
                      </span>
                    </div>
                  </>
                )}
                
                {/* --- Incident Attachments --- */}
                {/* Display all incident attachments */}
                {originalIncidentAttachments.length > 0 && (
                  <div className="detail-item full-width">
                    <span className="label">Original Incident Attachments:</span>
                    <div className="value">
                      <ul className="attachments-display" style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                        {originalIncidentAttachments.map((attachment, index) => {
                            const fileId = attachment.fileId || 
                                          attachment.fileid || 
                                          attachment.FILEID || 
                                          attachment.FILE_ID || 
                                          attachment.id || 
                                          attachment.ID;
                            const attachmentUrl = attachment.downloadUrl
                              ? getApiResourceUrl(attachment.downloadUrl)
                              : (fileId ? getApiResourceUrl(`/api/incidents/attachments/${fileId}`) : '');
                            const attachmentType = attachment.type || attachment.mimetype || attachment.MIMETYPE || '';
                            const attachmentName = attachment.ORIGINALNAME || 
                                       attachment.originalname || 
                                       attachment.FILENAME || 
                                       attachment.filename || 
                                       attachment.name ||
                                       `Original Attachment ${index + 1}`;
                            const isImage = typeof attachmentType === 'string' && attachmentType.toLowerCase().includes('image');
                            
                            return (
                              <li key={index} className="attachment-item" style={{ marginBottom: '12px' }}>
                                {attachmentUrl ? (
                                  isImage ? (
                                    <a 
                                      href={attachmentUrl}
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                    >
                                      <img
                                        src={attachmentUrl}
                                        alt={attachmentName}
                                        style={{ maxWidth: '200px', display: 'block', marginBottom: '6px' }}
                                      />
                                      <span>🖼️ {attachmentName}</span>
                                    </a>
                                  ) : (
                                    <a 
                                      href={attachmentUrl}
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                    >
                                      📎 {attachmentName}
                                    </a>
                                  )
                                ) : (
                                  <span>{attachmentName}</span>
                                )}
                                {(attachment.fileSize || attachment.SIZE || attachment.file_size) && (
                                  <span className="attachment-size">
                                    ({Math.round((attachment.fileSize || attachment.SIZE || attachment.file_size) / 1024)} KB)
                                  </span>
                                )}
                              </li>
                            );
                          })}
                      </ul>
                      {originalIncidentAttachments.length === 0 && (
                        <div className="no-attachments" style={{ fontStyle: 'italic', color: '#666' }}>
                          No original incident attachments available
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {incident?.witnesses && (
                  <div className="detail-item full-width">
                    <span className="label">Witness Statement(s):</span>
                    <div className="value">
                      {(() => {
                        const raw = incident.witnesses;

                        const tryParseJson = (val) => {
                          if (typeof val !== 'string') return val;
                          const trimmed = val.trim();
                          if (!trimmed) return val;
                          if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return val;
                          try {
                            return JSON.parse(trimmed);
                          } catch (e) {
                            return val;
                          }
                        };

                        const parsed = tryParseJson(raw);

                        // String witness data (could be a single statement or a comma-separated list)
                        if (typeof parsed === 'string') {
                          return <div style={{ whiteSpace: 'pre-wrap' }}>{parsed || 'None'}</div>;
                        }

                        // Array witness data
                        if (Array.isArray(parsed)) {
                          if (parsed.length === 0) return 'None';

                          // Array of strings
                          if (typeof parsed[0] === 'string') {
                            return <div style={{ whiteSpace: 'pre-wrap' }}>{parsed.join('\n')}</div>;
                          }

                          // Array of objects (preferred)
                          if (typeof parsed[0] === 'object' && parsed[0] !== null) {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {parsed.map((w, idx) => {
                                  const name =
                                    w.name ||
                                    w.NAME ||
                                    w.witnessName ||
                                    w.WITNESS_NAME ||
                                    w.fullName ||
                                    w.FULL_NAME ||
                                    `Witness #${idx + 1}`;

                                  const email = w.email || w.EMAIL || w.witnessEmail || w.WITNESS_EMAIL || '';
                                  const phone = w.phone || w.PHONE || w.witnessPhone || w.WITNESS_PHONE || '';
                                  const statement =
                                    w.statement ||
                                    w.STATEMENT ||
                                    w.witnessStatement ||
                                    w.WITNESS_STATEMENT ||
                                    w.comments ||
                                    w.COMMENTS ||
                                    '';

                                  const meta = [email, phone].filter(Boolean).join(' • ');

                                  return (
                                    <div
                                      key={idx}
                                      style={{
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        padding: '10px'
                                      }}
                                    >
                                      <div style={{ fontWeight: 600 }}>{name}</div>
                                      {meta ? (
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{meta}</div>
                                      ) : null}
                                      <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                                        {statement || 'No statement provided.'}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                        }

                        // Object witness data
                        if (parsed && typeof parsed === 'object') {
                          const values = parsed.value || parsed.VALUE;
                          if (Array.isArray(values)) {
                            return <div style={{ whiteSpace: 'pre-wrap' }}>{values.join('\n')}</div>;
                          }

                          // If it's a keyed object of witness entries
                          const objValues = Object.values(parsed).filter(Boolean);
                          if (objValues.length) {
                            return (
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                {objValues
                                  .map(v => (typeof v === 'string' ? v : JSON.stringify(v)))
                                  .join('\n')}
                              </div>
                            );
                          }

                          return <div style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(parsed)}</div>;
                        }

                        return 'None';
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Collapse>
        </div>
        {/* --- END MOVE --- */}

        <div className="investigation-status-section">
          <InvestigationStatusTracker 
            key={`status-${resolveInvestigationStatus(localIncident || {}, incident || {})}`}
            status={resolveInvestigationStatus(localIncident || {}, incident || {})} 
            />
        </div>

        {viewMode === 'locked' && (
          <div className="locked-form-banner">
            <div className="lock-icon">🔒</div>
            <div className="lock-message">
              {isWcbReadOnlyViewer ? (
                <>
                  <h3>This investigation is read-only for WCB reviewers</h3>
                  <p>You can review the original incident investigation, but editing is disabled from the WCB dashboard.</p>
                  <button
                    type="button"
                    onClick={() => navigate('/wcb-dashboard', { replace: true })}
                    className="action-button secondary"
                    style={{ marginTop: 12 }}
                  >
                    Return to WCB Dashboard
                  </button>
                </>
              ) : (
                <>
                  <h3>This investigation is finalized and locked</h3>
                  <p>All required approvals have been received:</p>
                  <ul>
                    <li>
                      Supervisor approval: {
                        incident.SUPERVISOR_SIGN_OFF_DATE && !isNaN(new Date(incident.SUPERVISOR_SIGN_OFF_DATE))
                          ? new Date(incident.SUPERVISOR_SIGN_OFF_DATE).toLocaleString()
                          : 'Not available'
                      }
                    </li>
                    <li>
                      Manager approval: {
                        incident.MANAGER_SIGN_OFF_DATE && !isNaN(new Date(incident.MANAGER_SIGN_OFF_DATE))
                          ? new Date(incident.MANAGER_SIGN_OFF_DATE).toLocaleString()
                          : 'Not available'
                      }
                    </li>
                  </ul>
                </>
              )}
            </div>
          </div>
        )}

        {approvalContext.normalizedStatus === 'NEEDS_REVISION' && revisionFeedback && (
          <div className="locked-form-banner" style={{ marginBottom: '16px' }}>
            <div className="lock-icon">📝</div>
            <div className="lock-message">
              <h3>Manager Notes</h3>
              <p>{revisionFeedback}</p>
            </div>
          </div>
        )}

        {workflowOptions}

        <form 
          onSubmit={handleSubmit}
            onKeyDown={e => {
    // Prevent Enter from submitting the form except on textarea or when explicitly desired
    if (
      e.key === 'Enter' &&
      e.target.tagName !== 'TEXTAREA' &&
      e.target.type !== 'submit'
    ) {
      e.preventDefault();
    }
  }}
          className={`investigation-form ${viewMode === 'locked' ? 'locked' : ''}`}
        >
          <Input 
            name="incidentDate" 
            type="date" 
            label="Incident Date" 
            // Bind to formData so refreshed server data populates the form; fallback to incident.date
            value={formData.incidentDate || incident.date || ''}
            onChange={handleChange} 
            required 
            disabled={viewMode === 'locked'} 
          />
          
          <Input 
            name="location" 
            label="Location" 
            value={formData.location}
            onChange={handleChange} 
            required 
            disabled={viewMode === 'locked' || isFormLocked}
          />
          
          <Textarea 
            name="description" 
            label="Incident Description" 
            value={formData.description}
            onChange={handleChange} 
            required 
            disabled={viewMode === 'locked'} 
          />

          {!canEditLeadershipSection && viewMode !== 'locked' && (
            <div className="locked-form-banner">
              <div className="lock-icon">🔒</div>
              <div className="lock-message">
                <h3>Manager and supervisor fields are read-only</h3>
                <p>Employees can view investigation findings, but only managers, supervisors, delegates, and admin-level reviewers can edit them.</p>
              </div>
            </div>
          )}

          <fieldset disabled={leadershipFieldsDisabled} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>

          <div className="form-group">
            <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>
              Does the incident need to be reported to WCB?
            </label>
            <select
              name="wcbReportRequired"
              value={formData.wcbReportRequired || 'No'}
              onChange={handleChange}
              className="form-select"
              disabled={leadershipFieldsDisabled}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>

            {String(formData.wcbReportRequired || 'No').toLowerCase() === 'yes' && (
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>
                  Select one of the following reasons to report to WCB
                </label>
                <select
                  name="wcbReportReason"
                  value={formData.wcbReportReason || ''}
                  onChange={handleChange}
                  className={`form-select ${errors.wcbReportReason ? 'error' : ''}`}
                  disabled={leadershipFieldsDisabled}
                  required
                >
                  <option value="">Select a reason...</option>
                  <option value="Injury Lost Time">Injury Lost Time</option>
                  <option value="Injury No Lost Time (Desk Report)">Injury No Lost Time (Desk Report)</option>
                  <option value="Admitted to the hospital">Admitted to the hospital</option>
                  <option value="Fatality">Fatality</option>
                  <option value="Vehicle Accident">Vehicle Accident</option>
                  <option value="Other">Other</option>
                </select>
                {errors.wcbReportReason && (
                  <div className="error-message">{errors.wcbReportReason}</div>
                )}

                {String(formData.wcbReportReason || '').toLowerCase() === 'other' && (
                  <div style={{ marginTop: 12 }}>
                    <TextField
                      fullWidth
                      name="wcbReportReasonOther"
                      label="Please specify"
                      value={formData.wcbReportReasonOther || ''}
                      onChange={handleChange}
                      disabled={leadershipFieldsDisabled}
                      required
                      error={Boolean(errors.wcbReportReasonOther)}
                      helperText={errors.wcbReportReasonOther || ''}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>
              Does the incident need to be reported to OHS?
            </label>
            <select
              name="ohsReportRequired"
              value={formData.ohsReportRequired || 'No'}
              onChange={handleChange}
              className="form-select"
              disabled={leadershipFieldsDisabled}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>

            {String(formData.ohsReportRequired || 'No').toLowerCase() === 'yes' && (
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>
                  Select one of the following reasons to report to OHS
                </label>
                <select
                  name="ohsReportReason"
                  value={formData.ohsReportReason || ''}
                  onChange={handleChange}
                  className={`form-select ${errors.ohsReportReason ? 'error' : ''}`}
                  disabled={leadershipFieldsDisabled}
                  required
                >
                  <option value="">Select a reason...</option>
                  <option value="OHS Serious Incident">OHS Serious Incident</option>
                  <option value="OHS Potential Serious Incident">OHS Potential Serious Incident</option>
                </select>
                {errors.ohsReportReason && (
                  <div className="error-message">{errors.ohsReportReason}</div>
                )}

                <div style={{ marginTop: 12 }}>
                  <TextField
                    fullWidth
                    name="ohsReportReference"
                    label="OHS Report Reference (date + file #)"
                    value={formData.ohsReportReference || ''}
                    onChange={handleChange}
                    disabled={leadershipFieldsDisabled}
                    required
                    error={Boolean(errors.ohsReportReference)}
                    helperText={errors.ohsReportReference || ''}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <FormControl fullWidth>
              <InputLabel id="unsafe-acts-label">Unsafe Acts</InputLabel>
              <Select
                labelId="unsafe-acts-label"
                id="unsafe-acts"
                multiple
                value={
                  formData.unsafeActs.map(a =>
                    typeof a === "object" && a.other !== undefined ? "Other" : a
                  )
                }
                onChange={e => {
                  const selected = e.target.value;
                  setFormData(prev => {
                    let unsafeActs = prev.unsafeActs.filter(
                      a => typeof a === "string" || (typeof a === "object" && a.other !== undefined)
                    );
                    if (selected.includes("Other")) {
                      if (!unsafeActs.some(a => typeof a === "object" && a.other !== undefined)) {
                        unsafeActs = [...unsafeActs, { other: "" }];
                      }
                    } else {
                      unsafeActs = unsafeActs.filter(a => !(typeof a === "object" && a.other !== undefined));
                    }
                    unsafeActs = [
                      ...selected.filter(act => act !== "Other"),
                      ...unsafeActs.filter(a => typeof a === "object" && a.other !== undefined)
                    ];
                    return { ...prev, unsafeActs };
                  });
                }}
                renderValue={selected => selected.join(', ')}
                disabled={leadershipFieldsDisabled}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  }
                }}
              >
                {UNSAFE_ACTS_OPTIONS.map(act => (
                  <MenuItem key={act} value={act}>
                    <MUICheckbox
                      checked={
                        act === "Other"
                          ? formData.unsafeActs.some(a => typeof a === "object" && a.other !== undefined)
                          : formData.unsafeActs.includes(act)
                      }
                    />
                    <ListItemText primary={act} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.unsafeActs.some(a => typeof a === "object" && a.other !== undefined) && (
              <TextField
                fullWidth
                label="Please specify"
                value={formData.unsafeActs.find(a => typeof a === "object" && a.other !== undefined)?.other || ""}
                onChange={e => {
                  const value = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    unsafeActs: prev.unsafeActs.map(a =>
                      typeof a === "object" && a.other !== undefined ? { other: value } : a
                    )
                  }));
                }}
                disabled={leadershipFieldsDisabled}
                style={{ marginTop: 8 }}
              />
            )}
          </div>
          
          <div className="form-group">
            <FormControl fullWidth>
              <InputLabel id="unsafe-conditions-label">Unsafe Conditions</InputLabel>
              <Select
                labelId="unsafe-conditions-label"
                id="unsafe-conditions"
                multiple
                value={
                  formData.unsafeConditions.map(a =>
                    typeof a === "object" && a.other !== undefined ? "Other" : a
                  )
                }
                onChange={e => {
                  const selected = e.target.value;
                  setFormData(prev => {
                    let unsafeConditions = prev.unsafeConditions.filter(
                      a => typeof a === "string" || (typeof a === "object" && a.other !== undefined)
                    );
                    if (selected.includes("Other")) {
                      if (!unsafeConditions.some(a => typeof a === "object" && a.other !== undefined)) {
                        unsafeConditions = [...unsafeConditions, { other: "" }];
                      }
                    } else {
                      unsafeConditions = unsafeConditions.filter(a => !(typeof a === "object" && a.other !== undefined));
                    }
                    unsafeConditions = [
                      ...selected.filter(act => act !== "Other"),
                      ...unsafeConditions.filter(a => typeof a === "object" && a.other !== undefined)
                    ];
                    return { ...prev, unsafeConditions };
                  });
                }}
                renderValue={selected => selected.join(', ')}
                disabled={leadershipFieldsDisabled}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  }
                }}
              >
                {UNSAFE_CONDITIONS_OPTIONS.map(act => (
                  <MenuItem key={act} value={act}>
                    <MUICheckbox
                      checked={
                        act === "Other"
                          ? formData.unsafeConditions.some(a => typeof a === "object" && a.other !== undefined)
                          : formData.unsafeConditions.includes(act)
                      }
                    />
                    <ListItemText primary={act} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.unsafeConditions.some(a => typeof a === "object" && a.other !== undefined) && (
              <TextField
                fullWidth
                label="Please specify"
                value={formData.unsafeConditions.find(a => typeof a === "object" && a.other !== undefined)?.other || ""}
                onChange={e => {
                  const value = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    unsafeConditions: prev.unsafeConditions.map(a =>
                      typeof a === "object" && a.other !== undefined ? { other: value } : a
                    )
                  }));
                }}
                disabled={leadershipFieldsDisabled}
                style={{ marginTop: 8 }}
              />
            )}
          </div>

          <div className="form-group">
            <FormControl fullWidth>
              <InputLabel id="personal-factors-label">Personal Factors</InputLabel>
              <Select
                labelId="personal-factors-label"
                id="personal-factors"
                multiple
                value={
                  formData.personalFactors.map(a =>
                    typeof a === "object" && a.other !== undefined ? "Other" : a
                  )
                }
                onChange={e => {
                  const selected = e.target.value;
                  setFormData(prev => {
                    let personalFactors = prev.personalFactors.filter(
                      a => typeof a === "string" || (typeof a === "object" && a.other !== undefined)
                    );
                    if (selected.includes("Other")) {
                      if (!personalFactors.some(a => typeof a === "object" && a.other !== undefined)) {
                        personalFactors = [...personalFactors, { other: "" }];
                      }
                    } else {
                      personalFactors = personalFactors.filter(a => !(typeof a === "object" && a.other !== undefined));
                    }
                    personalFactors = [
                      ...selected.filter(act => act !== "Other"),
                      ...personalFactors.filter(a => typeof a === "object" && a.other !== undefined)
                    ];
                    return { ...prev, personalFactors };
                  });
                }}
                renderValue={selected => selected.join(', ')}
                disabled={leadershipFieldsDisabled}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  }
                }}
              >
                {PERSONAL_FACTORS_OPTIONS.map(factor => (
                  <MenuItem key={factor} value={factor}>
                    <MUICheckbox
                      checked={
                        factor === "Other"
                          ? formData.personalFactors.some(a => typeof a === "object" && a.other !== undefined)
                          : formData.personalFactors.includes(factor)
                      }
                    />
                    <ListItemText primary={factor} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.personalFactors.some(a => typeof a === "object" && a.other !== undefined) && (
              <TextField
                fullWidth
                label="Please specify"
                value={formData.personalFactors.find(a => typeof a === "object" && a.other !== undefined)?.other || ""}
                onChange={e => {
                  const value = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    personalFactors: prev.personalFactors.map(a =>
                      typeof a === "object" && a.other !== undefined ? { other: value } : a
                    )
                  }));
                }}
                disabled={leadershipFieldsDisabled}
                style={{ marginTop: 8 }}
              />
            )}
          </div>

          <div className="form-group">
            <FormControl fullWidth>
              <InputLabel id="job-factors-label">Job Factors</InputLabel>
              <Select
                labelId="job-factors-label"
                id="job-factors"
                multiple
                value={
                  formData.jobFactors.map(a =>
                    typeof a === "object" && a.other !== undefined ? "Other" : a
                  )
                }
                onChange={e => {
                  const selected = e.target.value;
                  setFormData(prev => {
                    let jobFactors = prev.jobFactors.filter(
                      a => typeof a === "string" || (typeof a === "object" && a.other !== undefined)
                    );
                    // Handle "Other"
                    if (selected.includes("Other")) {
                      if (!jobFactors.some(a => typeof a === "object" && a.other !== undefined)) {
                        jobFactors = [...jobFactors, { other: "" }];
                      }
                    } else {
                      jobFactors = jobFactors.filter(a => !(typeof a === "object" && a.other !== undefined));
                    }
                    // Add all other selected factors
                    jobFactors = [
                      ...selected.filter(act => act !== "Other"),
                      ...jobFactors.filter(a => typeof a === "object" && a.other !== undefined)
                    ];
                    return { ...prev, jobFactors };
                  });
                }}
                renderValue={selected => selected.join(', ')}
                disabled={viewMode === 'locked'}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  }
                }}
              >
                {JOB_FACTORS_OPTIONS.map(factor => (
                  <MenuItem key={factor} value={factor}>
                    <MUICheckbox
                      checked={
                        factor === "Other"
                          ? formData.jobFactors.some(a => typeof a === "object" && a.other !== undefined)
                          : formData.jobFactors.includes(factor)
                      }
                    />
                    <ListItemText primary={factor} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Show free-form input if "Other" is selected */}
            {formData.jobFactors.some(a => typeof a === "object" && a.other !== undefined) && (
              <TextField
                fullWidth
                label="Please specify"
                value={formData.jobFactors.find(a => typeof a === "object" && a.other !== undefined)?.other || ""}
                onChange={e => {
                  const value = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    jobFactors: prev.jobFactors.map(a =>
                      typeof a === "object" && a.other !== undefined ? { other: value } : a
                    )
                  }));
                }}
                disabled={viewMode === 'locked'}
                style={{ marginTop: 8 }}
              />
            )}
          </div>
          
          <div className="form-group">
            <FormControl fullWidth>
              <InputLabel id="root-cause-analysis-label">
                Root Cause Analysis <span className="required">*</span>
              </InputLabel>
              <Select
                labelId="root-cause-analysis-label"
                id="root-cause-analysis"
                multiple
                value={
                  (formData.rootCauseAnalysis || []).map(a =>
                    typeof a === "object" && a.other !== undefined ? "Other" : a
                  )
                }
                onChange={e => {
                  const selected = e.target.value;
                  setFormData(prev => {
                    let rootCauseAnalysis = (prev.rootCauseAnalysis || []).filter(
                      a => typeof a === "string" || (typeof a === "object" && a.other !== undefined)
                    );
                    if (selected.includes("Other")) {
                      if (!rootCauseAnalysis.some(a => typeof a === "object" && a.other !== undefined)) {
                        rootCauseAnalysis = [...rootCauseAnalysis, { other: "" }];
                      }
                    } else {
                      rootCauseAnalysis = rootCauseAnalysis.filter(a => !(typeof a === "object" && a.other !== undefined));
                    }
                    rootCauseAnalysis = [
                      ...selected.filter(act => act !== "Other"),
                      ...rootCauseAnalysis.filter(a => typeof a === "object" && a.other !== undefined)
                    ];
                    return { ...prev, rootCauseAnalysis };
                  });
                }}
                renderValue={selected => selected.join(', ')}
                disabled={viewMode === 'locked'}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  }
                }}
              >
                {ROOT_CAUSE_ANALYSIS_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>
                    <MUICheckbox
                      checked={
                        option === "Other"
                          ? (formData.rootCauseAnalysis || []).some(a => typeof a === "object" && a.other !== undefined)
                          : (formData.rootCauseAnalysis || []).includes(option)
                      }
                    />
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(formData.rootCauseAnalysis || []).some(a => typeof a === "object" && a.other !== undefined) && (
              <TextField
                fullWidth
                label="Please specify"
                value={
                  (formData.rootCauseAnalysis || []).find(a => typeof a === "object" && a.other !== undefined)?.other || ""
                }
                onChange={e => {
                  const value = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    rootCauseAnalysis: (prev.rootCauseAnalysis || []).map(a =>
                      typeof a === "object" && a.other !== undefined ? { other: value } : a
                    )
                  }));
                }}
                disabled={viewMode === 'locked'}
                style={{ marginTop: 8 }}
              />
            )}
          </div>

          <Textarea 
            name="immediateActions" 
            label="Immediate Actions Taken" 
            value={formData.immediateActions}
            onChange={handleChange} 
            required 
            disabled={leadershipFieldsDisabled} 
          />
          </fieldset>
          
          <div className="form-group">
            <div className="section-header">
              <label>Corrective Actions</label>
              <button 
                type="button"
                className="btn btn-add"
                onClick={addCorrectiveAction}
                disabled={leadershipFieldsDisabled}
              >
                + Add Action
              </button>
            </div>
            
            {formData.correctiveActions.length > 0 ? (
              <div className="corrective-actions-list">
                {formData.correctiveActions.map((action, index) => (
                  <div key={action.id || index} className="corrective-action-item">
                    <div className="action-header">
                      <h4>Corrective Action #{index + 1}</h4>
                      <button 
                        type="button"
                        className="btn btn-remove"
                        onClick={() => removeCorrectiveAction(index)}
                        disabled={leadershipFieldsDisabled}
                      >
                        Remove
                      </button>
                    </div>
                    
                    <div className="action-field">
                      <label className={errors[`correctiveAction-${index}-description`] ? 'error-label' : ''}>
                        Description <span className="required">*</span>
                      </label>
                      <textarea 
                        className={errors[`correctiveAction-${index}-description`] ? 'form-control error' : 'form-control'}
                        rows="2"
                        value={action.description || ''}
                        onChange={(e) => updateCorrectiveAction(index, 'description', e.target.value)}
                        placeholder="Describe the corrective action"
                        disabled={leadershipFieldsDisabled} 
                      />
                      {errors[`correctiveAction-${index}-description`] && (
                        <p className="error-text">{errors[`correctiveAction-${index}-description`]}</p>
                      )}
                    </div>
                    
                    <div className="action-row">
                      <div className="action-field">
                        <label className={errors[`correctiveAction-${index}-assignedTo`] ? 'error-label' : ''}>
                          Assigned To <span className="required">*</span>
                        </label>
                        <select
                          className={errors[`correctiveAction-${index}-assignedTo`] ? 'form-control error' : 'form-control'}
                          value={action.assignedTo || ''}
                          onChange={e => updateCorrectiveAction(index, 'assignedTo', e.target.value)}
                          disabled={leadershipFieldsDisabled}
                          required
                        >
                          <option value="">Select Employee</option>
                          {employees.map(emp => (
                            <option key={emp.email || emp.username} value={emp.email || emp.username}>
                              {emp.lastName}, {emp.firstName} ({emp.email || emp.username})
                            </option>
                          ))}
                        </select>
                        {errors[`correctiveAction-${index}-assignedTo`] && (
                          <p className="error-text">{errors[`correctiveAction-${index}-assignedTo`]}</p>
                        )}
                      </div>
                      <div className="action-field">
                        <label className={errors[`correctiveAction-${index}-targetDate`] ? 'error-label' : ''}>
                          Target Date <span className="required">*</span> <span className="hint">(Must be a future date)</span>
                        </label>
                        <input 
                          type="date"
                          className={errors[`correctiveAction-${index}-targetDate`] ? 'form-control error' : 'form-control'}
                          value={action.targetDate || ''}
                          onChange={(e) => updateCorrectiveAction(index, 'targetDate', e.target.value)}
                          disabled={leadershipFieldsDisabled} 
                          // Set minimum date to tomorrow
                          min={new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]}
                        />
                        {errors[`correctiveAction-${index}-targetDate`] && (
                          <p className="error-text">{errors[`correctiveAction-${index}-targetDate`]}</p>
                        )}
                      </div>
                    </div>

                    <div className="action-field">
                      <label>Status</label>
                      <div className="action-status">
                        <span className={`status-badge ${action.status.toLowerCase()}`}>
                          {action.status === 'ASSIGNED' ? 'Assigned' : 
                           action.status === 'IN_PROGRESS' ? 'In Progress' : 'Completed'}
                        </span>
                        
                        {action.status !== 'COMPLETED' && (
                          <div className="status-buttons">
                            {action.status === 'ASSIGNED' && (
                              <button 
                                type="button"
                                className="btn btn-sm btn-progress"
                                onClick={() => markActionInProgress(index)}
                                disabled={viewMode === 'locked' || !(
                                  (action.assignedTo && ((localStorage.getItem('userEmail') || '').toLowerCase() === action.assignedTo.toLowerCase())) ||
                                  (['ADMIN','SUPERVISOR','FIELD_SUPERVISOR','MANAGER'].includes(((localStorage.getItem('userRole')||'') || '').toUpperCase()))
                                )}
                              >
                                Mark In Progress
                              </button>
                            )}
                            <button 
                              type="button"
                              className="btn btn-sm btn-complete"
                              onClick={() => markAsCompleted(index)}
                              disabled={viewMode === 'locked' || !(
                                (action.assignedTo && ((localStorage.getItem('userEmail') || '').toLowerCase() === action.assignedTo.toLowerCase())) ||
                                (['ADMIN','SUPERVISOR','FIELD_SUPERVISOR','MANAGER'].includes(((localStorage.getItem('userRole')||'') || '').toUpperCase()))
                              )}
                            >
                              Mark as Completed
                            </button>
                          </div>
                        )}
                        
                        {action.completedDate && (
                          <div className="completion-info">
                            <p>Completed on: {new Date(action.completedDate).toLocaleDateString()}</p>
                            {action.completedBy && <p>By: {action.completedBy}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="action-field action-buttons">

                      <button 
                        type="button"
                        className="btn btn-sm btn-notify"
                        onClick={() => saveAndNotifyCorrectiveAction(index)}
                        disabled={leadershipFieldsDisabled || !action.assignedTo}
                      >
                        Save & Notify Assignee
                      </button>

                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-actions">No corrective actions added yet.</p>
            )}
            
          </div>
          
          <div className="form-group attachments-section">
            <label>Evidence & Documentation</label>
            {console.log('🔍 DEBUG: Rendering AttachmentManager with incident.id:', incident.id, 'Full incident:', incident)}
            {console.log('🔍 DEBUG: Visible attachments for Evidence & Documentation:', visibleAttachments)}
            {console.log('🔍 DEBUG: URL pathname:', window.location.pathname)}
            {(() => {
              // Determine the actual canonical incident id to use for operations.
              const urlPath = window.location.pathname;
              const urlIncidentId = urlPath.includes('/fire-incident-investigations') ?
                new URLSearchParams(window.location.search).get('incidentId') ||
                (document.querySelector('[data-incident-id]')?.getAttribute('data-incident-id')) ||
                null : null;
              // Prefer explicit correctIncidentId prop, then canonical mapping on the incident, then urlIncidentId, then incident.id
              const actualIncidentId = canonicalIncidentId || urlIncidentId || incident.id;
              // Compute resolved investigation status (treat null as missing and prefer previous/cache)
              const resolvedStatus = (typeof resolveInvestigationStatus === 'function') ? resolveInvestigationStatus(incident, window.__selectedIncident || {}) : (incident.investigationStatus || incident.INVESTIGATION_STATUS || 'PENDING');
              console.log('🔍 DEBUG: Determined actualIncidentId for AttachmentManager:', actualIncidentId, 'resolvedInvestigationStatus:', resolvedStatus);
              return null;
            })()}
            <AttachmentManager 
              attachments={visibleAttachments}
              onUpload={handleAttachmentUpload}
              onRemove={handleAttachmentRemove}
              disabled={viewMode === 'locked'} 
              setFormData={setFormData}
              incidentId={canonicalIncidentId}
              selectedIncident={incident}
            />
          </div>

          {incident.DELEGATE_EMAIL && 
           incident.DELEGATE_EMAIL.toLowerCase() === localStorage.getItem('userEmail')?.toLowerCase() && (
            <div className="delegate-section">
              <h3>Delegate Assignment</h3>
              
              {incident.DELEGATE_INSTRUCTIONS && (
                <div className="delegate-instructions">
                  <h4>Instructions</h4>
                  <p>{incident.DELEGATE_INSTRUCTIONS}</p>
                </div>
              )}
              
              <Textarea 
                name="delegateFindings" 
                label="Delegate Findings" 
                onChange={handleChange}
                value={formData.delegateFindings || incident.DELEGATE_FINDINGS || ''}
                disabled={incident.DELEGATE_COMPLETED || viewMode === 'review'} 
                required={!incident.DELEGATE_COMPLETED}
              />
              
              <Textarea 
                name="delegateRecommendations" 
                label="Delegate Recommendations" 
                onChange={handleChange}
                value={formData.delegateRecommendations || incident.DELEGATE_RECOMMENDATIONS || ''}
                disabled={incident.DELEGATE_COMPLETED || viewMode === 'review'}
                required={!incident.DELEGATE_COMPLETED} 
              />
              
              {!incident.DELEGATE_COMPLETED && (
                <Button 
                  onClick={handleDelegateSubmit} 
                  className="btn-primary delegate-submit-btn"
                >
                  Submit Delegate Findings
                </Button>
              )}
            </div>
          )}

          {(() => {
            console.log('Approval section check:', approvalContext);

            return approvalContext.canShowApprovalSection && (
              <div className="approval-section">
                <h3>Investigation Approval ({approvalContext.approvalActorRole})</h3>
                <div className="approval-status">
                  <p>Current Status: <strong>{resolveInvestigationStatus(incident || {}, {})}</strong></p>
                  {incident.SUPERVISOR_APPROVAL && (
                    <p>Supervisor: <strong>{incident.SUPERVISOR_APPROVAL}</strong></p>
                  )}
                  {incident.MANAGER_APPROVAL && (
                    <p>Manager: <strong>{incident.MANAGER_APPROVAL}</strong></p>
                  )}
                </div>
                
                <textarea
                  placeholder={`Enter your feedback as ${(userRole || '').toLowerCase()}${approvalContext.approvalActorRole === 'MANAGER' ? ' (required for revision requests)' : ''}`}
                  value={approvalForm.feedback}
                  onChange={(e) => setApprovalForm({...approvalForm, feedback: e.target.value})}
                  className="form-control"
                  rows={3}
                  disabled={viewMode === 'locked'} 
                />

                <p className="form-text info-text" style={{ marginTop: '8px' }}>
                  Reviewers use these approval actions directly. The main Submit button is disabled for reviewer roles.
                </p>
                
                <div className="approval-actions">
                  <button 
                    type="button"
                    className="btn btn-success"
                    onClick={() => handleApproval('APPROVE')}
                    disabled={viewMode === 'locked'} 
                  >
                    {approvalContext.approvalActorRole === 'MANAGER' ? 'Final Approval' : 'Approve Investigation'}
                  </button>
                  {approvalContext.approvalActorRole === 'MANAGER' && (
                    <button 
                      type="button"
                      className="btn btn-warning"
                      onClick={() => handleApproval('REJECT')}
                      disabled={!approvalForm.feedback.trim() || viewMode === 'locked'} 
                    >
                      Return for Revision
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
          
          <div className="form-actions">
            <Button 
              onClick={onClose}
              className="btn-secondary"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                try { onClose && onClose(); } catch (e) { /* ignore */ }
                navigate('/fire-training-flha');
              }}
              className="btn-tertiary"
            >
              Back
            </Button>
            <Button 
              onClick={handleSaveDraft}
              className="btn-tertiary" 
              disabled={!canSaveDraft} 
            >
              Save as Draft
            </Button>
          </div>
        </form>
        {console.log('Rendering status tracker with:', {
          localIncidentStatus: localIncident?.investigationStatus,
          originalIncidentStatus: incident?.investigationStatus,
          statusToUse: resolveInvestigationStatus(localIncident || {}, incident || {})
        })}
      </DialogContent>

    </Dialog>
  );
};

const StandaloneIncidentForm = () => {
  const [formData, setFormData] = useState({
    incidentDate: "",
    location: "",
    description: "",
    unsafeActs: [],
    unsafeConditions: [],
    personalFactors: [],
    immediateActions: "",
    rootCause: "",
    correctiveActions: "",
    attachments: [],
  });

  const [viewMode, setViewMode] = useState('edit'); // 'normal', 'locked', or 'review'
  const canEditLeadershipSection = canEditLeadershipInvestigationFields({
    role: localStorage.getItem('userRole') || 'USER',
    email: localStorage.getItem('userEmail') || ''
  });
  const leadershipFieldsDisabled = viewMode === 'locked' || !canEditLeadershipSection;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (option) => {
    setFormData((prev) => {
      const newUnsafeActs = prev.unsafeActs.includes(option)
        ? prev.unsafeActs.filter((act) => act !== option)
        : [...prev.unsafeActs, option];
      return { ...prev, unsafeActs: newUnsafeActs };
    });
  };

  const handleFileUpload = (e) => {
    setFormData((prev) => ({ 
      ...prev, 
      attachments: e.target.files ? Array.from(e.target.files) : [] 
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('handleSubmit called', e);
    console.log("Form Submitted", formData);
    alert("Form submitted successfully!");
  };

  return (
    <div className="standalone-form">
      <h2>Incident Investigation Form</h2>
      <form onSubmit={handleSubmit} className="investigation-form">
        <Input 
          name="incidentDate" 
          type="date" 
          label="Incident Date" 
          value={formData.incidentDate}
          onChange={handleChange} 
          required 
          disabled={viewMode === 'locked'} 
        />
        
        <Input 
          name="location" 
          label="Location" 
          onChange={handleChange} 
          required 
        />
        
        <Textarea 
          name="description" 
          label="Incident Description" 
          onChange={handleChange} 
          required 
        />
        
        <div className="form-group">
          <label>Unsafe Acts</label>
          <div className="checkbox-group">
            {['Failure to use PPE', 'Improper lifting', 'Speeding', 'Other'].map((act) => (
              <Checkbox 
                key={act} 
                label={act} 
                checked={formData.unsafeActs.includes(act)} 
                onChange={() => handleCheckboxChange(act)} 
                required
              />
            ))}
          </div>
        </div>
        
        <Textarea 
          name="immediateActions" 
          label="Immediate Actions Taken" 
          value={formData.immediateActions}
          onChange={handleChange} 
          required 
          disabled={leadershipFieldsDisabled} 
        />
        
        <Textarea 
          name="rootCause" 
          label="Root Cause Analysis" 
          onChange={handleChange} 
          required 
          disabled={leadershipFieldsDisabled}
        />
        
        <Textarea 
          name="correctiveActions" 
          label="Corrective Actions" 
          onChange={handleChange} 
          required 
          disabled={leadershipFieldsDisabled}
        />
        
        <div className="form-group">
          <label>Upload Attachments</label>
          <AttachmentManager 
            attachments={formData.attachments}
            onUpload={(file) => setFormData((prev) => ({
              ...prev,
              attachments: [...prev.attachments, file]
            }))}
            onRemove={(fileId) => setFormData((prev) => ({
              ...prev,
              attachments: prev.attachments.filter(file => file.id !== fileId)
            }))}
            incidentId={null} // No incident ID for new incidents
          />
        </div>
        
        <Button type="submit" className="btn-primary">Submit</Button>
      </form>
    </div>
  );
};

const FireIncidentInvestigationsDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const highlightedIncidentId = queryParams.get('incidentId');
  const routedIncident = location.state?.incident || null;
  const routedIncidentId = getCanonicalIncidentId(routedIncident);
  
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail') || '');
  const [userDepartment, setUserDepartment] = useState(localStorage.getItem('userDepartment') || '');
  const [incidents, setIncidents] = useState([]);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [urlIncidentId, setUrlIncidentId] = useState(null); // Track the correct URL-based incident ID
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [incidentsWithDrafts, setIncidentsWithDrafts] = useState({});
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState({
    isDelegate: false,
    canEdit: false,
    canView: false,
    canAssignDelegate: false,
    canApproveInvestigation: false,
    canRejectInvestigation: false,
    canManageCorrectiveActions: false
  });
  const [accessDenied, setAccessDenied] = useState({
    show: false,
    message: ''
  });
  const [hasDelegateAssignments, setHasDelegateAssignments] = useState(false);
  const [viewMode, setViewMode] = useState('normal');
  const [showAllDepartments, setShowAllDepartments] = useState(false);
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState('');
  const [availableDepartments, setAvailableDepartments] = useState([]);

  const fireAndNonFireOverrideEmails = useMemo(() => new Set([
    'doreen@davg.ca',
    'safety@diamondvalley.town',
    'contact@3p3.ca',
    'trevord@diamondvalley.town',
    'firechief@diamondvalley.town',
    'logans@diamondvalley.town'
  ]), []);

  const normalizedEmailForOverride = useMemo(() => {
    const emailLike = userEmail || localStorage.getItem('userEmail') || '';
    return String(emailLike).trim().toLowerCase();
  }, [userEmail]);

  const isFireAndNonFireOverride = useMemo(() => {
    return !!normalizedEmailForOverride && fireAndNonFireOverrideEmails.has(normalizedEmailForOverride);
  }, [fireAndNonFireOverrideEmails, normalizedEmailForOverride]);

  const userEmailLower = (localStorage.getItem('userEmail') || '').toLowerCase();
  const isCAO = safeToLowerCase(userEmailLower) === 'cao@diamondvalley.town';
  const hasWcbDashboardAccess = hasWcbDashboardAccessEmail(userEmailLower);
  const isCraigB = safeToLowerCase(userEmailLower) === 'craigb@diamondvalley.town';

  // Default override users to "show all departments" so they immediately see everything.
  useEffect(() => {
    if (isFireAndNonFireOverride) {
      setShowAllDepartments(true);
    }
  }, [isFireAndNonFireOverride]);

    // Ensure we have the current user's email and role set on mount.
    useEffect(() => {
      let mounted = true;
      const loadCurrentUser = async () => {
        try {
          // Prefer the /api/users/me endpoint if available on this server
          let res;
          try {
            res = await API.get('/api/users/me');
          } catch (err) {
            // Fallback to the auth endpoint used elsewhere
            res = await API.get('/api/auth/me');
          }

          const user = (res && res.data && (res.data.user || res.data)) || {};
          const email = (user.email || user.username || localStorage.getItem('userEmail') || '').trim();
          const roleRaw = (user.role || localStorage.getItem('userRole') || '').toString();
          const role = roleRaw ? roleRaw.toUpperCase() : '';

          if (!mounted) return;

          if (email) {
            setUserEmail(email);
            localStorage.setItem('userEmail', email);
          }
          if (role) {
            setUserRole(role);
            localStorage.setItem('userRole', role);
          }

          if (user.department) {
            setUserDepartment(user.department);
            localStorage.setItem('userDepartment', user.department);
          }
        } catch (err) {
          console.warn('Could not load current user from API:', err && err.message ? err.message : err);
        }
      };

      loadCurrentUser();
      return () => { mounted = false; };
    }, []);

  const handleCloseInvestigationForm = () => {
    const url = new URL(window.location);
    url.searchParams.delete('incidentId');
    window.history.replaceState({}, '', url);
    setSelectedIncident(null);
  };

  console.log('Dashboard rendered, URL:', location.pathname + location.search);
  console.log('Query parameter:', highlightedIncidentId);
  
  useEffect(() => {
    const isValidQueryId =
      highlightedIncidentId != null &&
      /^\d+$/.test(String(highlightedIncidentId)) &&
      Number(highlightedIncidentId) > 0;

    if (routedIncident && routedIncidentId) {
      console.log('🔍 Dashboard received routed incident context with canonical ID:', routedIncidentId, routedIncident);
      setSelectedIncident(routedIncident);
      setUrlIncidentId(String(routedIncidentId));
      return;
    }

    if (isValidQueryId) {
      // Always fetch fresh individual incident data to get attachments and latest details
      console.log(`🔄 URL has incidentId=${highlightedIncidentId}, fetching fresh data...`);
      // Try to resolve to canonical parent id from our incidents list first
      const matched = incidents.find(inc => String(inc.id) === String(highlightedIncidentId) || String(inc.incident_id) === String(highlightedIncidentId) || String(inc.INCIDENT_ID) === String(highlightedIncidentId));
      const canonicalFromList = matched ? getCanonicalIncidentId(matched) : null;
      const idToFetch = canonicalFromList || highlightedIncidentId;
      setUrlIncidentId(String(idToFetch)); // Store the correct URL-based ID
      if (!loading) {
        fetchIncidentById(idToFetch);
      }
    } else {
      // If the URL incidentId is invalid (e.g. 0), clear it to avoid bad fetches and state resets.
      if (highlightedIncidentId) {
        try {
          console.warn('Ignoring invalid URL incidentId:', highlightedIncidentId);
          const url = new URL(window.location.href);
          url.searchParams.delete('incidentId');
          window.history.replaceState({}, '', url);
        } catch (e) {
          // ignore
        }
      }
      // Always close modal if incidentId is not in URL
      setSelectedIncident(null);
      setUrlIncidentId(null);
    }
  }, [highlightedIncidentId, incidents, loading, routedIncident, routedIncidentId]);

  useEffect(() => {
    const isValidQueryId =
      highlightedIncidentId != null &&
      /^\d+$/.test(String(highlightedIncidentId)) &&
      Number(highlightedIncidentId) > 0;

    if (isValidQueryId) {
      const allowedPages = JSON.parse(sessionStorage.getItem('allowedPages') || '[]');
      if (!allowedPages.includes('/fire-incident-investigations')) {
        allowedPages.push('/fire-incident-investigations');
        sessionStorage.setItem('allowedPages', JSON.stringify(allowedPages));
        console.log('Added temporary delegate access to investigations page');
      }
    }
  }, [highlightedIncidentId]);

  const checkDelegateStatus = async (incidentId) => {
    if (!incidentId) return false;
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('No auth token found, asking user to login');
        const currentUrl = encodeURIComponent(
          `/fire-incident-investigations?incidentId=${incidentId}`
        );
        window.location.href = getAppPath(`/login?redirect=${currentUrl}`);
        return false;
      }
      
      // Get the incident data to check delegate status
      const incident = incidents.find(inc => String(inc.id) === String(incidentId));
      if (incident) {
        const delegateEmail = incident.DELEGATE_EMAIL || incident.delegateEmail;
        const isDelegate = delegateEmail && 
                          userEmail && 
                          delegateEmail.toLowerCase() === userEmail.toLowerCase();
        
        console.log('Checking delegate status:', { incidentId, delegateEmail, userEmail, isDelegate });
        
        setUserPermissions(prev => ({
          ...prev,
          isDelegate: isDelegate
        }));
        
        return isDelegate;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking delegate status:', error);
      
      if (error.response?.status === 401) {
        const currentUrl = encodeURIComponent(
          `/fire-incident-investigations?incidentId=${incidentId}`
        );
        window.location.href = getAppPath(`/login?redirect=${currentUrl}`);
      }
      
      return false;
    }
  };


  
  const checkRolePermissions = async (incidentId) => {
    try {
      const response = await API.get(`/api/auth/me`);
  const userRoleRaw = response.data.role || response.data?.user?.role || 'USER';
  const userRole = String(userRoleRaw).toUpperCase();
  setUserRole(userRole);
      
      // Check delegate status from incident data instead of separate API call
      const incident = incidents.find(inc => String(inc.id) === String(incidentId));
      const delegateEmail = incident?.DELEGATE_EMAIL || incident?.delegateEmail;
      const isDelegate = delegateEmail && 
                        userEmail && 
                        delegateEmail.toLowerCase() === userEmail.toLowerCase();
      
      setUserPermissions({
        isDelegate: isDelegate,
        canEdit: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(userRole) ||
                 isDelegate,
        canView: true,
        canAssignDelegate: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(userRole),
        canApproveInvestigation: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER'].includes(userRole),
        canRejectInvestigation: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER'].includes(userRole),
        canManageCorrectiveActions: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(userRole) || 
                                    isDelegate
      });
    } catch (error) {
      console.error('Error checking role permissions:', error);
      const fallbackRoleRaw = localStorage.getItem('userRole') || 'USER';
      const fallbackRole = String(fallbackRoleRaw).toUpperCase();
      setUserRole(fallbackRole);
      setUserPermissions({
        isDelegate: false,
        canEdit: false,
        canView: true,
        canAssignDelegate: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(fallbackRole),
        canApproveInvestigation: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER'].includes(fallbackRole),
        canRejectInvestigation: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR', 'MANAGER'].includes(fallbackRole),
        canManageCorrectiveActions: ['ADMIN', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(fallbackRole)
      });
    }
  };

  const fetchIncidentById = async (id) => {
    try {
      console.log(`🔍 fetchIncidentById called with ID: ${id}`);
      console.log(`🔍 DEBUG: URL parameter ID being used: ${id}`);

      const idStr = String(id || '');

      setLoading(true);
      setError(null);
      setAccessDenied({ show: false, message: '' });
      
  console.log(`📡 Making API request to /incidents/${id}`);
  validateIncidentIdForFetch(id, 'fetchIncidentById', incidents, selectedIncident);
  let response;
  try {
    response = await API.get(`/api/incidents/${id}`);
  } catch (err) {
    // If we get a 404, try to resolve the canonical parent by scanning the incidents list
    console.warn(`Initial fetch for /api/incidents/${id} failed:`, err && err.response ? err.response.status : err);
    if (err && err.response && err.response.status === 404) {
      console.log('🔁 Attempting canonical parent resolution for id', id);
      try {
        const listRes = await API.get('/api/incidents');
        const list = Array.isArray(listRes.data) ? listRes.data : (listRes.data.incidents || []);
        // Try to find an incident where a detail/investigation row references this detail id
        let matchedParent = null;
        for (const inc of list) {
          // Check common nested shapes: investigations array, details array, or fields like INCIDENT_ID
          const investigations = inc.investigations || inc.INVESTIGATIONS || inc.details || inc.INVESTIGATION_DETAILS || [];
          if (Array.isArray(investigations)) {
            for (const det of investigations) {
              if (!det) continue;
              const detailIds = [det.ID, det.id, det.DETAIL_ID, det.detail_id, det.INCIDENT_ID, det.incident_id].map(String).filter(Boolean);
              if (detailIds.includes(String(id))) {
                matchedParent = inc;
                break;
              }
            }
          }
          // Also check if the incident itself has an INCIDENT_ID field matching the id
          const possibleParentId = inc.INCIDENT_ID || inc.incident_id || inc.incidentId || inc.ID || inc.id;
          if (!matchedParent && possibleParentId && String(possibleParentId) === String(id)) {
            // unlikely but handle it
            matchedParent = inc;
          }
          if (matchedParent) break;
        }

        if (matchedParent) {
          const canonicalId = matchedParent.ID || matchedParent.id || matchedParent.INCIDENT_ID || matchedParent.incident_id;
          console.log('🔍 Resolved canonical parent id:', canonicalId, 'for detail id', id);
          // update the stored urlIncidentId so the UI shows the canonical parent
          setUrlIncidentId(String(canonicalId));
          // retry the GET with canonical id
          response = await API.get(`/api/incidents/${canonicalId}`);
        } else {
          console.warn('Could not resolve canonical parent from incidents list for id', id);
          // As a last-ditch fallback, attempt to use any client-side snapshot left by the POST flow or debug tooling
          try {
            const snapshot = window.__serverProcessedIncidentSnapshot || window.__lastPostResponseData || null;
            if (snapshot) {
              // If snapshot looks like an axios response wrapper, unwrap
              const candidate = Array.isArray(snapshot) ? snapshot[0] : (snapshot && snapshot.data ? snapshot.data : snapshot);
              // Try to ensure this snapshot is relevant to the requested id by checking common id fields
              const snapIds = [candidate?.ID, candidate?.id, candidate?.INCIDENT_ID, candidate?.incident_id, candidate?.incidentId, candidate?.investigationId, candidate?.INVESTIGATION_ID].filter(Boolean).map(String);
              const matches = snapIds.length === 0 || snapIds.includes(String(id)) || snapIds.includes(String(id));
              if (candidate && matches) {
                console.log('Using client-side snapshot fallback for id', id, candidate);
                response = { data: candidate };
              } else {
                // If snapshot doesn't include explicit ids, but contains investigation-level fields, still consider it useful
                const hasInvestigationFields = candidate && (candidate.CONTRIBUTING_FACTORS || candidate.immediateActions || candidate.investigationStatus || candidate.INVESTIGATION_STATUS);
                if (candidate && hasInvestigationFields) {
                  console.log('Using client-side snapshot fallback (no explicit id match) for id', id, candidate);
                  response = { data: candidate };
                }
              }
            }
          } catch (snapErr) {
            console.warn('Snapshot fallback check failed:', snapErr);
          }

          if (!response) {
            throw err; // rethrow original
          }
        }
      } catch (listErr) {
        console.error('Error while attempting canonical resolution:', listErr);
        throw err; // surface original 404
      }
    } else {
      throw err;
    }
  }

      console.log(`✅ API response received:`, response.data);
      console.log(`🔍 DEBUG: Raw response.data type:`, typeof response.data);
      console.log(`🔍 DEBUG: Raw response.data content:`, response.data);
      
      const incidentData = Array.isArray(response.data) ? response.data[0] : response.data;
      let mergedIncidentData = incidentData;
      // Enrich with investigation-row fields (CONTRIBUTING_FACTORS JSON, status, etc.)
      // so the form can re-hydrate WCB/OHS answers reliably.
      try {
        const refreshed = await fetchRefreshedIncidentById(id).catch(() => null);
        if (refreshed && typeof refreshed === 'object') {
          mergedIncidentData = { ...incidentData, ...refreshed };
          console.log('🔁 Enriched incident payload with fetchRefreshedIncidentById:', { id });
        }
      } catch (e) {
        console.warn('Failed to enrich incident payload with fetchRefreshedIncidentById:', e && e.message);
      }

      console.log(`📋 Incident data processed:`, mergedIncidentData);
      console.log(`📎 Attachments in response:`, mergedIncidentData.ATTACHMENTS || mergedIncidentData.attachments);


      const delegateEmail = mergedIncidentData.DELEGATE_EMAIL || mergedIncidentData.delegateEmail || '';
      const isUserDelegate = delegateEmail && 
                            userEmail && 
                            delegateEmail.toLowerCase() === userEmail.toLowerCase();
      
      const mappedIncident = {
        ...mergedIncidentData,
        // Prefer INCIDENTS.ID when available; fall back to other id fields defensively.
        id: mergedIncidentData.ID || mergedIncidentData.id || mergedIncidentData.INCIDENT_ID || mergedIncidentData.incident_id,
        title: mergedIncidentData.TITLE || mergedIncidentData.title || 'Untitled Incident',
        location: mergedIncidentData.LOCATION || mergedIncidentData.location || 'Unknown Location',
        severity: mergedIncidentData.SEVERITY || mergedIncidentData.severity || 'Medium',
        description: mergedIncidentData.DESCRIPTION || mergedIncidentData.description || '',
        status: mergedIncidentData.STATUS || mergedIncidentData.status || 'Open',
        date: mergedIncidentData.DATE || mergedIncidentData.date || new Date().toISOString().split('T')[0],
        attachments: mergeIncidentAttachmentLists(mergedIncidentData.ATTACHMENTS, mergedIncidentData.attachments),
        isDelegateFor: isUserDelegate,
        delegateEmail: delegateEmail,
        primaryDepartment: mergedIncidentData.PRIMARY_DEPARTMENT || mergedIncidentData.primaryDepartment || '',
        department: mergedIncidentData.DEPARTMENT || mergedIncidentData.department || '',
      INVESTIGATION_STATUS: resolveInvestigationStatus(mergedIncidentData, incidentData || {}),
      investigationStatus: resolveInvestigationStatus(mergedIncidentData, incidentData || {}),
        SUPERVISOR_APPROVAL: mergedIncidentData.SUPERVISOR_APPROVAL || 'PENDING',
        MANAGER_APPROVAL: mergedIncidentData.MANAGER_APPROVAL || 'PENDING',
        SUPERVISOR_SIGN_OFF: mergedIncidentData.SUPERVISOR_SIGN_OFF || 0,
        MANAGER_SIGN_OFF: mergedIncidentData.MANAGER_SIGN_OFF || 0,
        SUPERVISOR_SIGN_OFF_DATE: mergedIncidentData.SUPERVISOR_SIGN_OFF_DATE || null,
        MANAGER_SIGN_OFF_DATE: mergedIncidentData.MANAGER_SIGN_OFF_DATE || null,
        ISSUE_DESCRIPTION: mergedIncidentData.ISSUE_DESCRIPTION || mergedIncidentData.issueDescription || '',
        issueDescription: mergedIncidentData.ISSUE_DESCRIPTION || mergedIncidentData.issueDescription || ''
      };
      
      console.log('🔍 DEBUG: incidentData.ID value:', incidentData.ID);
      console.log('🔍 DEBUG: Mapped incident ID:', mappedIncident.id);
      console.log('🔍 DEBUG: Full mapped incident object:', mappedIncident);
      // If we requested a canonical id but the backend returned a different detail ID,
      // prefer the requested id as the canonical identifier for UI state so subsequent
      // fetches use the canonical parent id instead of a detail PK that may 404.
      try {
        const requestedIdStr = String(id);
        if (requestedIdStr) {
          // If server returned no id, use the requested id
          if (!mappedIncident.id || String(mappedIncident.id).trim() === '') {
            console.log('Setting mappedIncident.id to requested fetch id because mappedIncident.id was missing:', requestedIdStr, 'was:', mappedIncident.id);
            mappedIncident.id = requestedIdStr;
            mappedIncident.ID = mappedIncident.ID || requestedIdStr;
          } else if (String(mappedIncident.id) !== requestedIdStr) {
            // Server responded with a different internal ID than the id we requested.
            // This causes downstream calls (e.g. /api/incidents/<id>/investigation) to hit the wrong id
            // and return 404. Prefer the requested fetch id for client-side canonical identity,
            // but preserve the server-returned id in serverReturnedId for auditing/debugging.
            try {
              console.log('Server returned a different internal id than requested. Requested:', requestedIdStr, 'Server returned id:', mappedIncident.id);
              mappedIncident.serverReturnedId = mappedIncident.id;
              // Keep server values under a separate field, then treat the requested id as the canonical one
              mappedIncident.id = requestedIdStr;
              mappedIncident.ID = mappedIncident.ID || mappedIncident.serverReturnedId || requestedIdStr;
              // Also preserve the original returned value under INCIDENT_ID if helpful
              mappedIncident.INCIDENT_ID = mappedIncident.INCIDENT_ID || mappedIncident.serverReturnedId;
            } catch (e) {
              console.warn('Failed to reconcile mismatched server id vs requested id:', e);
            }
          } else {
            console.log('Keeping mappedIncident.id as canonical parent id:', mappedIncident.id);
          }
        }
      } catch (e) {
        // ignore
      }

      // Prefer server-processed investigation fields when present. Only merge client cache
      // when the server did not provide meaningful investigation-level values.
      try {
        const canonicalMappedId = mappedIncident.id || mappedIncident.ID || mappedIncident.incident_id || mappedIncident.INCIDENT_ID;
        const serverHasInvestigation = (val => {
          if (val === undefined || val === null) return false;
          try {
            const s = String(val).trim();
            if (!s) return false;
            const low = s.toLowerCase();
            if (low === 'none' || low === 'null' || s === '[]') return false;
            return true;
          } catch (e) { return false; }
        });

        const serverProvidesInvestigationFields = serverHasInvestigation(mappedIncident.CONTRIBUTING_FACTORS || mappedIncident.contributingFactors || mappedIncident.immediateActions || mappedIncident.immediate_actions) ||
          serverHasInvestigation(mappedIncident.INVESTIGATION_STATUS || mappedIncident.investigationStatus || mappedIncident.investigation_status);

        const cache = getClientCacheForId(canonicalMappedId) || getClientCacheForId(mappedIncident.ID) || getClientCacheForId(mappedIncident.incident_id) || getClientCacheForId(mappedIncident.id);

        if (!serverProvidesInvestigationFields && cache) {
          // Merge only when server omitted the fields to avoid overwriting authoritative server values
          Object.keys(cache).forEach(k => {
            const existing = mappedIncident[k];
            const val = cache[k];
            if ((existing === undefined || existing === null || existing === '' || (Array.isArray(existing) && existing.length === 0)) && val !== undefined) {
              mappedIncident[k] = val;
            }
          });
          if ((mergedIncidentData.INVESTIGATION_STATUS === null || mergedIncidentData.INVESTIGATION_STATUS === undefined) && cache.investigationStatus) {
            mappedIncident.investigationStatus = cache.investigationStatus;
            mappedIncident.INVESTIGATION_STATUS = cache.investigationStatus;
          }
        } else if (serverProvidesInvestigationFields) {
          // Persist server-provided investigation fields into the client cache so other views can immediately read them
          try {
            if (canonicalMappedId) {
              const toCache = {};
              if (mappedIncident.immediateActions) toCache.immediateActions = mappedIncident.immediateActions;
              if (mappedIncident.UNSAFE_ACTS || mappedIncident.unsafeActs) toCache.unsafeActs = mappedIncident.unsafeActs || mappedIncident.UNSAFE_ACTS;
              if (mappedIncident.UNSAFE_CONDITIONS || mappedIncident.unsafeConditions) toCache.unsafeConditions = mappedIncident.unsafeConditions || mappedIncident.UNSAFE_CONDITIONS;
              if (mappedIncident.CORRECTIVE_ACTIONS_JSON || mappedIncident.correctiveActionsJson) toCache.correctiveActions = mappedIncident.CORRECTIVE_ACTIONS_JSON || mappedIncident.correctiveActionsJson;
              if (mappedIncident.investigationStatus || mappedIncident.INVESTIGATION_STATUS) toCache.investigationStatus = mappedIncident.investigationStatus || mappedIncident.INVESTIGATION_STATUS;
              try { setClientCacheForId(canonicalMappedId, toCache); console.log('Merged server-processed investigation fields into client cache for id', canonicalMappedId, toCache); } catch (e) { }
            }
          } catch (e) { /* ignore */ }
        }
      } catch (e) {
        console.warn('Failed to merge client cache into mappedIncident:', e);
      }

      // Ensure investigationStatus uses authoritative resolver against any previously selected incident
      try {
        const prev = window.__selectedIncident || {};
        const resolved = resolveInvestigationStatus(mappedIncident, prev || {});
        mappedIncident.investigationStatus = resolved;
        mappedIncident.INVESTIGATION_STATUS = resolved;
      } catch (e) {}

      console.log('Setting selected incident with full investigation data (merged):', mappedIncident);
      setSelectedIncident(mappedIncident);
      // FORCE: store the exact server-processed incident object on window for the UI component
      try {
        console.log('FORCE UI: storing server-processed incident object on window for authoritative UI application');
        try { if (typeof window !== 'undefined') window.__serverProcessedIncidentSnapshot = mappedIncident; } catch (e) {}
      } catch (e) {
        console.warn('FORCE UI: failed to store server object snapshot:', e && e.message);
      }
      
      await checkRolePermissions(mappedIncident.id);
    } catch (err) {
      console.error(`Failed to fetch incident with ID ${id}:`, err);
    } finally {
      setLoading(false);
    }
  };

  // Keep a lightweight global cache/reference for validation helper to use
  // (helps when validation is called from different helper scopes)
  useEffect(() => {
    try {
      window.__incidentsCache = window.__incidentsCache || [];
      window.__incidentsCache = incidents;
      window.__selectedIncident = selectedIncident;
    } catch (e) {
      // ignore
    }
  }, [incidents, selectedIncident]);

  // When the normalized URL-based incident id changes (e.g. we replaced it with the canonical ID),
  // ensure we fetch the canonical incident so the modal/card shows the up-to-date data.
  useEffect(() => {
    const isValidUrlId =
      urlIncidentId != null &&
      /^\d+$/.test(String(urlIncidentId)) &&
      Number(urlIncidentId) > 0;

    if (isValidUrlId) {
      console.log('📌 urlIncidentId changed, fetching canonical incident:', urlIncidentId);
      fetchIncidentById(urlIncidentId);
    } else {
      // if cleared, close the modal
      setSelectedIncident(null);
    }
  }, [urlIncidentId]);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      setError(null);

      let currentUserRole = 'USER';
      let currentUserEmail = '';
      let currentUserDept = '';
      
      try {
        console.log('🔍 Making auth/me API call to:', `${API.defaults.baseURL}/api/auth/me`);
        const userResponse = await API.get('/api/auth/me');
        console.log('🔍 Auth API response:', userResponse);
        currentUserRole = userResponse.data.user?.role || userResponse.data?.role || 'USER';
        currentUserEmail = userResponse.data.user?.email || userResponse.data.user?.username || '';
        currentUserDept = userResponse.data.user?.department || '';
        
        setUserEmail(currentUserEmail);
        setUserDepartment(currentUserDept);
        localStorage.setItem('userEmail', currentUserEmail);
        localStorage.setItem('userDepartment', currentUserDept);
        
        console.log('Current user role:', currentUserRole, 'Email:', currentUserEmail, 'Department:', currentUserDept);
        
        // Debug for safety user
        if (currentUserEmail?.toLowerCase() === 'safety@diamondvalley.town') {
          console.log('🔐 SAFETY USER DETECTED - Should see ALL incidents (admin access)');
          console.log('🔐 User details:', { role: currentUserRole, email: currentUserEmail, dept: currentUserDept });
        }
      } catch (authError) {
        console.warn('Could not fetch user details:', authError);
        currentUserRole = localStorage.getItem('userRole') || 'USER';
        currentUserEmail = localStorage.getItem('userEmail') || '';
        currentUserDept = localStorage.getItem('userDepartment') || '';
      }
      
  const normalizedCurrentUserRole = String(currentUserRole || 'USER').toUpperCase();
  setUserRole(normalizedCurrentUserRole);
      
      let endpoint = '/api/incidents';
      let params = {};
      
      if (currentUserRole === 'DELEGATE' && currentUserEmail) {
        console.log('Using delegate filter with email:', currentUserEmail);
        params.delegateEmail = currentUserEmail;
        params.delegateOnly = true;
      }
      
      console.log(`Fetching incidents with role ${currentUserRole} and params:`, params);
      console.log('🔍 Making API call to endpoint:', endpoint);
      console.log('🔍 Full API URL will be:', `${API.defaults.baseURL}${endpoint}`);

      // Fire dashboard only needs Fire-related incidents; ask the server to filter so
      // override users don't download the entire incident history just to filter locally.
      const pageSize = 500;
      let offset = 0;
      let allRows = [];

      // On cold starts Snowflake can be slow; allow longer for the list call.
      const requestTimeoutMs = 120000;

      while (true) {
        const pageParams = {
          ...params,
          fireOnly: true,
          limit: pageSize,
          offset
        };

        console.log('🔍 Fetching incident page:', { limit: pageSize, offset, fireOnly: true });

        let response;
        try {
          response = await API.get(endpoint, { params: pageParams, timeout: requestTimeoutMs });
        } catch (pageErr) {
          // One retry for the first page (most common cold-start failure)
          if (offset === 0) {
            console.warn('First page fetch failed; retrying once:', pageErr);
            response = await API.get(endpoint, { params: pageParams, timeout: requestTimeoutMs });
          } else {
            throw pageErr;
          }
        }
      
      console.log('🔍 Raw API response:', response);
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response data type:', typeof response.data);
      console.log('🔍 Response data structure:', response.data);
      
        const incidentDataRaw = Array.isArray(response.data)
          ? response.data
          : (response.data.incidents || []);

        const pageRows = (incidentDataRaw || []).filter(isFireDeptIncident);
        allRows = allRows.concat(pageRows);

        // Stop when we got less than a full page.
        if (!incidentDataRaw || incidentDataRaw.length < pageSize) {
          break;
        }
        offset += pageSize;

        // Safety valve: don't loop forever if API returns unexpected shapes.
        if (offset > 50000) {
          console.warn('Aborting incident paging after large offset:', offset);
          break;
        }
      }

      const incidentData = allRows;

      // If there are no Fire incidents returned, show an empty list (no demo data).
      if (!incidentData || incidentData.length === 0) {
        setIncidents([]);
        setLoading(false);
        return;
      }
      
      console.log("Raw incident data sample:", incidentData[0]);
      console.log(`Total incidents fetched from backend: ${incidentData.length}`);
      
      // Debug: Log Safety department incidents specifically
      const safetyIncidents = incidentData.filter(incident => {
        const dept = incident.PRIMARY_DEPARTMENT || incident.primaryDepartment || '';
        return dept.toLowerCase().includes('safety');
      });
      console.log(`Safety department incidents found: ${safetyIncidents.length}`, safetyIncidents);

  const mappedIncidents = incidentData.map(incident => {
        const delegateEmail = incident.DELEGATE_EMAIL || incident.delegateEmail;
      const reportedBy = incident.USER || incident.user || incident.CREATED_BY || incident.createdBy || '';
      const employeeEmail = resolveIncidentEmployeeEmail(incident);
        
        console.log(`Incident ${incident.ID || incident.id}: Found delegate email: ${delegateEmail || 'none'}`);
        
        const isUserDelegate = delegateEmail && 
                             userEmail && 
                             delegateEmail.toLowerCase() === userEmail.toLowerCase();
        
  const base = normalizeStatusField({
          id: incident.ID, // <-- INCIDENTS.ID only!
          ID: incident.ID || incident.id,
          incident_id: incident.INCIDENT_ID || incident.incident_id || incident.incidentId || null,
          INCIDENT_ID: incident.INCIDENT_ID || incident.incident_id || incident.incidentId || null,
          title: incident.TITLE || incident.title || 'Untitled Incident',
          location: incident.LOCATION || incident.location || 'Unknown Location',
          severity: incident.SEVERITY || incident.severity || 'Medium',
          description: incident.DESCRIPTION || incident.description || '',
          status: incident.STATUS || incident.status || 'Open',
          date: incident.DATE || incident.date || new Date().toISOString().split('T')[0],
          isDelegateFor: isUserDelegate,
          delegateEmail: delegateEmail,
          reportedBy,
          reportedByName: incident.REPORTED_BY_NAME || incident.reportedByName || incident.REPORTED_BY_FULL_NAME || incident.reportedByFullName || '',
          IS_ANONYMOUS: incident.IS_ANONYMOUS ?? incident.isAnonymous ?? incident.is_anonymous ?? incident.SUBMIT_ANONYMOUSLY ?? incident.submitAnonymously ?? false,
          isAnonymous: incident.isAnonymous ?? incident.IS_ANONYMOUS ?? incident.is_anonymous ?? incident.SUBMIT_ANONYMOUSLY ?? incident.submitAnonymously ?? false,
          SUBMIT_ANONYMOUSLY: incident.SUBMIT_ANONYMOUSLY ?? incident.submitAnonymously ?? incident.IS_ANONYMOUS ?? incident.isAnonymous ?? false,
          employeeEmail,
          EMPLOYEE_ID: incident.EMPLOYEE_ID || incident.employee_id || incident.employeeId || incident.EMPLOYEES || incident.employees || '',
          employeeName: incident.EMPLOYEE_NAME || incident.employeeName || incident.REPORTING_EMPLOYEE_NAME || incident.reportingEmployeeName || '',
          ASSIGNEE: incident.ASSIGNEE || incident.assignee || '',
          assignee: incident.assignee || incident.ASSIGNEE || '',
          primaryDepartment: incident.PRIMARY_DEPARTMENT || incident.primaryDepartment || '',
          PRIMARY_DEPARTMENT: incident.PRIMARY_DEPARTMENT || incident.primaryDepartment || '',
          secondaryDepartment: incident.SECONDARY_DEPARTMENT || incident.secondaryDepartment || '',
          SECONDARY_DEPARTMENT: incident.SECONDARY_DEPARTMENT || incident.secondaryDepartment || '',
          INVESTIGATION_STATUS: resolveInvestigationStatus(incident, {}),
          investigationStatus: resolveInvestigationStatus(incident, {}),
          SUPERVISOR_APPROVAL: incident.SUPERVISOR_APPROVAL || 'PENDING',
          MANAGER_APPROVAL: incident.MANAGER_APPROVAL || 'PENDING',
          SUPERVISOR_SIGN_OFF: incident.SUPERVISOR_SIGN_OFF || 0,
          MANAGER_SIGN_OFF: incident.MANAGER_SIGN_OFF || 0,
          attachments: Array.isArray(incident.ATTACHMENTS) ? incident.ATTACHMENTS : [],
          ATTACHMENTS: Array.isArray(incident.ATTACHMENTS) ? incident.ATTACHMENTS : []
        });

        // merge client cache for this incident if present (prefer canonical INCIDENTS.ID key)
        try {
          const canonicalId = base.id || base.ID || base.incident_id;
          const cache = getClientCacheForId(canonicalId) || getClientCacheForId(base.ID) || getClientCacheForId(base.incident_id) || getClientCacheForId(base.id);
          if (cache) {
            // merge only when server omitted the fields to avoid overwriting authoritative values
            const merged = { ...base };
            Object.keys(cache).forEach(k => {
              const existing = merged[k];
              const val = cache[k];
              if ((existing === undefined || existing === null || existing === '' || (Array.isArray(existing) && existing.length === 0)) && val !== undefined) {
                merged[k] = val;
              }
            });
            return merged;
          }
        } catch (e) {
          console.warn('Failed to merge client cache into mapped incident:', e);
        }

        return base;
      });
      
      setIncidents(mappedIncidents);
      setLoading(false);
      
      // No need to check delegate status via API since it's already in the incident data
      console.log('Incidents loaded with delegate information already included');
      
    } catch (error) {
      console.error('Error fetching incidents:', error);
      setError('Failed to load incidents. Please ensure the backend server is running.');
      setIncidents([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, []);
  
  useEffect(() => {
    const checkForDrafts = () => {
      const draftsMap = {};
      const parsedDrafts = readInvestigationDraftMap();

      incidents.forEach((incident) => {
        const draftEntry = getDraftEntryForIncident(incident, parsedDrafts);
        const canonicalIncidentId = getDraftStorageKey(incident);

        if (!draftEntry?.draftData || !canonicalIncidentId) {
          return;
        }

        if (isDraftObsoleteForServerData({ draftData: draftEntry.draftData, serverData: incident })) {
          clearDraftForIncident(incident);
          return;
        }

        draftsMap[canonicalIncidentId] = true;
      });
      
      setIncidentsWithDrafts(draftsMap);
    };
    
    checkForDrafts();
  }, [incidents]);

  useEffect(() => {
    const hasAssignments = incidents.some(incident => 
      (incident.DELEGATE_EMAIL && incident.DELEGATE_EMAIL.toLowerCase() === userEmail.toLowerCase()) ||
      (incident.delegateEmail && incident.delegateEmail.toLowerCase() === userEmail.toLowerCase())
    );
    
    console.log("Checking delegate assignments:", {
      incidents: incidents.length,
      userEmail,
      hasAssignments
    });
    
    if (hasAssignments !== hasDelegateAssignments) {
      setHasDelegateAssignments(hasAssignments);
    }
  }, [incidents, userEmail, hasDelegateAssignments]);

  // Extract unique departments from incidents for the filter dropdown
  useEffect(() => {
    // Get departments that actually have incidents
    const incidentDepartments = new Set();
    
    incidents.forEach(incident => {
      const dept = incident.PRIMARY_DEPARTMENT || incident.primaryDepartment || '';
      if (dept && dept.trim()) {
        incidentDepartments.add(dept.trim());
      }
    });
    
    // Combine predefined departments with any additional departments found in incidents
    // This ensures we show all expected departments plus any new ones from the database
    const allDepartments = new Set([...ALL_DEPARTMENTS, ...incidentDepartments]);
    const sortedDepartments = Array.from(allDepartments).sort();
    
    setAvailableDepartments(sortedDepartments);
    
    console.log('Available departments for filter:', sortedDepartments);
    console.log('Departments with incidents:', Array.from(incidentDepartments));
  }, [incidents]);

  const navigateToIncident = async (incident) => {
    try {
  // Always fetch the latest data from the server
  const candidateId = incident.id || incident.ID;
  validateIncidentIdForFetch(candidateId, 'navigateToIncident', incidents, selectedIncident);
  let response;
  try {
    response = await API.get(`/api/incidents/${candidateId}`);
  } catch (err) {
    console.warn('navigateToIncident: GET /api/incidents/' + candidateId + ' failed, attempting investigation lookup', err && err.message);
    // If the GET failed, try the investigation detail row for this id — it may be a detail PK.
    try {
      const invResp = await API.get(`/api/incidents/${candidateId}/investigation`);
      const invData = Array.isArray(invResp.data) ? invResp.data[0] : invResp.data;
      const parentId = invData?.INCIDENT_ID || invData?.incident_id || invData?.incidentId || null;
      if (parentId) {
        console.log('navigateToIncident: discovered canonical parent id via investigation row:', parentId);
        // Try fetching the canonical parent incident
        try {
          response = await API.get(`/api/incidents/${parentId}`);
        } catch (parentErr) {
          console.warn('navigateToIncident: failed to fetch canonical parent incident', parentId, parentErr && parentErr.message);
          // fall through to using the original incident object as a fallback
        }
      } else {
        console.log('navigateToIncident: investigation row did not include a parent INCIDENT_ID, falling back');
      }
    } catch (invErr) {
      console.warn('navigateToIncident: investigation lookup also failed for id', candidateId, invErr && invErr.message);
    }
  }

  const incidentData = response ? (Array.isArray(response.data) ? response.data[0] : response.data) : incident;

  // Always use the database ID when available
  const normalizedIncident = {
    ...normalizeInvestigationFormData(incidentData),
    ...incidentData,
    id: incidentData?.ID || incidentData?.id || incident.id,
    title: incidentData?.TITLE || incidentData?.title || incident.title,
    date: incidentData?.DATE || incidentData?.date || incident.date,
    severity: incidentData?.SEVERITY || incidentData?.severity || incident.severity,
    location: incidentData?.LOCATION || incidentData?.location || incident.location,
    description: incidentData?.DESCRIPTION || incidentData?.description || incident.description,
    ISSUE_DESCRIPTION: incidentData?.ISSUE_DESCRIPTION || '',
    issueDescription: incidentData?.ISSUE_DESCRIPTION || '',
  };

  // Use the correct ID in the URL (prefer normalizedIncident.id)
  try {
    const idForUrl = getCanonicalIncidentId(normalizedIncident) || candidateId || '';
    navigate(`/fire-incident-investigations?incidentId=${encodeURIComponent(String(idForUrl))}`, { state: { incident: normalizedIncident } });
  } catch (pushErr) {
    console.warn('navigateToIncident: failed to update URL with incidentId', pushErr && pushErr.message);
  }

  setSelectedIncident(normalizedIncident);
    } catch (err) {
      console.error('Failed to fetch full incident data:', err);
      setSelectedIncident(incident); // fallback
    }
  };

  const getButtonClass = (incident) => {
    const isDelegateMember = (incident.DELEGATE_EMAIL && userEmail && 
      incident.DELEGATE_EMAIL.toLowerCase() === userEmail.toLowerCase()) ||
      (incident.delegateEmail && userEmail && 
      incident.delegateEmail.toLowerCase() === userEmail.toLowerCase());
    const isAssignedFireInvestigator = isAssignedFireInvestigatorForIncident(incident, userEmail);

    const status = (incident.investigationStatus || incident.INVESTIGATION_STATUS || '').toUpperCase();
    const hasActiveDraft = !!incidentsWithDrafts[getDraftStorageKey(incident)];

    if (isDelegateMember) return 'btn btn-delegate';

    if (hasActiveDraft && status !== 'SUPERVISOR_APPROVED' && status !== 'COMPLETED') return 'btn btn-draft';
    
    if (status === 'COMPLETED' || 
        (incident.SUPERVISOR_APPROVAL === 'APPROVED' && incident.MANAGER_APPROVAL === 'APPROVED')) {
      return 'btn btn-view';
    }
    
    if ((isSupervisor(userRole) || isAssignedFireInvestigator) && status === 'SUBMITTED') {
      return 'btn btn-review';
    }
    
    if (userRole === 'MANAGER' && status === 'SUPERVISOR_APPROVED') {
      return 'btn btn-final-review';
    }
    
    if (incident.SUPERVISOR_APPROVAL === 'REJECTED' || incident.MANAGER_APPROVAL === 'REJECTED') {
      return 'btn btn-revision';
    }
    
    return 'btn';
  };

  const getButtonText = (incident) => {
    if (incident.isDelegateFor) return 'Complete Delegate Tasks';
    const isAssignedFireInvestigator = isAssignedFireInvestigatorForIncident(incident, userEmail);
    
    const status = (incident.investigationStatus || incident.INVESTIGATION_STATUS || '').toUpperCase();
    const hasActiveDraft = !!incidentsWithDrafts[getDraftStorageKey(incident)];
  
    console.log(`Button for incident ${incident.id}: Status=${status}, Role=${userRole}`);
  
    if (status === 'COMPLETED' || 
        (incident.SUPERVISOR_APPROVAL === 'APPROVED' && incident.MANAGER_APPROVAL === 'APPROVED')) {
      return 'View';
    }
  
    if (userRole === 'MANAGER' && status === 'SUPERVISOR_APPROVED') {
      return 'Final Review';
    }

    if (isAssignedFireInvestigator && status === 'SUBMITTED') {
      return 'Review';
    }
  
    if (incident.SUPERVISOR_APPROVAL === 'REJECTED' || incident.MANAGER_APPROVAL === 'REJECTED') {
      return 'Revise Investigation';
    }
  
    return hasActiveDraft ? 'Continue Draft' : 
           (status ? 'Edit' : 'Start Investigation');
  };

  const renderIncidentsList = () => {
    console.log("Rendering incidents list with viewMode:", viewMode);
    console.log("User email for filtering:", userEmail);
    console.log("User department for filtering:", userDepartment);
    console.log("User role for filtering:", userRole);
    console.log(`Total incidents available for filtering: ${incidents.length}`);
    
    const userEmailLower = userEmail?.toLowerCase() || '';
    const normalizedUserRole = String(userRole || '').toUpperCase();
    const isSafetyUser = safeToLowerCase(userEmailLower) === 'safety@diamondvalley.town';
    console.log("Is safety user:", isSafetyUser, "Email:", userEmailLower);
    
    if (isSafetyUser) {
      console.log("🔐 SAFETY USER - This user should see ALL incidents regardless of department");
    }
    
    const isDepartmentScopedLeader = ['MANAGER', 'SUPERVISOR', 'FIELD_SUPERVISOR'].includes(normalizedUserRole);
    const isPrivilegedDepartmentViewer =
      normalizedUserRole === 'ADMIN' ||
      normalizedUserRole === 'OHS_ADVISOR' ||
      isCAO ||
      hasWcbDashboardAccess ||
      isCraigB ||
      isSafetyUser ||
      isFireAndNonFireOverride;
    const canShowDepartmentControls = isPrivilegedDepartmentViewer || isDepartmentScopedLeader;

    const filteredIncidents = incidents.filter(incident => {
      const delegateEmail = incident.DELEGATE_EMAIL || incident.delegateEmail;
      const isAssignedDelegate = delegateEmail && 
        userEmail && 
        delegateEmail.toLowerCase() === userEmail.toLowerCase();
      const isAssignedFireInvestigator = isAssignedFireInvestigatorForIncident(incident, userEmail);
      
      // Delegate view should still include incidents where the user is the identified investigator.
      if (viewMode === 'delegate') {
        return isAssignedDelegate || isAssignedFireInvestigator;
      }
      
      // Apply department filter if one is selected
      const incidentDept = incident.PRIMARY_DEPARTMENT || incident.primaryDepartment || '';
      if (selectedDepartmentFilter && selectedDepartmentFilter !== 'all') {
        if (incidentDept !== selectedDepartmentFilter) {
          return false; // Filter out incidents not matching selected department
        }
      }
      
      // Department access filtering - skip if showing all departments
      if (showAllDepartments || isCAO || hasWcbDashboardAccess || isCraigB || isFireAndNonFireOverride) {
        return true;
      }
      
      // Special case: override users should see ALL incidents (admin-level access)
      if (isSafetyUser || isFireAndNonFireOverride) {
        console.log(`Safety user ${userEmailLower} - showing ALL incidents (admin access)`);
        return true;
      }
      
      const incidentReporter = String(
        incident.reportedBy ||
        incident.USER ||
        incident.user ||
        incident.CREATED_BY ||
        incident.createdBy ||
        ''
      ).trim().toLowerCase();
      const incidentEmployeeEmail = resolveIncidentEmployeeEmail(incident) || String(incident.employeeEmail || '').trim().toLowerCase();
      const isOwnReportedIncident = !!userEmailLower && (
        incidentReporter === userEmailLower ||
        incidentEmployeeEmail === userEmailLower
      );
      
      console.log(`Incident ${incident.id}: delegate=${delegateEmail}, isDelegate=${isAssignedDelegate}, isAssignedInvestigator=${isAssignedFireInvestigator}, dept=${incidentDept}, reporter=${incidentReporter}, employee=${incidentEmployeeEmail}, hasAccess=${isOwnReportedIncident}`);
      
      // Regular employees see incidents they reported and incidents submitted on their behalf.
      return isOwnReportedIncident || isAssignedDelegate || isAssignedFireInvestigator;
    });

    const getSubmissionSummary = (incident) => {
      const subtitleAnonymousFlag =
        incident.IS_ANONYMOUS ??
        incident.isAnonymous ??
        incident.is_anonymous ??
        incident.SUBMIT_ANONYMOUSLY ??
        incident.submitAnonymously;
      const normalizedSubtitleAnonymousFlag = String(subtitleAnonymousFlag ?? '').trim().toLowerCase();
      const isAnonymousReporter =
        subtitleAnonymousFlag === true ||
        subtitleAnonymousFlag === 1 ||
        ['true', 'yes', '1', 'y'].includes(normalizedSubtitleAnonymousFlag) ||
        String(incident.reportedByName || '').trim().toLowerCase() === 'anonymous reporter';

      if (isAnonymousReporter) {
        return 'Submitted by Anonymous Reporter';
      }

      const submitterEmail = String(
        incident.reportedBy ||
        incident.USER ||
        incident.user ||
        incident.CREATED_BY ||
        incident.createdBy ||
        ''
      ).trim();
      const submitterName = String(incident.reportedByName || '').trim() || toDisplayNameFromEmail(submitterEmail);
      const employeeEmail = resolveIncidentEmployeeEmail(incident) || String(incident.employeeEmail || '').trim();
      const employeeName = String(incident.employeeName || '').trim() || toDisplayNameFromEmail(employeeEmail);

      if (submitterName && employeeName && employeeEmail && submitterEmail && submitterEmail.toLowerCase() !== employeeEmail.toLowerCase()) {
        return `Submitted by ${submitterName} for ${employeeName}`;
      }

      if (employeeName && employeeEmail && (!submitterEmail || submitterEmail.toLowerCase() !== employeeEmail.toLowerCase())) {
        return `Submitted for ${employeeName}`;
      }

      if (submitterName) {
        return `Submitted by ${submitterName}`;
      }

      return '';
    };

    console.log(`Filtered from ${incidents.length} to ${filteredIncidents.length} incidents`);

    return (
      <div className="incidents-list">
        {/* Add department controls for privileged users */}
        {canShowDepartmentControls && (
          <div className="department-controls">
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              alignItems: 'center', 
              marginBottom: '12px',
              padding: '12px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              flexWrap: 'wrap'
            }}>
              <button 
                className={`dept-toggle-btn ${showAllDepartments ? 'active' : ''}`}
                onClick={() => setShowAllDepartments(!showAllDepartments)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: showAllDepartments ? '#3b82f6' : 'white',
                  color: showAllDepartments ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {showAllDepartments ? 'Show My Department Only' : 'Show All Departments'}
              </button>
              
              {/* Department Filter Dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label htmlFor="department-filter" style={{ 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}>
                  Filter by Department:
                </label>
                <select
                  id="department-filter"
                  value={selectedDepartmentFilter}
                  onChange={(e) => setSelectedDepartmentFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    color: '#374151',
                    minWidth: '150px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Departments</option>
                  <option value="all">All Departments</option>
                  {availableDepartments.map(dept => (
                    <option key={dept} value={dept}>
                      {getDepartmentDisplayName(dept)} ({incidents.filter(inc => (inc.PRIMARY_DEPARTMENT || inc.primaryDepartment) === dept).length})
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedDepartmentFilter && selectedDepartmentFilter !== 'all' && (
                <span style={{ 
                  fontSize: '14px', 
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  Showing {filteredIncidents.length} incidents
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Department indicator */}
        {selectedDepartmentFilter && selectedDepartmentFilter !== 'all' && (
          <div className="department-filter-indicator">
            <span className="filter-label">Filtered by Department:</span>
            <span className="department-badge">{getDepartmentDisplayName(selectedDepartmentFilter)}</span>
            <button 
              onClick={() => setSelectedDepartmentFilter('')}
              style={{
                marginLeft: '8px',
                padding: '2px 6px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
              title="Clear department filter"
            >
              ×
            </button>
          </div>
        )}
        
        {userDepartment && !showAllDepartments && !selectedDepartmentFilter && (
          <div className="department-filter-indicator">
            <span className="filter-label">Department Filter:</span>
            <span className="department-badge">{getDepartmentDisplayName(userDepartment)}</span>
          </div>
        )}
        
        {!userDepartment && !selectedDepartmentFilter && (
          <div className="department-filter-indicator test-mode">
            <span className="filter-label">Test Mode:</span>
            <span className="department-badge">Showing All Departments (No Department Assigned)</span>
          </div>
        )}
        
        {/* Rest of your existing code */}
        {hasDelegateAssignments && (
          <div className="view-toggle">
            <button 
              className={`view-toggle-btn ${viewMode === 'normal' ? 'active' : ''}`}
              onClick={() => setViewMode('normal')}
            >
              Normal View
            </button>
            <button 
              className={`view-toggle-btn ${viewMode === 'delegate' ? 'active' : ''}`}
              onClick={() => setViewMode('delegate')}
            >
              Delegate View
            </button>
          </div>
        )}
        
        <h3>
          Incident List 
          {viewMode === 'delegate' ? ' (Delegate / Investigator Assignments)' : 
           selectedDepartmentFilter && selectedDepartmentFilter !== 'all' ? ` (${getDepartmentDisplayName(selectedDepartmentFilter)} Department)` :
           userDepartment ? ` (${getDepartmentDisplayName(userDepartment)} Department)` : ''}
          {selectedDepartmentFilter && ` - ${filteredIncidents.length} incidents`}
        </h3>
        
        {/* Rest of your existing code remains the same */}
        {filteredIncidents.length === 0 ? (
          <p className="no-incidents">
            {viewMode === 'delegate' 
              ? 'You have no delegate or investigator assignments' 
              : 'No incidents found'}
          </p>
        ) : (
          <table className="incident-table" style={{ 
            fontSize: '12px', 
            width: '100%', 
            tableLayout: 'fixed',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr>
                <th style={{ width: '20%', padding: '6px 8px', fontSize: '11px', fontWeight: '600' }}>Title</th>
                <th style={{ width: '18%', padding: '6px 8px', fontSize: '11px', fontWeight: '600' }}>Location</th>
                <th style={{ width: '12%', padding: '6px 8px', fontSize: '11px', fontWeight: '600' }}>Department</th>
                <th style={{ width: '10%', padding: '6px 8px', fontSize: '11px', fontWeight: '600' }}>Severity</th>
                <th style={{ width: '12%', padding: '6px 8px', fontSize: '11px', fontWeight: '600' }}>Date</th>
                <th style={{ width: '18%', padding: '6px 8px', fontSize: '11px', fontWeight: '600' }}>Approval Status</th>
                <th style={{ width: '10%', padding: '6px 8px', fontSize: '11px', fontWeight: '600' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredIncidents.map(incident => (
                <tr 
                  key={incident.id}
                  className={incident.isDelegateFor ? 'delegate-assigned-row' : ''}
                  style={{ fontSize: '11px' }}
                >
                  <td style={{ 
                    padding: '6px 8px', 
                    fontSize: '11px', 
                    lineHeight: '1.3',
                    wordWrap: 'break-word',
                    overflow: 'hidden'
                  }}>
                    {incident.title}
                    {getSubmissionSummary(incident) && (
                      <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                        {getSubmissionSummary(incident)}
                      </div>
                    )}
                    {incidentsWithDrafts[getDraftStorageKey(incident)] && (
                      <span className="draft-badge" title="Draft investigation in progress" style={{ fontSize: '10px', marginLeft: '4px' }}>
                        📝
                      </span>
                    )}
                    {((incident.DELEGATE_EMAIL && userEmail && 
                       incident.DELEGATE_EMAIL.toLowerCase() === userEmail.toLowerCase()) ||
                      (incident.delegateEmail && userEmail && 
                       incident.delegateEmail.toLowerCase() === userEmail.toLowerCase())) && (
                      <span className="delegate-badge" title="You are assigned as delegate for this incident" style={{ fontSize: '10px', marginLeft: '4px' }}>
                        👤
                      </span>
                    )}
                  </td>
                  <td style={{ 
                    padding: '6px 8px', 
                    fontSize: '11px', 
                    lineHeight: '1.3',
                    wordWrap: 'break-word',
                    overflow: 'hidden'
                  }}>{incident.location}</td>
                  <td style={{ 
                    padding: '6px 4px', 
                    fontSize: '10px', 
                    lineHeight: '1.2',
                    textAlign: 'center',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    maxWidth: '80px'
                  }}>{getDepartmentDisplayName(incident.PRIMARY_DEPARTMENT || incident.primaryDepartment || 'N/A')}</td>
                  <td style={{ 
                    padding: '6px 8px', 
                    fontSize: '11px', 
                    lineHeight: '1.3',
                    textAlign: 'center'
                  }}>{incident.severity}</td>
                  <td style={{ 
                    padding: '6px 8px', 
                    fontSize: '11px', 
                    lineHeight: '1.3',
                    textAlign: 'center'
                  }}>{formatDisplayDate(incident.date || incident.DATE)}</td>
                  <td style={{ 
                    padding: '6px 8px', 
                    fontSize: '10px', 
                    lineHeight: '1.2',
                    textAlign: 'center'
                  }}>
                    {((incident.SUPERVISOR_SIGN_OFF === 1 && incident.MANAGER_SIGN_OFF === 1) || 
                      incident.investigationStatus === 'COMPLETED') ? (
                      <span className="approval-badge approved" style={{ fontSize: '9px', padding: '2px 4px' }}>Fully Approved</span>
                    ) : (incident.SUPERVISOR_SIGN_OFF === 1 || 
                        incident.SUPERVISOR_APPROVAL === 'APPROVED' || 
                        incident.investigationStatus === 'SUPERVISOR_APPROVED') ? (
                      <span className="approval-badge supervisor-approved" style={{ fontSize: '9px', padding: '2px 4px' }}>Supervisor Approved</span>
                    ) : (incident.SUPERVISOR_APPROVAL === 'REJECTED' || 
                        incident.MANAGER_APPROVAL === 'REJECTED' || 
                        incident.investigationStatus === 'NEEDS_REVISION') ? (
                      <span className="approval-badge rejected" style={{ fontSize: '9px', padding: '2px 4px' }}>Needs Revision</span>
                    ) : (incident.investigationStatus === 'SUBMITTED') ? (
                      <span className="approval-badge pending" style={{ fontSize: '9px', padding: '2px 4px' }}>Submitted for Review</span>
                    ) : (
                      <span className="approval-badge pending" style={{ fontSize: '9px', padding: '2px 4px' }}>Pending Submission</span>
                    )}
                  </td>
                  <td style={{ 
                    padding: '6px 4px', 
                    fontSize: '10px', 
                    textAlign: 'center'
                  }}>
                    <button 
                      onClick={() => navigateToIncident(incident)}
                      className={getButtonClass(incident)}
                      style={{ 
                        fontSize: '10px', 
                        padding: '4px 8px',
                        minWidth: 'auto'
                      }}
                    >
                      {getButtonText(incident)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Incident Investigations</h1>
        {hasDelegateAssignments && viewMode === 'delegate' && (
          <div className="role-indicator">
            <span className="badge delegate-badge">Delegate View</span>
          </div>
        )}
        {/* 'Create New Report' button removed per UX request */}
        <Button onClick={fetchIncidents} className="btn-refresh">
          Refresh Data
        </Button>
      </div>


      
      {showNewForm ? (
        <StandaloneIncidentForm />
      ) : (
        <>
          {loading ? (
            <p>Loading incidents...</p>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <div className="error-actions">
                <Button onClick={fetchIncidents}>Try Again</Button>
                </div>
              <div className="troubleshooting">
                <h4>Troubleshooting Steps:</h4>
                <ol>
                  <li>Make sure your backend server is running</li>
                  <li>Check if it's running on port 3001</li>
                  <li>Verify the API endpoints are correct</li>
                  <li>Check browser console for CORS errors</li>
                  <li>Try accessing the API directly in a new browser tab</li>
                </ol>
              </div>
            </div>
          ) : accessDenied.show ? (
            <div className="access-denied-container">
              <div className="access-denied-message">
                <h3>Access Denied</h3>
                <p>{accessDenied.message}</p>
                <p>If you were assigned as a delegate, please ask the supervisor to verify your assignment.</p>
                <button 
                  className="btn btn-secondary mt-3"
                  onClick={() => setAccessDenied({ show: false, message: '' })}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ) : incidents.length === 0 ? (
            <p>No open incidents found to investigate</p>
          ) : (
            renderIncidentsList()
          )}
        </>
      )}
      
      {selectedIncident && (
        <IncidentInvestigationForm 
          incident={selectedIncident} 
          onClose={handleCloseInvestigationForm}
          correctIncidentId={urlIncidentId} 
          onIncidentUpdate={(updatedIncident) => {
            console.log('Parent received onIncidentUpdate for:', updatedIncident && (updatedIncident.id || updatedIncident.ID || updatedIncident.incident_id));
            setSelectedIncident(updatedIncident);

            setIncidents(prevIncidents => prevIncidents.map(inc => {
              const updatedIds = [String(updatedIncident?.id || ''), String(updatedIncident?.ID || ''), String(updatedIncident?.incident_id || '')].filter(Boolean);
              const incIds = [String(inc?.id || ''), String(inc?.ID || ''), String(inc?.incident_id || '')].filter(Boolean);
              const intersects = updatedIds.some(uid => incIds.includes(uid));
              if (intersects) {
                console.log('Updating incident list entry', inc.id, 'with new status', updatedIncident.investigationStatus);
                return {
                  ...inc,
                  ...updatedIncident,
                  investigationStatus: updatedIncident.investigationStatus,
                  INVESTIGATION_STATUS: updatedIncident.investigationStatus,
                  SUPERVISOR_SIGN_OFF: updatedIncident.SUPERVISOR_SIGN_OFF,
                  MANAGER_SIGN_OFF: updatedIncident.MANAGER_SIGN_OFF,
                  SUPERVISOR_APPROVAL: updatedIncident.SUPERVISOR_APPROVAL,
                  MANAGER_APPROVAL: updatedIncident.MANAGER_APPROVAL
                };
              }
              return inc;
            }));
          }}
          fetchRefreshedIncidentById={fetchRefreshedIncidentById}
          setIncidents={setIncidents}
          fetchIncidents={fetchIncidents}
          setUrlIncidentId={setUrlIncidentId}
        />
      )}
    </div>
  );
};

export default FireIncidentInvestigationsDashboard;
